import { isWeb, getPlatform } from '../src/utils/platformUtils'
import { getRealDatabase } from '../database-sqlite/DatabaseSingleton'

import { POST_TABLE_NAMES } from '../src/db/constants'
import { openDB } from '../src/db/indexDB'
import { SCHEDULES_STORE_NAMES } from '../src/db/constants'
import {
    updateFormAnswerSchedule as updateFormAnswerScheduleNew,
    getFormsAnswersInStore as getFormsAnswersInStoreNew,
} from '../db-sqlite/repositories/schedule.repository'
import {
    FORM_SCHEDULE_STATUS,
    FORM_TYPES,
    ID_DROPDOWN_USERS,
    STATUS_FORM_PENDING_SYNC,
    STATUS_SEND,
    STORE_STATUS,
} from '../src/components/stores/constants/constants.stores'
import { distanceInMeters } from '../src/components/customUtils/calculateDistance'
import { PROGRESS_STEPS } from '../src/redux/zustand/progressScheduleStore'
import { captureAndSaveDataError } from '../src/utils/errorUtils'
import {
    generateIdDateNow,
    getDateNow,
    isLocalGeneratedId,
    isValidMongoId,
    padCodeComplete,
} from '../src/utils/basicUtils'
import { TABLE_NAME } from '../db-sqlite/constants/tables'
import { getDb } from '../db-sqlite/adapter'
const USER_PROFILE = 'user_profiles'
const CANT_DIGIT_CODE_COMPANY = 5
const _INITIAL_CODE_ORGANIZATION = '00001'

// ✅ MIGRADO: openDatabase() eliminado - ahora usamos getRealDatabase() en todos los métodos
// Para obtener la DB, usar: const realDb = await getRealDatabase()

/**
 * Queue de operaciones para evitar race conditions
 * Garantiza que las operaciones se ejecuten secuencialmente
 */
class FormUpdateQueue {
    constructor() {
        this.queue = Promise.resolve()
        this.isProcessing = false
    }

    /**
     * Encola una operación para ejecutarse secuencialmente
     * @param {Function} operation - Función async que retorna una Promise
     * @returns {Promise<T>} - Resultado de la operación
     */
    enqueue(operation) {
        const result = this.queue
            .then(() => {
                this.isProcessing = true
                return operation()
            })
            .catch((error) => {
                // Propagar el error pero no bloquear la queue
                throw error
            })
            .finally(() => {
                this.isProcessing = false
            })

        // Actualizar queue para próxima operación
        // Si hay error, la queue continúa (no se bloquea)
        this.queue = result.catch(() => {})

        return result
    }

    /**
     * Verifica si hay operaciones en proceso
     */
    get isPending() {
        return this.isProcessing
    }
}

// Instancia global de la queue
const formUpdateQueue = new FormUpdateQueue()

class schedulesService {
    static isUpdating = false // DEPRECATED - usar formUpdateQueue en su lugar

    constructor() {
        this.updateQueue = formUpdateQueue
    }

    dbExecuteSql = async (query, params) => {
        if (isWeb()) {
            throw new Error('dbExecuteSql not supported on web')
        }

        const realDb = await getRealDatabase()
        const isSelect = /^\s*SELECT/i.test(query)

        if (isSelect) {
            const rows = await realDb.getAllAsync(query, params)
            return {
                rows: {
                    length: rows.length,
                    _array: rows,
                    item: (i) => rows[i],
                },
                rowsAffected: 0,
                insertId: undefined,
            }
        } else {
            const result = await realDb.runAsync(query, params)
            return {
                rows: {
                    length: 0,
                    _array: [],
                    item: () => undefined,
                },
                rowsAffected: result.changes || 0,
                insertId: result.lastInsertRowId,
            }
        }
    }

    addPosSchedules = async (posSchedules, batchSize = 1000, step, setProgress) => {
        if (isWeb()) {
            throw new Error(
                'No se puede realizar una inserción masiva en IndexedDB en entorno web.',
            )
        }

        const realDb = await getRealDatabase()
        const totalposSchedules = posSchedules.length

        if (totalposSchedules === 0) {
            return
        }

        const updateProgress = (currentIndex) => {
            if (step != null && setProgress != null) {
                const { start, end } = PROGRESS_STEPS[step]
                const progress = Math.round(
                    start + (end - start) * (currentIndex / totalposSchedules),
                )
                setProgress(progress)
            }
        }

        let startIndex = 0
        while (startIndex < totalposSchedules) {
            const batchStores = posSchedules.slice(
                startIndex,
                startIndex + batchSize,
            )
            const placeholders = batchStores
                .map(() => '(?,?,?,?,?,?,?,?,?,?)')
                .join(',')
            const values = batchStores.flatMap((schedule) => [
                schedule._id,
                schedule.store_id,
                schedule.schedule_id,
                JSON.stringify(schedule.forms || []),
                JSON.stringify(schedule.forms_relevant),
                JSON.stringify(schedule.forms_featured),
                schedule.campaign_status,
                parseFloat(schedule.lat),
                parseFloat(schedule.lng),
                true,
            ])

            await realDb.runAsync(
                `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} (
                    _id,
                    store_id,
                    schedule_id,
                    forms,
                    forms_relevant,
                    forms_featured,
                    campaign_status,
                    lat,
                    lng,
                    existsInDB
                ) VALUES ${placeholders};`,
                values,
            )

            startIndex += batchSize
            updateProgress(startIndex)
        }
    }

    addAdvancesSchedules = async (advancesSchedules) => {
        console.log(
            '📊 addAdvancesSchedules (wrapper) => ',
            advancesSchedules.length,
        )

        // ✅ Usar la nueva implementación de schedule.repository.ts
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.addAdvancesSchedules(advancesSchedules)
    }


    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de getFormStatusFromAdvances del repository TypeScript.
     *
     * Mantiene la misma interfaz para no romper código existente.
     *
     * @description Obtiene el estado de progreso de un formulario desde SCHEDULES_ADVANCES
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario (para forms de agenda matutino/vespertino)
     * @param {*} dateSelected - Fecha seleccionada (actualmente no se usa en la query)
     * @returns {Promise<any | null>} Registro del advance o null si no se encuentra
     */
    getFormStatusFromAdvances = async (schedule_id, form_id, dateSelected) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const scheduleRepository =
                await import('../db-sqlite/repositories/schedule.repository')

            return await scheduleRepository.getFormStatusFromAdvances(
                schedule_id,
                form_id,
                dateSelected,
            )
        } catch (error) {
            console.error('❌ Error en getFormStatusFromAdvances (wrapper):', error)
            throw error
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT SERVICE
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de getLatestFormAdditional del service TypeScript.
     *
     * @description Obtiene el último formulario adicional ordenado por date_finish DESC
     * @returns {Promise<any[] | null>} Array con el formulario adicional más reciente o null
     */
    getLatestFormAdditional = async () => {
        if (isWeb()) {
            return null
        }

        try {
            const realDb = await getRealDatabase()
            const query = `SELECT * 
                FROM forms_additionals 
                ORDER BY date_finish DESC 
                LIMIT 1;`

            const result = await realDb.getAllAsync(query)
            return result && result.length > 0 ? result : null
        } catch (error) {
            console.error('❌ Error en getLatestFormAdditional:', error)
            return null
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT SERVICE
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de saveFormsAdditionals del service TypeScript.
     *
     * @description Guarda formularios adicionales eliminando los existentes e insertando nuevos
     * @param {Array} newFormsAdditionals - Array de formularios adicionales a guardar
     * @returns {Promise<any[] | void>} Array con los formularios guardados o void
     */
    saveFormsAdditionals = async (newFormsAdditionals) => {
        if (isWeb()) {
            return
        }

        try {
            if (!newFormsAdditionals || newFormsAdditionals.length === 0) {
                return
            }

            const realDb = await getRealDatabase()

            await realDb.runAsync(
                `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS};`,
            )

            for (const form of newFormsAdditionals) {
                const _id = form._id
                const title = form.title
                const date = form.date
                const date_finish = form.date_finish
                const icon = form.icon
                const questions = JSON.stringify(form.questions)

                const query = `
                    INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_ADDITIONALS} (
                        _id, 
                        title, 
                        date, 
                        date_finish, 
                        icon, 
                        questions
                    ) VALUES (?,?,?,?,?,?);
                `
                const params = [_id, title, date, date_finish, icon, questions]

                await realDb.runAsync(query, params)
            }

            return newFormsAdditionals
        } catch (error) {
            console.error('❌ Error en saveFormsAdditionals:', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    getProgressByForm = async (schedule_id, form_id) => {
        if (!form_id) {
            return undefined
        }

        if (isWeb()) {
            return 0
        }

        const realDb = await getRealDatabase()
        let count = 0
        let storesCounted = new Set()

        // consulta a adv
        const rows = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ?`,
            [schedule_id],
        )

        for (let i = 0; i < rows.length; i++) {
            const item = rows[i]
            try {
                if (item.forms) {
                    const formsArray = JSON.parse(item.forms)
                    formsArray.forEach((form) => {
                        if (
                            form._id === form_id &&
                            !storesCounted.has(item.pos_id)
                        ) {
                            storesCounted.add(item.pos_id)
                            count += 1
                        }
                    })
                }
            } catch (parseError) {
                console.error('Error al parsear forms:', parseError)
            }
        }

        // consulta a FORMS_QUESTION_ANSWERS por forms locales
        const answerRows = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE schedule_id = ? AND form_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.BLOCKED}, ${FORM_SCHEDULE_STATUS.PENDING}, ${FORM_SCHEDULE_STATUS.IN_PROGRESS})`,
            [schedule_id, form_id],
        )

        for (let j = 0; j < answerRows.length; j++) {
            const answerItem = answerRows[j]
            if (!storesCounted.has(answerItem.store_id)) {
                storesCounted.add(answerItem.store_id)
                count += 1
            }
        }

        return count
    }
    getProgressByForms = async (schedule_id, form_ids) => {
        if (!Array.isArray(form_ids) || form_ids.length === 0) {
            throw new Error('Invalid form_ids parameter. Must be a non-empty array.')
        }

        if (isWeb()) {
            return 0
        }

        const realDb = await getRealDatabase()
        let count = 0
        let storesCounted = new Set()

        // Consulta a adv
        const rows = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ?`,
            [schedule_id],
        )

        for (let i = 0; i < rows.length; i++) {
            const item = rows[i]
            try {
                if (item.forms) {
                    const formsArray = JSON.parse(item.forms)
                    formsArray.forEach((form) => {
                        if (
                            form_ids.includes(form._id) &&
                            !storesCounted.has(item.pos_id)
                        ) {
                            storesCounted.add(item.pos_id)
                            count += 1
                        }
                    })
                }
            } catch (parseError) {
                console.error('Error al parsear forms:', parseError)
            }
        }

        // Consulta a FORMS_QUESTION_ANSWERS por forms locales
        const answerRows = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE schedule_id = ? AND form_id IN (${form_ids
                .map(() => '?')
                .join(
                    ',',
                )}) AND status NOT IN (${FORM_SCHEDULE_STATUS.BLOCKED}, ${FORM_SCHEDULE_STATUS.PENDING}, ${
                FORM_SCHEDULE_STATUS.IN_PROGRESS
            })`,
            [schedule_id, ...form_ids],
        )

        for (let j = 0; j < answerRows.length; j++) {
            const answerItem = answerRows[j]
            if (!storesCounted.has(answerItem.store_id)) {
                storesCounted.add(answerItem.store_id)
                count += 1
            }
        }

        return count
    }

    getLatestAdvanceSchedule = async () => {
        if (isWeb()) {
            return null
        }

        try {
            const realDb = await getRealDatabase()
            const result = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} ORDER BY date_modified DESC LIMIT 1;`,
                [],
            )
            return result || null
        } catch (error) {
            console.error('Query error:', error)
            return null
        }
    }

    getLatestAdvancePendingsSchedule = async () => {
        if (isWeb()) {
            return null
        }

        try {
            const realDb = await getRealDatabase()
            const result = await realDb.getFirstAsync(
                `SELECT * 
                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                WHERE status = ? 
                ORDER BY date_modified 
                DESC LIMIT 1
                ;`,
                [STORE_STATUS.PENDING_CONNECTION],
            )
            return result || null
        } catch (error) {
            console.error('Query error:', error)
            return null
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de upsertFormStatus del repository TypeScript.
     *
     * Mantiene la misma interfaz para no romper código existente.
     *
     * @description Realiza un UPSERT (UPDATE o INSERT) del estado de progreso de un formulario
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} newStatus - Nuevo estado del formulario
     * @param {*} dateInit - Fecha de inicio
     * @param {*} progress - Progreso del formulario (0-100)
     * @param {*} scoreObtained - Puntaje obtenido
     * @returns {Promise<{ success: boolean; action: 'inserted' | 'updated' }>}
     */
    upsertFormStatus = async (
        schedule_id,
        form_id,
        newStatus,
        dateInit,
        progress,
        scoreObtained,
    ) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const scheduleRepository =
                await import('../db-sqlite/repositories/schedule.repository')
            return await scheduleRepository.upsertFormStatus(
                schedule_id,
                form_id,
                newStatus,
                dateInit,
                progress,
                scoreObtained,
            )
        } catch (error) {
            console.error('❌ Error en upsertFormStatus (wrapper):', error)
            throw error
        }
    }

    getStoreScheduleAdvances = async (scheduleId, storeId, distributor = false) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const query = distributor
            ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND distributor_id = ? ORDER BY date_modified DESC LIMIT 1;`
            : `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND pos_id = ? ORDER BY date_modified DESC LIMIT 1;`

        const result = await realDb.getFirstAsync(query, [scheduleId, storeId])
        return result || null
    }

    getAllScheduleAdvances = async () => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`

        const rows = await realDb.getAllAsync(query, [])
        const schedule_adv = rows.map((item) => ({
            _id: item.id,
            schedule_id: item.schedule_id,
            pos_id: item.pos_id,
            forms: JSON.parse(item.forms),
        }))
        return schedule_adv
    }

    getPosSchedulesByScheduleId = async (scheduleId) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const rows = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE schedule_id = ?;`,
            [scheduleId],
        )

        const posSchedules = rows.map((item) => ({
            _id: item._id,
            store_id: item.store_id,
            schedule_id: item.schedule_id,
            forms: JSON.parse(item.forms),
            campaign_status: item.campaign_status,
            lat: item.lat,
            lng: item.lng,
        }))

        return posSchedules
    }

    getProgressStatus = async (scheduleId, statusMap) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()

        // Primero obtenemos el total de tiendas
        const totalResult = await realDb.getFirstAsync(
            `SELECT COUNT(*) as total 
             FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
             WHERE schedule_id = ?;`,
            [scheduleId],
        )
        const totalStores = totalResult.total

        // Luego, obtenemos los avances por estado (solo tiendas válidas, con pos_id)
        const queryNew = `
            SELECT status, COUNT(*) as count 
            FROM (
                SELECT adv.pos_id, adv.schedule_id, adv.status
                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                INNER JOIN (
                    SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                    WHERE schedule_id = ? AND pos_id > 0
                    GROUP BY pos_id, schedule_id
                ) AS latest_adv
                ON adv.pos_id = latest_adv.pos_id 
                AND adv.schedule_id = latest_adv.schedule_id 
                AND adv.date_modified = latest_adv.latest_date
                INNER JOIN stores ON adv.pos_id = stores._id
            ) AS recent_advances
            GROUP BY status;
            `

        const rows = await realDb.getAllAsync(queryNew, [scheduleId])

        let statusCounts = rows.map((row) => ({
            _id: row?.status,
            label: statusMap[row.status]?.label || 'Unknown',
            value: row.count,
            color_hex: statusMap[row.status]?.color_hex || '#000000',
        }))

        // Calcular las tiendas sin avances (status desconocido)
        const totalAdvances = rows.reduce((sum, row) => sum + row.count, 0)
        const pending = totalStores - totalAdvances

        if (pending > 0) {
            statusCounts.push({
                _id: 0,
                label: statusMap[0]?.label || 'Unknown',
                value: pending,
                color_hex: statusMap[0]?.color_hex || '#000000',
            })
        }

        return statusCounts
    }

    getForm = async (formId) => {
        try {
            if (isWeb()) {
                return null
            }

            const realDb = await getRealDatabase()
            const result = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`,
                [formId],
            )

            // ✅ Validación defensiva: verificar que result exista y no sea null/undefined
            if (!result) {
                console.warn(
                    `⚠️ getForm: Formulario no encontrado para ID: ${formId}`,
                )
                return null
            }

            // ✅ Parsear campos JSON solo si existen y no son null
            try {
                result.type_form = result.type_form
                    ? JSON.parse(result.type_form)
                    : null
                result.contents = result.contents ? JSON.parse(result.contents) : []
                result.segments = result.segments ? JSON.parse(result.segments) : []
                result.tags = result.tags ? JSON.parse(result.tags) : []
            } catch (parseError) {
                console.error(
                    `❌ getForm: Error parseando JSON para form ${formId}:`,
                    parseError,
                )
                // Si hay error de parsing, devolver valores por defecto
                result.type_form = result.type_form || null
                result.contents = []
                result.segments = []
                result.tags = []
            }

            return result
        } catch (error) {
            console.error(`❌ getForm: ${formId} - `, error)
            return null
        }
    }

    getFormName = async (formId) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const result = await realDb.getFirstAsync(
            `SELECT name_form FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`,
            [formId],
        )

        return result ? result.name_form : null
    }

    /**
     * @description updatea formularios de la tabla SCHEDULES_STORE_NAMES.FORMS
     * @param {*} formId
     * @returns
     */
    updateStatusForm = async (formId, storeId, status) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                    SET status = ?
                    WHERE _id = ? AND store_id = ?;`

        const queryParams = [status, formId, storeId]

        await realDb.runAsync(query, queryParams)
        console.log('Registro actualizado con exito: updateStatusForm')
    }

    /**
     * @description es para remplazar
     * @param {*} formId
     */
    getFormAndUpdateSecondaryAction = async (formId, storeId, secondary_actions) => {
        try {
            if (isWeb()) {
                return null
            }

            const realDb = await getRealDatabase()
            const result = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`,
                [formId],
            )

            // ✅ Validación defensiva
            if (!result) {
                console.warn(
                    `⚠️ getFormAndUpdateSecondaryAction: Formulario no encontrado para ID: ${formId}`,
                )
                return null
            }

            const form = result

            // ✅ Validar que contents exista antes de parsear
            if (!form.contents) {
                console.warn(
                    `⚠️ getFormAndUpdateSecondaryAction: form.contents es null para ID: ${formId}`,
                )
                form.contents = []
            } else {
                try {
                    form.contents = JSON.parse(form.contents)
                } catch (parseError) {
                    console.error(
                        `❌ getFormAndUpdateSecondaryAction: Error parseando contents para form ${formId}:`,
                        parseError,
                    )
                    form.contents = []
                }
            }

            form.contents.forEach((question) => {
                if (
                    question.input_type &&
                    question.input_type['_id'] &&
                    question.input_type['_id'] === ID_DROPDOWN_USERS
                ) {
                    question.secondary_actions = secondary_actions
                }
            })

            try {
                await this.updateFormSecondaryAction(
                    formId,
                    storeId,
                    JSON.stringify(form.contents),
                )
            } catch (error) {
                console.error('618 ---- updateFormSecondaryAction - error: ', error)
            }

            return form
        } catch (error) {
            console.error(`❌ getFormAndUpdateSecondaryAction: ${formId} - `, error)
            return null
        }
    }

    updateFormsSecondaryActionUsersDropdown = async (arrayFormUsers, storeId) => {
        if (isWeb()) {
            return
        }

        const formIds = arrayFormUsers.map((form) => `'${form.form_id}'`).join(',')
        if (!formIds) {
            return null
        }

        const realDb = await getRealDatabase()
        const result = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${formIds})`,
            [],
        )

        if (result.length === 0) {
            return null
        }

        const formUpdates = {}
        arrayFormUsers.forEach((form) => {
            formUpdates[form.form_id] = form.secondary_action
        })

        const updatedForms = []
        for (let i = 0; i < result.length; i++) {
            let form = result[i]

            // ✅ Validar que contents exista antes de parsear
            if (!form.contents) {
                console.warn(
                    `⚠️ updateFormsSecondaryActionUsersDropdown: form.contents es null para ID: ${form._id}`,
                )
                continue
            }

            try {
                form.contents = JSON.parse(form.contents)
            } catch (parseError) {
                console.error(
                    `❌ updateFormsSecondaryActionUsersDropdown: Error parseando contents para form ${form._id}:`,
                    parseError,
                )
                continue
            }

            form.contents.forEach((question) => {
                if (
                    question.input_type &&
                    question.input_type['_id'] === ID_DROPDOWN_USERS
                ) {
                    question.secondary_actions = formUpdates[form._id] || []
                }
            })

            updatedForms.push({
                id: form._id,
                contents: JSON.stringify(form.contents),
            })
        }

        if (updatedForms.length === 0) {
            return null
        }

        let query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS} SET contents = CASE `
        let ids = []

        updatedForms.forEach(({ id, contents }, index) => {
            query += `WHEN _id = ? THEN ? `
            ids.push(id, contents)
        })

        query += `END WHERE _id IN (${updatedForms.map(() => '?').join(', ')});`
        ids.push(...updatedForms.map(({ id }) => id))

        await realDb.runAsync(query, ids)
        console.log('Formularios actualizados correctamente')
    }

    updateFormSecondaryAction = async (formId, storeId, arrayFormsModified) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                    SET contents = ?
                    WHERE _id = ?;`

        const queryParams = [arrayFormsModified, formId]

        await realDb.runAsync(query, queryParams)
    }

    updateFormSchedule = async (formId) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const result = await realDb.getFirstAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`,
            [formId],
        )

        if (result) {
            result.type_form = JSON.parse(result.type_form)
            result.contents = JSON.parse(result.contents)
            result.segments = JSON.parse(result.segments)
            return result
        }

        return null
    }

    addOrUpdateSchedule = async (schedule) => {
        if (isWeb()) {
            throw new Error(
                'No se puede realizar una inserción masiva en IndexedDB en entorno web.',
            )
        }

        const realDb = await getRealDatabase()
        const countResult = await realDb.getFirstAsync(
            `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`,
            [schedule._id],
        )

        const count = countResult.count || 0

        if (count === 0) {
            // Insertar nuevo registro (comentado en el original)
            return
        } else {
            // Actualizar registro existente
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET
                    date_finish = ?,
                    date_init = ?,
                    form_finish = ?,
                    form_init = ?,
                    stores = ?
                    WHERE _id = ?`
            const params = [
                schedule.date_finish,
                schedule.date_init,
                JSON.stringify(schedule.form_finish || {}),
                JSON.stringify(schedule.form_init || {}),
                JSON.stringify(schedule.stores || []),
                schedule._id,
            ]

            const result = await realDb.runAsync(query, params)
            console.log('🚀 ~ Agenda guardada/actualizada', result)
            return result
        }
    }

    // Función para obtener la agenda vigente de hoy
    getTodaySchedule = async (today) => {
        console.log('🚀 ~ schedulesService ~ today:', today)

        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const result = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE date_init <= ? AND date_finish >= ? LIMIT 1`,
            [today, today],
        )

        if (result.length > 0) {
            const scheduleRow = result[0]
            return {
                _id: scheduleRow._id,
                additional: JSON.parse(scheduleRow.additional),
                date_finish: scheduleRow.date_finish,
                date_init: scheduleRow.date_init,
            }
        }

        console.log('----------------. else')
        throw new Error('Schedule not found')
    }

    update_schedule = async (schedule_id, key, value) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const result = await realDb.runAsync(
            `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET ${key} = ? WHERE _id = ?;`,
            [value, schedule_id],
        )
        console.log('agenda actualizada con éxito', result)
        return result
    }

    updateScheduleReport = async (schedule_id, report) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const reportValue = report ? 1 : 0

        const result = await realDb.runAsync(
            `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES} SET reports = ? WHERE _id = ?;`,
            [reportValue, schedule_id],
        )

        console.log('✅ Reporte actualizado con éxito:', {
            schedule_id,
            report: reportValue,
            changes: result.changes,
        })
        return result
    }

    get_schedule = async (schedule_id) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const scheduleRow = await realDb.getFirstAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?;`,
            [schedule_id],
        )

        if (scheduleRow) {
            return {
                _id: scheduleRow._id,
                date_finish: scheduleRow.date_finish,
                date_init: scheduleRow.date_init,
                form_finish:
                    scheduleRow.form_finish.length > 6
                        ? JSON.parse(scheduleRow.form_finish)
                        : null,
                form_init:
                    scheduleRow.form_init.length > 6
                        ? JSON.parse(scheduleRow.form_init)
                        : null,
                stores: [],
                progress_diary: scheduleRow.progress_diary,
                additional: JSON.parse(scheduleRow.additional),
            }
        }

        throw new Error('No se encontró la agenda')
    }

    getStore = async (storeId) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const store = await realDb.getFirstAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?`,
            [storeId],
        )

        if (store) {
            return store
        }

        throw new Error(`Store with ID ${storeId} not found`)
    }

    getStoreForGoto = async (storeId) => {
        if (isWeb()) {
            return null
        }

        const realDb = await getRealDatabase()
        const query = `
            SELECT pos.*, adv.status AS campaign_status, adv.forms AS forms_advances, adv.score_global AS score_global
            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
            LEFT JOIN (
                SELECT adv.pos_id, adv.schedule_id, adv.status, adv.forms, adv.score_global
                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                INNER JOIN (
                    SELECT pos_id , MAX(date_modified) AS latest_date 
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                    WHERE pos_id = ?
                    GROUP BY pos_id
                ) AS latest_adv
                ON adv.pos_id = latest_adv.pos_id 
                AND adv.date_modified = latest_adv.latest_date
            ) AS adv
            ON pos.store_id = adv.pos_id
            WHERE pos.store_id = ?`

        const params = [storeId, storeId]
        const store = await realDb.getFirstAsync(query, params)

        if (store) {
            return store
        }

        throw new Error(`Store with ID ${storeId} not found`)
    }

    getAllStores = async (pageNumber, pageSize, campaignStatus) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const offset = pageNumber * pageSize
        console.log('🚀 ~ returnnewPromise ~ offset:', offset)

        let query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} LIMIT ${pageSize} OFFSET ${offset};`

        if (campaignStatus != null && campaignStatus != -1) {
            query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE campaign_status = '${campaignStatus}' LIMIT ${pageSize} OFFSET ${offset};`
        }

        const result = await realDb.getAllAsync(query, [])
        return result
    }
    validateCheckins = async (schedule_id) => {
        console.log('🚀 ~ schedulesService ~ schedule_id:', schedule_id)

        if (isWeb()) {
            return { status: true, store: {} }
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} WHERE schedule_id = ? AND status = 4 ORDER BY date_init DESC`

        const result = await realDb.getAllAsync(query, [schedule_id])

        if (result.length > 0) {
            const schedule_advances = result[0]
            let res_store = {}

            try {
                if (schedule_advances && schedule_advances.pos_id) {
                    console.log('1398 -- estoy')
                    const store = await this.getStore(schedule_advances.pos_id)
                    res_store = store

                    return {
                        status: false,
                        store: res_store,
                        schedule_advances: schedule_advances,
                    }
                } else {
                    return {
                        status: true,
                        store: {},
                    }
                }
            } catch (error) {
                console.error('852 --- error getStore: ', error)
                return {
                    status: false,
                    store: {},
                }
            }
        }

        return {
            status: true,
            store: {},
        }
    }

    getStoresFromSchedule = async (
        pageNumber,
        pageSize,
        scheduleID,
        campaignStatus,
        lat,
        lng,
        radius,
        schedule_free,
        dateSelected,
    ) => {
        console.log(
            `🔍 [getStoresFromSchedule] Buscando stores para schedule: ${scheduleID}, page: ${pageNumber}, size: ${pageSize}`,
        )

        const realDb = await getRealDatabase()

        try {
            // ✅ Construir query con filtros SQL optimizados
            let query = `
                SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster, stores.frequency, stores.topstore, stores.potencial,
                       adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured,
                       COALESCE(adv.status, 0) AS campaign_status,
                       adv.last_checkout,
                       adv.incidence_answer,
                       adv.score_global,
                       (
                           (pos.lat - ?) * (pos.lat - ?) +
                           (pos.lng - ?) * (pos.lng - ?)
                       ) AS distance_sq
                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
                INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores
                ON pos.store_id = stores._id
                LEFT JOIN (
                    SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status, adv.last_checkout,adv.incidence_answer, adv.score_global, adv.date_modified
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                    INNER JOIN (
                        SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                        GROUP BY pos_id, schedule_id
                    ) AS latest_adv
                    ON adv.pos_id = latest_adv.pos_id
                    AND adv.schedule_id = latest_adv.schedule_id
                    AND adv.date_modified = latest_adv.latest_date
                ) AS adv
                ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id
                WHERE pos.schedule_id = ?`

            let params = [lat, lat, lng, lng, scheduleID]

            // ✅ Aplicar filtro de campaign_status en SQL
            if (campaignStatus !== null && campaignStatus !== -1) {
                if (campaignStatus === 0) {
                    query += ` AND COALESCE(adv.status, 0) = 0`
                } else if (campaignStatus === STORE_STATUS.INCIDENCE_REPORTED) {
                    query += ` AND COALESCE(adv.status, 0) IN (${STORE_STATUS.INCIDENCE_REPORTED}, ${STORE_STATUS.INCIDENCE_REPORTED_OTHER}, ${STORE_STATUS.INCIDENCE_REPORTED_PERMANENTE})`
                } else {
                    query += ` AND COALESCE(adv.status, 0) = ?`
                    params.push(campaignStatus)
                }
            }

            // ✅ Ordenar en SQL: prioridad 4 primero, luego por distancia
            query += ` ORDER BY
                        CASE 
                            WHEN COALESCE(adv.status, 0) = 4 THEN 0
                            WHEN COALESCE(adv.status, 0) = 0 THEN 1
                            WHEN COALESCE(adv.status, 0) = 1 THEN 2
                            ELSE 3
                        END,
                        distance_sq`

            // ✅ Aplicar paginación en SQL si pageSize != 0
            if (pageSize > 0) {
                query += ` LIMIT ? OFFSET ?`
                params.push(pageSize, pageNumber * pageSize)
            }

            console.log(`🔍 [getStoresFromSchedule] Ejecutando query optimizada...`)

            const rows = await realDb.getAllAsync(query, params)

            console.log(
                `📊 [getStoresFromSchedule] Encontradas ${rows.length} tiendas`,
            )

            if (rows.length === 0) {
                console.log(`⚠️ [getStoresFromSchedule] No se encontraron tiendas`)
                return []
            }

            // ✅ Solo parsear JSON y calcular distancia exacta (mínimo procesamiento)
            const stores = rows.map((item) => {
                // 1. Parseamos los arrays que necesitamos procesar
                const forms_advances = item.forms_advances
                    ? JSON.parse(item.forms_advances)
                    : []
                let forms_relevant = item.forms_relevant
                    ? JSON.parse(item.forms_relevant)
                    : []
                let forms_featured = item.forms_featured
                    ? JSON.parse(item.forms_featured)
                    : []

                // 2. Armamos un diccionario temporal para buscar colores rápido (O(1))
                const advancesColors = {}
                forms_advances.forEach((form) => {
                    if (form._id && form.hex_color) {
                        advancesColors[form._id] = form.hex_color
                    }
                })

                // 3. Función auxiliar para sobreescribir el color si existe en el avance
                const overrideColors = (formsArray) => {
                    return formsArray.map((form) => {
                        if (advancesColors[form._id]) {
                            form.hex_color = advancesColors[form._id]
                        }
                        return form
                    })
                }

                // 4. Aplicamos el cruce de colores
                forms_relevant = overrideColors(forms_relevant)
                forms_featured = overrideColors(forms_featured)

                // 5. Retornamos el objeto final mapeado
                return {
                    _id: schedule_free ? item._id : item.store_id,
                    schedule_id: item.schedule_id,
                    name: item.name,
                    code: item.code,
                    address: item.address,
                    forms: item.forms ? JSON.parse(item.forms) : [],
                    campaign_status: item.campaign_status,
                    lat: item.lat,
                    lng: item.lng,
                    forms_relevant: forms_relevant,
                    forms_featured: forms_featured,
                    segment: item.segment ?? '',
                    forms_advances: item.forms_advances
                        ? JSON.parse(item.forms_advances)
                        : [],
                    cluster: item.cluster ?? '',
                    distance: newcalculateDistance(lat, lng, item.lat, item.lng),
                    incidence_answer: item.incidence_answer || null,
                    frequency: item.frequency || '',
                    topstore: item.topstore || '',
                    potencial: item.potencial || '',
                    last_checkout: item.last_checkout || null,
                    score_global: item.score_global ?? null,
                }
            })


            console.log(
                `✅ [getStoresFromSchedule] Retornando ${stores.length} tiendas (optimizado)`,
            )
            return stores
        } catch (error) {
            console.error('❌ [getStoresFromSchedule] Error:', error)
            throw error
        }
    }

    searchStoresFromSchedule = async (
        pageNumber,
        pageSize,
        scheduleID,
        campaignStatus,
        lat,
        lng,
        radius,
        schedule_free,
        dateSelected,
    ) => {
        const realDb = await getRealDatabase()

        try {
            let query = `
                SELECT pos.*, stores.name, stores.code, stores.address, stores.segment, stores.cluster,
                       adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured,
                       COALESCE(adv.status, 0) AS campaign_status,
                       adv.score_global
                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos
                INNER JOIN ${SCHEDULES_STORE_NAMES.STORES} AS stores
                ON pos.store_id = stores._id
                LEFT JOIN (
                    SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status, adv.score_global
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                    INNER JOIN (
                        SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                        GROUP BY pos_id, schedule_id
                    ) AS latest_adv
                    ON adv.pos_id = latest_adv.pos_id 
                    AND adv.schedule_id = latest_adv.schedule_id 
                    AND adv.date_modified = latest_adv.latest_date
                ) AS adv
                ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id 
                WHERE pos.schedule_id = ?`

            let params = [scheduleID]

            const rows = await realDb.getAllAsync(query, params)

            const stores = rows.map((item) => ({
                _id: schedule_free ? item._id : item.store_id,
                schedule_id: item.schedule_id,
                name: item.name,
                code: item.code,
                address: item.address,
                forms: item.forms ? JSON.parse(item.forms) : [],
                campaign_status: item.campaign_status ? item.campaign_status : 0,
                lat: item.lat,
                lng: item.lng,
                forms_relevant: item.forms_relevant
                    ? JSON.parse(item.forms_relevant)
                    : [],
                forms_featured: item.forms_featured
                    ? JSON.parse(item.forms_featured)
                    : [],
                segment: item.segment ?? '',
                forms_advances: item.forms_advances
                    ? JSON.parse(item.forms_advances)
                    : [],
                cluster: item.cluster ?? '',
                distance: newcalculateDistance(lat, lng, item.lat, item.lng),
                frequency: item.frequency || '',
                topstore: item.topstore || '',
                potencial: item.potencial || '',
                score_global: item.score_global ?? null,
            }))

            let accumulatedStores = stores
            console.log('CAMPAING STATUS ==> ', campaignStatus)
            if (campaignStatus !== null && campaignStatus !== -1) {
                if (campaignStatus === 0) {
                    accumulatedStores = accumulatedStores.filter(
                        (item) => item.campaign_status == 0,
                    )
                    accumulatedStores.sort((a, b) => a.distance - b.distance)
                } else {
                    accumulatedStores = accumulatedStores.filter(
                        (item) => item.campaign_status == campaignStatus,
                    )
                }
            } else {
                const priority = {
                    4: 0,
                    0: 1,
                    1: 2,
                }
                accumulatedStores.sort((a, b) => {
                    const pa = priority[a.campaign_status] ?? 3
                    const pb = priority[b.campaign_status] ?? 3

                    // 1. Prioridad por status
                    if (pa !== pb) return pa - pb

                    // 2. Dentro del grupo, por distancia
                    return 0 - (b.distance ?? Inf0inity)
                })
            }

            return accumulatedStores
        } catch (error) {
            console.error('Transaction Error - searchStoresFromSchedule:', error)
            throw error
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de searchStoresByText del repository TypeScript.
     *
     * Mantiene la misma interfaz para no romper código existente.
     *
     * @description Busca tiendas por texto (nombre, código o dirección) dentro de un schedule
     * @param {string} scheduleID - ID del schedule
     * @param {string} searchText - Texto a buscar (case-insensitive)
     * @returns {Promise<Array>} Array de tiendas encontradas
     */
    searchStoresByText = async (scheduleID, searchText) => {
        console.log(
            '🚀 ~ schedulesService ~ searchStoresByText ~ searchText:',
            searchText,
            'scheduleID => ',
            scheduleID,
        )

        try {
            // Lazy import del repository TypeScript
            const scheduleRepo =
                await import('../db-sqlite/repositories/schedule.repository')
            const stores = await scheduleRepo.searchStoresByText(
                scheduleID,
                searchText,
            )

            console.log(
                '✅ searchStoresByText (nuevo) - Encontradas:',
                stores.length,
                'tiendas',
            )
            return stores
        } catch (error) {
            console.error('❌ Error en searchStoresByText (nuevo):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    //
    // ⚠️ PROBLEMA DETECTADO EN VERSIÓN ANTERIOR:
    // - params.unshift() agregaba searchText al inicio, pero la query esperaba
    //   searchText (x3) primero y scheduleID al final
    // - Esto causaba que nunca encontrara resultados
    //
    // SOLUCIÓN EN NUEVA VERSIÓN:
    // - Orden correcto de parámetros: [searchText, searchText, searchText, scheduleID]
    // - Usa prepared statements del nuevo adapter
    // - Mejor logging para debugging
    // ========================================================================
    /*
    searchStoresByText_OLD = (scheduleID, searchText) => {
        const db = await openDB()

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    let params = [scheduleID]  // ❌ ERROR: scheduleID se agrega primero
                    let query = `
                   
                    WITH filtered_stores AS (
                        SELECT _id, name, code, address, segment, cluster 
                        FROM ${SCHEDULES_STORE_NAMES.STORES}
                        WHERE LOWER(name) LIKE LOWER(?)    // Espera searchText #1
                        OR LOWER(code) LIKE LOWER(?)       // Espera searchText #2
                        OR LOWER(address) LIKE LOWER(?)    // Espera searchText #3
                    )
                    SELECT pos.*, fs.name, fs.code, fs.address, fs.segment, fs.cluster, 
                           adv.forms AS forms_advances, pos.forms_relevant, pos.forms_featured, 
                           COALESCE(adv.status, 0) AS campaign_status
                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} AS pos 
                    INNER JOIN filtered_stores AS fs ON pos.store_id = fs._id
                    LEFT JOIN (
                        SELECT adv.pos_id, adv.schedule_id, adv.forms, adv.status 
                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                        INNER JOIN (
                            SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                            GROUP BY pos_id, schedule_id
                        ) AS latest_adv
                        ON adv.pos_id = latest_adv.pos_id 
                        AND adv.schedule_id = latest_adv.schedule_id 
                        AND adv.date_modified = latest_adv.latest_date
                    ) AS adv
                    ON pos.store_id = adv.pos_id AND pos.schedule_id = adv.schedule_id 
                    WHERE pos.schedule_id = ?;              // Espera scheduleID
                    `

                    if (searchText && searchText.trim() !== '') {
                        // ❌ ERROR: unshift agrega al inicio, pero scheduleID ya estaba primero
                        // Resultado: [searchText, searchText, searchText, scheduleID]
                        // Pero la query lee: scheduleID en los LIKE, y searchText al final en WHERE
                        params.unshift(`%${searchText}%`, `%${searchText}%`, `%${searchText}%`)
                    }

                    tx.executeSql(
                        query,
                        params,
                        (_, result) => {
                            const rows = result.rows._array
                            const stores = rows.map((item) => ({
                                _id: item.store_id,
                                schedule_id: item.schedule_id,
                                name: item.name,
                                code: item.code,
                                address: item.address,
                                forms: item.forms ? JSON.parse(item.forms) : [],
                                campaign_status: item.campaign_status ?? 0,
                                segment: item.segment ?? '',
                                cluster: item.cluster ?? '',
                            }))

                            resolve(stores)
                        },
                        (_, error) => {
                            console.error('Query Execution Error:', error)
                            reject(error)
                        },
                    )
                },
                (error) => {
                    console.error('Transaction Error:', error)
                    reject(error)
                },
            )
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    deleteAllAdvanceSchedule = async () => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            const advCount = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`,
                [],
            )
            if (advCount.count > 0) {
                await realDb.runAsync(
                    `DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES};`,
                    [],
                )
            }

            const checkinCount = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.CHECKIN};`,
                [],
            )
            if (checkinCount.count > 0) {
                await realDb.runAsync(
                    `DELETE FROM ${SCHEDULES_STORE_NAMES.CHECKIN};`,
                    [],
                )
            }

            const checkoutCount = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.CHECKOUT};`,
                [],
            )
            if (checkoutCount.count > 0) {
                await realDb.runAsync(
                    `DELETE FROM ${SCHEDULES_STORE_NAMES.CHECKOUT};`,
                    [],
                )
            }

            console.log('590 La tabla schdeule advance eliminada exitosamente')
        } catch (error) {
            console.log('🚀 ~ deleteAllAdvanceSchedule ~ error:', error)
            console.error('595 Error al eliminar las tablas psoteos:', error)
            throw error
        }
    }
    deleteAllTablesSchedule = async () => {
        if (isWeb()) {
            return
        }
        const db = await getDb()
        const tables = [
            TABLE_NAME.SCHEDULE_CAMPAINGS, // CAPAINGNS
            TABLE_NAME.SCHEDULE_STORE_FORMS, // FORMS
            TABLE_NAME.SCHEDULE_CREATE_STORES, // INFO CREAR TIENDA
            TABLE_NAME.SCHEDULES, // AGENDA
            TABLE_NAME.SCHEDULE_STORES, // TIENDAS
            TABLE_NAME.SCHEDULES_POS, // SCHEDULE POS
            TABLE_NAME.SCHEDULE_ADVANCES, // AVANCES
            TABLE_NAME.SCHEDULE_STORES_INDICATORS, // INDICADORES KPIS
            TABLE_NAME.SCHEDULE_FORMS_ADDITIONALS,
            TABLE_NAME.SCHEDULE_ADVANCES,
        ]

        for (const table of tables) {
            const countResult = await db.getFirst({
                sql: `SELECT COUNT(*) as count FROM ${table};`,
                params: [],
            })

            if (countResult && countResult.count > 0) {
                try {
                    const result = await db.run({
                        sql: `DELETE FROM ${table};`,
                        params: [],
                    })
                    console.log(`✅ Tabla ${table} limpiada exitosamente.`)
                } catch (error) {
                    console.error(`❌ Error al limpiar la tabla ${table}:`, error)
                }
            }
        }
    }

    add_checkin = async (data, checkin_id = '', distributor_id = '') => {
        if (!data) {
            return
        }

        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.CHECKIN} (
            date,
            store_id,
            lat_checkin,
            lng_checkin,
            checkin_id,
            schedule_id,
            date_init,
            dif_utc,
            status,
            distributor_id,
            objectives,
            steps_distributor,
            formsIdsFqa
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`
        const params = [
            data.date,
            data.store_id,
            data.lat_checkin,
            data.lng_checkin,
            checkin_id,
            data.schedule_id,
            data.date_init,
            data.dif_utc,
            data.status,
            distributor_id,
            JSON.stringify(data.objectives || []),
            JSON.stringify(data.steps_distributor || []),
            JSON.stringify(data.formsIdsFqa || []),
        ]

        const result = await realDb.runAsync(query, params)
        return result
    }

    add_checkin_steps = async (data) => {
        if (!data || isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.CHECKIN_STEPS} (
            date,
            store_id,
            schedule_id
        ) VALUES (?, ?, ?)`
        const params = [data.date, data.store_id, data.schedule_id]

        try {
            const result = await realDb.runAsync(query, params)
            // console.log('🚀 ~ se guardo el checkin', result)
            return result
        } catch (error) {
            console.error('Transaction error:', error)
            throw error
        }
    }

    updateCheckinActivities = async (
        scheduleId,
        distributorId,
        activityId,
        comment,
    ) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            // 1. Buscar el registro existente
            const result = await realDb.getAllAsync(
                `SELECT activities, checkin_id FROM ${SCHEDULES_STORE_NAMES.CHECKIN}
                 WHERE schedule_id = ? AND distributor_id = ?`,
                [scheduleId, distributorId],
            )

            if (result.length > 0) {
                // 2. Parsear el campo activities
                const existingRecord = result[0]
                let activities = JSON.parse(existingRecord.activities || '[]')

                // 3. Agregar la nueva actividad
                let findActivity = activities.find(
                    (a) => a.activity_id === activityId,
                )
                if (findActivity) {
                    // Si la actividad ya existe, actualiza el comentario
                    findActivity.comment = comment
                } else {
                    // Si la actividad no existe, agrégala
                    activities.push({ activity_id: activityId, comment: comment })
                }

                // 4. Stringificar el campo activities
                const updatedActivities = JSON.stringify(activities)

                // Actualizar el registro con el nuevo campo activities
                const updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKIN}
                                     SET activities = ?
                                     WHERE schedule_id = ? AND distributor_id = ?`

                await realDb.runAsync(updateQuery, [
                    updatedActivities,
                    scheduleId,
                    distributorId,
                ])
                console.log('Registro actualizado')

                let data_update = existingRecord
                data_update.activities = activities
                return data_update
            } else {
                // Si no se encontró ningún registro
                console.log('No se encontró el check-in para actualizar')
                throw new Error('No se encontró el check-in para actualizar')
            }
        } catch (error) {
            console.error('Error:', error)
            throw error
        }
    }
    updateScheduleActivities = async (scheduleId, activityId, comment) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            // 1. Buscar el registro existente
            const result = await realDb.getAllAsync(
                `SELECT activities FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                 WHERE schedule_id = ?`,
                [scheduleId],
            )

            if (result.length > 0) {
                // 2. Parsear el campo activities
                const existingRecord = result[0]
                let activities = JSON.parse(existingRecord.activities || '[]')

                // 3. Agregar la nueva actividad o actualizarla
                let findActivity = activities.find(
                    (a) => a.activity_id === activityId,
                )
                if (findActivity) {
                    // Si la actividad ya existe, actualiza el comentario
                    findActivity.comment = comment
                } else {
                    // Si la actividad no existe, agrégala
                    activities.push({ activity_id: activityId, comment: comment })
                }

                // 4. Stringificar el campo activities
                const updatedActivities = JSON.stringify(activities)

                // Actualizar el registro con el nuevo campo activities
                const updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                                     SET activities = ?
                                     WHERE schedule_id = ?`

                const updateResult = await realDb.runAsync(updateQuery, [
                    updatedActivities,
                    scheduleId,
                ])
                console.log('Registro actualizado:', updateResult)
                return updateResult
            } else {
                // Si no se encontró ningún registro, crear uno nuevo
                const newActivities = JSON.stringify([
                    { activity_id: activityId, comment: comment },
                ])
                const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES}
                                     (_id, schedule_id, activities, status, date)
                                     VALUES (?, ?, ?, ?, ?)`

                const insertResult = await realDb.runAsync(insertQuery, [
                    scheduleId,
                    scheduleId,
                    newActivities,
                    1,
                    new Date().toISOString(),
                ])
                console.log('Registro creado:', insertResult)
                return insertResult
            }
        } catch (error) {
            console.error('Error:', error)
            throw error
        }
    }

    get_checkin_steps = async (store_id, schedule_id) => {
        console.log(
            '🚀 ~ schedulesService ~ get_checkin_steps for store_id:',
            store_id,
            'and schedule_id:',
            schedule_id,
        )

        if (isWeb()) {
            return false
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN_STEPS} WHERE store_id = ? AND schedule_id = ?`
        const params = [store_id, schedule_id]

        const result = await realDb.getAllAsync(query, params)
        if (result.length > 0) {
            console.log(
                '🚀 ~ checkin steps found for store_id and schedule_id:',
                result,
            )
            return result
        }

        console.log('🚀 ~ no checkin steps found for store_id and schedule_id')
        return false
    }

    get_checkin = async (store_id, schedule_id, distributor = false) => {
        if (isWeb()) {
            const db = await openDB()
            const transaction = db.transaction(
                [SCHEDULES_STORE_NAMES.CHECKIN],
                'readonly',
            )
            const objectStore = transaction.objectStore(
                SCHEDULES_STORE_NAMES.CHECKIN,
            )

            return new Promise((resolve, reject) => {
                const index = objectStore.index('store_id_schedule_id')
                const range = IDBKeyRange.bound(
                    [store_id, schedule_id],
                    [store_id, schedule_id],
                )
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        resolve(cursor.value)
                    } else {
                        console.log(
                            '🚀 ~ no checkin found for store_id and schedule_id',
                        )
                        resolve(false)
                    }
                }
                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            })
        }

        const realDb = await getRealDatabase()
        const query = distributor
            ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE distributor_id = ? AND schedule_id = ?`
            : `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE store_id = ? AND schedule_id = ?`
        const params = [store_id, schedule_id]

        const rows = await realDb.getAllAsync(query, params)
        if (rows.length > 0) {
            const check_ins = rows.map((item) => ({
                ...item,
                activities: item.activities ? JSON.parse(item.activities) : [],
            }))
            return check_ins
        }

        return false
    }
    get_advances_distributor = async (distributor_id, schedule_id) => {
        try {
            if (isWeb()) {
                return false
            }

            const realDb = await getRealDatabase()

            const query = `
            SELECT *
            FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
            WHERE distributor_id = ?
              AND schedule_id = ?
        `

            const params = [distributor_id, schedule_id]

            const rows = await realDb.getAllAsync(query, params)

            return rows.length > 0 ? rows : false
        } catch (error) {
            console.error('Transaction error - get_advances_distributor:', error)
            throw error
        }
    }

    get_checkout_by_id = async (checkin_id_local) => {
        if (isWeb()) {
            return false
        }

        const realDb = await getRealDatabase()

        try {
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE checkin_id_local = ? `
            const params = [checkin_id_local.toString()]

            const result = await realDb.getAllAsync(query, params)

            console.log('checkout result:', result)
            if (result && result.length > 0) {
                const modifiedCheckout = {
                    ...result[0],
                    forms_view: JSON.parse(result[0]['forms_view']),
                }
                console.log('modifiedCheckout => ', modifiedCheckout)
                return modifiedCheckout
            } else {
                return false
            }
        } catch (error) {
            console.error('Transaction error - get_checkout_by_id:', error)
            throw error
        }
    }

    update_checkout_key_checkinID = async (checkin_id_local, checkin_id) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKOUT} SET checkin_id = ? WHERE checkin_id_local = ?`
        const params = [checkin_id.toString(), checkin_id_local.toString()]

        const result = await realDb.runAsync(query, params)
        console.log('Checkout updated successfully:', result)
        return true
    }

    get_checkins_pending = async (status_pending_connection) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKIN} WHERE status =?`
        const params = [status_pending_connection]

        const result = await realDb.getAllAsync(query, params)
        return result.length > 0 ? result : []
    }
    get_checkouts_pending = async (status_pending_connection) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE status =?`
        const params = [status_pending_connection]

        const result = await realDb.getAllAsync(query, params)
        // console.log('result.rows._array: ', result)
        if (result.length > 0) {
            const updateResult = result.map((checkout) => {
                return {
                    ...checkout,
                    forms_view: JSON.parse(checkout.forms_view),
                }
            })
            return updateResult
        }

        return []
    }

    get_newstores_pending = async (status_pending_connection) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const posQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE campaign_status = ?`
        const posParams = [status_pending_connection]

        const pendingPos = await realDb.getAllAsync(posQuery, posParams)

        if (pendingPos.length > 0) {
            const storeIds = pendingPos.map((item) => `'${item.store_id}'`).join(',')
            const storeQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id IN (${storeIds})`

            const storesData = await realDb.getAllAsync(storeQuery, [])

            const combinedData = pendingPos.map((pos) => {
                let store = storesData.find((store) => store._id === pos.store_id)
                if (store) {
                    store = { ...store, schedule_id: pos.schedule_id }
                }
                return {
                    ...pos,
                    storeDetails: store || {},
                }
            })

            return combinedData
        }

        return []
    }

    updateCheckinStatus = async (_id, status, checkin_id) => {
        if (isWeb()) {
            return false
        }

        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKIN} SET status = ?, checkin_id = ? WHERE _id = ?`
        const params = [status, checkin_id, _id]

        const result = await realDb.runAsync(query, params)
        if (result.changes > 0) {
            console.log(`🚀 ~ Check-in with id ${_id} updated successfully`)
            return true
        }

        console.log(`🚀 ~ No check-in found with id ${_id} to update`)
        return false
    }
    updateCheckoutStatus = async (_id, status) => {
        if (isWeb()) {
            return false
        }

        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.CHECKOUT} SET status = ? WHERE _id = ?`
        const params = [status, _id]

        const result = await realDb.runAsync(query, params)
        if (result.changes > 0) {
            console.log(`🚀 ~ Checkout updated successfully`)
            return true
        }

        console.log(`🚀 ~ No checkout to update`)
        return false
    }

    add_checkout = async (data, checkout_id = '', distributor_id = '') => {
        if (!data) {
            throw new Error('Data is required')
        }

        if (isWeb()) {
            return
        }
        try {
            const db = await getDb()
            const query = `INSERT INTO ${TABLE_NAME.SCHEDULE_CHECKOUT} (
                                    date,
                                    store_id,
                                    lat_checkout,
                                    lng_checkout,
                                    checkout_id,
                                    checkin_id,
                                    schedule_id,
                                    date_finish,
                                    dif_utc,
                                    status,
                                    checkin_id_local,
                                    distributor_id,
                                    forms_view,
                                    steps_distributor
                                ) VALUES (?, ?, ?, ?, ?, ?, ?,?,?,?,?,?,?,?)`
            const params = [
                data.date,
                data.store_id,
                data.lat_checkout,
                data.lng_checkout,
                checkout_id,
                data.checkin_id,
                data.schedule_id,
                data.date_finish,
                data.dif_utc,
                data.status,
                data.checkin_id_local,
                distributor_id,
                data.forms_view,
                JSON.stringify(data.steps_distributor || []),
            ]

            const result = await db.run({ sql: query, params: params })
            return result
        } catch (error) {
            console.error('1Transaction error - add_checkout:', error)
            throw error
        }
    }

    update_status_store = async (
        storeId,
        scheduleId,
        newStatus,
        dateInit,
        distributor = false,
    ) => {
        const realDb = await getRealDatabase()

        let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                    SET status = ?
                    WHERE pos_id = ? AND schedule_id = ?;`

        if (distributor) {
            updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                             SET status = ?
                             WHERE distributor_id = ? AND schedule_id = ? AND status = ${FORM_SCHEDULE_STATUS.IN_PROGRESS};`
        }

        const result = await realDb.runAsync(updateQuery, [
            newStatus,
            storeId,
            scheduleId,
        ])

        if (result.changes == 0) {
            const insertQuery = distributor
                ? `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                (_id, distributor_id, schedule_id, status, date_init, date_modified)
                                VALUES (?,?,?,?,?,?);`
                : `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                                     (_id, pos_id, schedule_id, status, date_init, date_modified)
                                                     VALUES (?,?,?,?,?,?);`
            const newId = generateUniqueId()
            const insertResult = await realDb.runAsync(insertQuery, [
                newId,
                storeId,
                scheduleId,
                newStatus,
                dateInit,
                new Date().toISOString(),
            ])
            return insertResult
        }

        return result
    }

    get_checkout = async (store_id, schedule_id, distributor_id = false) => {
        if (isWeb()) {
            const db = await openDB()
            const transaction = db.transaction(
                [SCHEDULES_STORE_NAMES.CHECKOUT],
                'readonly',
            )
            const objectStore = transaction.objectStore(
                SCHEDULES_STORE_NAMES.CHECKOUT,
            )

            return new Promise((resolve, reject) => {
                const index = objectStore.index('store_id_schedule_id')
                const range = IDBKeyRange.bound(
                    [store_id, schedule_id],
                    [store_id, schedule_id],
                )
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const checkout = cursor.value
                        console.log(
                            '🚀 ~ CHECKOUT found for store_id and schedule_id:',
                            checkout,
                        )
                        resolve(checkout)
                    } else {
                        console.log(
                            '🚀 ~ no CHECKOUT found for store_id and schedule_id',
                        )
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            })
        }

        const realDb = await getRealDatabase()
        const query = distributor_id
            ? `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE distributor_id = ? AND schedule_id = ?`
            : `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT} WHERE store_id = ? AND schedule_id = ?`
        const params = [store_id, schedule_id]

        const result = await realDb.getAllAsync(query, params)
        if (result.length > 0) {
            return result
        }

        return false
    }
    get_all_checkout = async () => {
        if (isWeb()) {
            return false
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.CHECKOUT}`

        const result = await realDb.getAllAsync(query, [])
        if (result.length > 0) {
            console.log(
                '🚀 ~get_all_checkout found for store_id and schedule_id:',
                result,
            )
            return result
        }

        console.log(
            '🚀 ~ no get_all_checkout found for store_id and schedule_id:',
            result,
        )
        return false
    }

    get_total_checkin_count = async (schedule_id) => {
        if (isWeb()) {
            const db = await openDB()
            const transaction = db.transaction(
                [SCHEDULES_STORE_NAMES.CHECKIN],
                'readonly',
            )
            const objectStore = transaction.objectStore(
                SCHEDULES_STORE_NAMES.CHECKIN,
            )

            return new Promise((resolve, reject) => {
                const request = objectStore.count()

                request.onsuccess = (event) => {
                    const count = event.target.result
                    resolve(count)
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            })
        }

        const realDb = await getRealDatabase()
        const queryNew = `
                            SELECT COUNT(*) as count
                            FROM (
                                SELECT adv.pos_id, adv.schedule_id, adv.status
                                FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS adv
                                INNER JOIN (
                                    SELECT pos_id, schedule_id, MAX(date_modified) AS latest_date 
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                                    WHERE schedule_id = ? AND pos_id > 0
                                    GROUP BY pos_id, schedule_id
                                ) AS latest_adv
                                ON adv.pos_id = latest_adv.pos_id 
                                AND adv.schedule_id = latest_adv.schedule_id 
                                AND adv.date_modified = latest_adv.latest_date
                                INNER JOIN stores ON adv.pos_id = stores._id
                                WHERE adv.status IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION}, ${STORE_STATUS.INCIDENCE_REPORTED}, ${STORE_STATUS.INCIDENCE_REPORTED_PERMANENTE}, ${STORE_STATUS.INCIDENCE_REPORTED_CLOSE})
                            ) AS recent_advances;
                        `
        const params = [schedule_id]

        const result = await realDb.getFirstAsync(queryNew, params)
        if (result) {
            return result.count
        }

        console.log('🚀 ~ no checkins found')
        return 0
    }
    isFormComplete = async (
        form_id,
        store_id,
        schedule_id,
        status_complete,
        status_pending_connection,
        status_pending_form_moderation,
    ) => {
        if (isWeb()) {
            const db = await openDB()
            const transaction = db.transaction(
                [SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS],
                'readonly',
            )
            const objectStore = transaction.objectStore(
                SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS,
            )

            return new Promise((resolve, reject) => {
                const index = objectStore.index('form_id_store_id_schedule_id')
                const range = IDBKeyRange.bound(
                    [form_id, store_id, schedule_id],
                    [form_id, store_id, schedule_id],
                )
                const request = index.openCursor(range)

                request.onsuccess = (event) => {
                    const cursor = event.target.result
                    if (cursor) {
                        const record = cursor.value
                        if (record.status === 3) {
                            resolve(true)
                        } else {
                            console.log('🚀 ~ Formulario no completo:', record)
                            resolve(false)
                        }
                    } else {
                        console.log('🚀 ~ No se encontró el formulario')
                        resolve(false)
                    }
                }

                request.onerror = (event) => {
                    console.error('Transaction error:', event.target.error)
                    reject(event.target.error)
                }
            })
        }

        const generateIdDate = generateIdDateNow()
        const realDb = await getRealDatabase()
        const query = `SELECT status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} WHERE form_id = ? AND store_id = ? AND schedule_id = ? AND day_unique_id = ?;`
        const params = [form_id, store_id, schedule_id, generateIdDate]

        const result = await realDb.getFirstAsync(query, params)
        if (result) {
            const status = result.status
            if (
                status === status_complete ||
                status == status_pending_connection ||
                status == status_pending_form_moderation
            ) {
                return true
            }
            return false
        }

        return false
    }

    clearFqaAnswersById = async (fqaId, retryCount = 0) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 150

        if (isWeb()) {
            return
        }

        try {
            const realDb = await getRealDatabase()
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            SET answers = ?
                                            WHERE _id = ?;
                            `
            const params = ['[]', fqaId]

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [clearFqaAnswersById] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.clearFqaAnswersById(fqaId, retryCount + 1)
            }
            console.error('Error clearing fqa answers by id:', error)
            throw error
        }
    }

    deleteFqaById = async (fqaId, retryCount = 0) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 150

        if (isWeb()) {
            throw new Error(
                'La eliminación de registros no está soportada en la plataforma web.',
            )
        }

        try {
            const realDb = await getRealDatabase()
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE _id = ?;
                            `
            const params = [fqaId]

            const result = await realDb.runAsync(query, params)
            console.log(`✅ deleteFqaById: Registro ${fqaId} eliminado exitosamente`)
            return result
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [deleteFqaById] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.deleteFqaById(fqaId, retryCount + 1)
            }
            console.error('Error deleting fqa by id:', error)
            throw error
        }
    }

    /**
     * @description funcion para updatear el status de un registro de formulario de la tabla SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS
     * @param {*} status
     * @param {*} schedule_id
     * @param {*} form_id
     * @returns
     */
    updateStatusFormAnswerSchedule = async (
        status,
        schedule_id,
        form_id,
        store_id = null,
        from = 'null',
        register_id = null,
        checkin_id = null,
        retryCount = 0,
    ) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 150

        if (isWeb()) {
            return
        }

        try {
            const realDb = await getRealDatabase()
            const generateIdDate = generateIdDateNow()
            let querySelect
            let queryParamsSelect

            if (register_id) {
                querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                            WHERE _id = ?;`
                queryParamsSelect = [register_id]
            } else {
                if (store_id) {
                    querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?
                                ORDER BY _id DESC;`
                    queryParamsSelect = [
                        schedule_id,
                        form_id,
                        store_id,
                        generateIdDate,
                    ]
                } else {
                    querySelect = `SELECT _id, status FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?
                                ORDER BY _id DESC;`
                    queryParamsSelect = [schedule_id, form_id, generateIdDate]
                }
            }

            const selectResult = await realDb.getAllAsync(
                querySelect,
                queryParamsSelect,
            )

            // ✅ Si no se encuentra el registro y aún quedan reintentos, esperar y reintentar
            if (selectResult.length === 0) {
                if (retryCount < MAX_RETRIES) {
                    console.warn(
                        `⚠️ [updateStatusFormAnswerSchedule] Registro no encontrado, retrying ${retryCount + 1}/${MAX_RETRIES}... (form: ${form_id}, store: ${store_id})`,
                    )
                    await new Promise((resolve) =>
                        setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                    )
                    return this.updateStatusFormAnswerSchedule(
                        status,
                        schedule_id,
                        form_id,
                        store_id,
                        from,
                        register_id,
                        retryCount + 1,
                    )
                }
                // Si ya se agotaron los reintentos, lanzar error
                throw new Error('No se encontró el registro')
            }

            let updatedId
            const registerId = selectResult[0]._id
            const currentStatus = selectResult[0].status

            let query
            let queryParams

            if (register_id) {
                query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                SET status = ?
                                WHERE _id = ?;`

                queryParams = [status, register_id]

                updatedId = register_id
            } else {
                if (store_id) {
                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    SET status = ?
                                    WHERE _id = ?;`

                    queryParams = [status, registerId]

                    updatedId = registerId
                } else {
                    if (from == 'PENDING_CONNECTION') {
                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        SET status = ?
                                        WHERE _id = ?;` //AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION})

                        queryParams = [status, registerId]

                        updatedId = registerId
                    } else {
                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                            SET status = ?
                            WHERE _id =?;` //
                        // WHERE schedule_id = ? AND form_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});` //

                        queryParams = [status, registerId]
                        updatedId = registerId
                    }
                }
            }

            console.log(
                '💾 [updateStatusFormAnswerSchedule] SQL:',
                query.replace(/\s+/g, ' '),
            )
            console.log('💾 [updateStatusFormAnswerSchedule] PARAMS:', queryParams)

            const result = await realDb.runAsync(query, queryParams)

            console.log('✅ [updateStatusFormAnswerSchedule] UPDATE completado:', {
                updatedId,
                status,
                changes: result.changes,
            })

            // Actualizar FORMS_PENDING_SYNC solo si se proporcionó checkin_id (safe, sin cross-session).
            // Sin checkin_id no se toca FORMS_PENDING_SYNC para evitar contaminar otras sesiones.
            if (checkin_id) {
                // Reglas:
                // - PENDING_CONNECTION: primera vez que el form se completa (offline). Actualizar date y count+1.
                // - COMPLETED: sincronización confirmada por backend. Si ya estaba PENDING_CONNECTION,
                //   NO actualizar date (preservar la fecha real de completado) NI count (ya se contó).
                //   Si venía de otro estado (online directo), sí actualizar date y count+1.
                // - Otros: no tocar date ni count.
                const isPendingConnection =
                    status === FORM_SCHEDULE_STATUS.PENDING_CONNECTION
                const isCompleted = status === FORM_SCHEDULE_STATUS.COMPLETED

                let updateSql
                let updateParams
                if (isPendingConnection) {
                    // Primera completación offline: actualizar date y count
                    updateSql = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                 SET status = ?, date = datetime('now', 'localtime'), status_send = ?, count = count + 1
                                 WHERE checkin_id = ? AND activity_id = ?;`
                    updateParams = [
                        status,
                        STATUS_FORM_PENDING_SYNC.NO_SENDED,
                        checkin_id,
                        form_id,
                    ]
                } else if (isCompleted) {
                    // Sync confirmado: preservar date y count si ya era PENDING_CONNECTION
                    updateSql = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                 SET status = ?,
                                     date = CASE WHEN status = ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION} THEN date ELSE datetime('now', 'localtime') END,
                                     status_send = ?,
                                     count = CASE WHEN status = ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION} THEN count ELSE count + 1 END
                                 WHERE checkin_id = ? AND activity_id = ?;`
                    updateParams = [
                        status,
                        STATUS_FORM_PENDING_SYNC.NO_SENDED,
                        checkin_id,
                        form_id,
                    ]
                } else {
                    // IN_PROGRESS, PENDING, etc: solo actualizar status y status_send, no date ni count
                    updateSql = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                 SET status = ?, status_send = ?
                                 WHERE checkin_id = ? AND activity_id = ?;`
                    updateParams = [
                        status,
                        STATUS_FORM_PENDING_SYNC.NO_SENDED,
                        checkin_id,
                        form_id,
                    ]
                }

                await realDb.runAsync(updateSql, updateParams)
                console.log(
                    `[updateStatusFormAnswerSchedule] FORMS_PENDING_SYNC: checkin_id=${checkin_id} activity_id=${form_id} status=${status}`,
                )
            }

            return { ...result, updatedId }
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [updateStatusFormAnswerSchedule] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.updateStatusFormAnswerSchedule(
                    status,
                    schedule_id,
                    form_id,
                    store_id,
                    from,
                    register_id,
                    checkin_id,
                    retryCount + 1,
                )
            }
            console.error(
                'Transaction error: updateStatusFormAnswerSchedule =>',
                error,
            )
            throw error
        }
    }
    updateStatusSendForm = async (
        status,
        schedule_id,
        form_id,
        store_id = null,
        from = 'null',
        register_id = null,
        date_finish = null,
        retryCount = 0,
    ) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 150

        // 🔍 LOG: Caller y parámetros
        const caller = new Error().stack.split('\n')[2]?.trim() || 'unknown'
        console.log('📝 [updateStatusSendForm] INICIO', {
            caller,
            status,
            schedule_id,
            form_id,
            store_id,
            from,
            register_id,
            date_finish,
            retryCount,
        })

        if (isWeb()) {
            return
        }

        try {
            const realDb = await getRealDatabase()
            const generateIdDate = generateIdDateNow()

            let querySelect
            let queryParamsSelect

            if (register_id) {
                querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                            WHERE _id = ?;`
                queryParamsSelect = [register_id]
            } else {
                if (store_id) {
                    querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?
                                ORDER BY _id DESC;`
                    queryParamsSelect = [
                        schedule_id,
                        form_id,
                        store_id,
                        generateIdDate,
                    ]
                } else {
                    querySelect = `SELECT _id, status_send FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?
                                ORDER BY _id DESC;`
                    queryParamsSelect = [schedule_id, form_id, generateIdDate]
                }
            }

            const selectResult = await realDb.getAllAsync(
                querySelect,
                queryParamsSelect,
            )

            console.log(
                '🔍 [updateStatusSendForm] SELECT result:',
                selectResult.length > 0
                    ? {
                          _id: selectResult[0]._id,
                          current_status_send: selectResult[0].status_send,
                          new_status_send: status,
                      }
                    : 'NO RESULTS',
            )

            // ✅ Si no se encuentra el registro y aún quedan reintentos, esperar y reintentar
            if (selectResult.length === 0) {
                if (retryCount < MAX_RETRIES) {
                    console.warn(
                        `⚠️ [updateStatusSendForm] Registro no encontrado, retrying ${retryCount + 1}/${MAX_RETRIES}... (form: ${form_id}, store: ${store_id})`,
                    )
                    await new Promise((resolve) =>
                        setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                    )
                    return this.updateStatusSendForm(
                        status,
                        schedule_id,
                        form_id,
                        store_id,
                        from,
                        register_id,
                        date_finish,
                        retryCount + 1,
                    )
                }
                // Si ya se agotaron los reintentos, lanzar error
                throw new Error('No se encontró el registro')
            }

            // console.log('2930 ---- result.rows: ', selectResult)
            const registerId = selectResult[0]._id
            const currentStatus = selectResult[0].status

            let query
            let queryParams

            if (register_id) {
                if (date_finish != null) {
                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    SET status_send = ?, date_finish = ?
                                    WHERE _id = ?;`

                    queryParams = [status, date_finish, register_id]
                } else {
                    query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    SET status_send = ?
                                    WHERE _id = ?;`

                    queryParams = [status, register_id]
                }
            } else {
                if (store_id) {
                    if (date_finish != null) {
                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        SET status_send = ?, date_finish = ?
                                        WHERE _id = ?;`

                        queryParams = [status, date_finish, registerId]
                    } else {
                        query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                        SET status_send = ?
                                        WHERE _id = ?;`

                        queryParams = [status, registerId]
                    }
                } else {
                    if (from == 'PENDING_CONNECTION') {
                        if (date_finish != null) {
                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            SET status_send = ?, date_finish = ?
                                            WHERE _id = ?;`

                            queryParams = [status, date_finish, registerId]
                        } else {
                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            SET status_send = ?
                                            WHERE _id = ?;`

                            queryParams = [status, registerId]
                        }
                    } else {
                        if (date_finish != null) {
                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                SET status_send = ?, date_finish = ?
                                WHERE _id =?;`

                            queryParams = [status, date_finish, registerId]
                        } else {
                            query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                SET status_send = ?
                                WHERE _id =?;`

                            queryParams = [status, registerId]
                        }
                    }
                }
            }

            console.log('💾 [updateStatusSendForm] SQL:', query.replace(/\s+/g, ' '))
            console.log('💾 [updateStatusSendForm] PARAMS:', queryParams)

            const result = await realDb.runAsync(query, queryParams)

            console.log('✅ [updateStatusSendForm] UPDATE completado:', {
                registerId,
                status_send: status,
                changes: result.changes,
            })
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [updateStatusSendForm] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.updateStatusSendForm(
                    status,
                    schedule_id,
                    form_id,
                    store_id,
                    from,
                    register_id,
                    date_finish,
                    retryCount + 1,
                )
            }
            console.error('Transaction error: updateStatusSendForm =>', error)
            throw error
        }
    }

    getFormsAnswersByStatus = async (status) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const query = `SELECT * 
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE status = ?;`
        const queryParams = [status]

        const result = await realDb.getAllAsync(query, queryParams)
        return result.length > 0 ? result : []
    }

    getFormsAnswersByStatus2 = async (status1, status2, incidence = false) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        let query = `
                        SELECT *
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} AS fqa
                        WHERE (status_send = ? OR status = ?)
                    `

        let queryParams = [status1, status2]

        if (incidence) {
            query += `
                            AND store_id IS NOT NULL
                            AND store_id != ''
                        `
        } else {
            query += `
                            AND (
                                (store_id IS NOT NULL AND store_id != '' AND checkout = 0)
                                OR (store_id IS NOT NULL AND store_id != '' AND checkout = 1 AND EXISTS (
                                    SELECT 1
                                    FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS sa
                                    WHERE sa.status = 1
                                    AND sa.schedule_id = fqa.schedule_id
                                    AND sa.pos_id = fqa.store_id
                                    AND sa.date_modified = (
                                        SELECT MAX(sub_sa.date_modified)
                                        FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} AS sub_sa
                                        WHERE sub_sa.schedule_id = sa.schedule_id
                                        AND sub_sa.pos_id = sa.pos_id
                                    )
                                ))
                                OR (store_id IS NULL OR store_id = '')
                            )
                        `
        }

        query += ` ORDER BY date_finish ASC, _id ASC`

        const result = await realDb.getAllAsync(query, queryParams)
        if (result.length > 0) {
            if (incidence) {
                const updatesResults = result.filter((fqa) => {
                    const typeForm = JSON.parse(fqa.type_form)
                    return typeForm.type == FORM_TYPES.FORM_INCIDENCE
                })
                return updatesResults
            }
            return result
        }

        return []
    }

    /**
     * @description obtiene todos los formularios guardados en FORMS_QUESTION_ANSWERS para debugging
     * @param {number} limit - límite de registros a obtener (default: 100)
     * @returns {Promise<Array>} Array con todos los registros de formularios
     */
    getAllFormsAnswers = async (limit = 100) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const query = `
                        SELECT 
                            fqa._id,
                            fqa.answers,
                            fqa.name_form,
                            fqa.status,
                            fqa.status_send,
                            fqa.form_id,
                            fqa.store_id,
                            fqa.schedule_id,
                            fqa.date_init,
                            fqa.date_finish,
                            s.name as store_name,
                            s.code as store_code,
                            s.address as store_address
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} fqa
                        LEFT JOIN ${SCHEDULES_STORE_NAMES.STORES} s ON fqa.store_id = s._id
                        ORDER BY fqa.date_init DESC
                        LIMIT ?
                    `

        const result = await realDb.getAllAsync(query, [limit])
        if (result && result.length > 0) {
            const processedResults = result.map((row) => ({
                ...row,
                answers_count: row.answers ? JSON.parse(row.answers).length : 0,
                answers_preview: row.answers
                    ? JSON.parse(row.answers).slice(0, 2)
                    : [],
            }))
            return processedResults
        }

        return []
    }

    updateStatusCheckoutFormsQuestionsAnswers = async (
        schedule_id,
        store_id,
        status_update,
    ) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const generateIdDate = generateIdDateNow()

        const queryUpdate = `
                                UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                SET checkout = ?
                                WHERE schedule_id = ?
                                AND store_id = ?
                                AND day_unique_id = ?
                                AND checkout IS NULL;
                        `
        const paramsUpdate = [status_update, schedule_id, store_id, generateIdDate]

        const resultUpdate = await realDb.runAsync(queryUpdate, paramsUpdate)
        console.log(
            '✅ updateStatusCheckoutFormsQuestionsAnswers: Formularios actualizados del día actual:',
            {
                schedule_id,
                store_id,
                day_unique_id: generateIdDate,
                rowsAffected: resultUpdate.changes,
            },
        )
        return resultUpdate.changes
    }

    /**
     * @description funcion para traerme los registros de ese form y respuestas que guardo, devuelvo las asnwers, date_init, repeat, status, totalScore, scoreObtained de la tabla FORMS_QUESTION_ANSWERS
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {?} store_id
     * @returns {*} {answers, date_init, repeat, status, scoreObtained, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderar}
     */
    getFormsAnswers = async (
        schedule_id,
        form_id,
        store_id = null,
        register_id = null,
        repeat = null,
    ) => {
        console.log('🔍 DEBUG getFormsAnswers - Platform check:', {
            isWeb: isWeb(),
            platform: getPlatform().OS,
        })

        if (isWeb()) {
            const db = await openDB()
            console.log('🌐 WEB mode - db object:', {
                hasTransaction: typeof db?.transaction,
            })
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(
                    [SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS],
                    'readonly',
                )
                const objectStore = transaction.objectStore(
                    SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS,
                )
                const request = objectStore.getAll()

                request.onsuccess = (event) => {
                    const courses = event.target.result
                    resolve(courses)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            })
        }

        const realDb = await getRealDatabase()

        try {
            let query
            let queryParams

            const generateIdDate = generateIdDateNow()

            if (register_id) {
                query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id, scoresByKey, totalByKey
                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                    WHERE _id = ?;`
                queryParams = [register_id]
            } else {
                if (store_id) {
                    if (repeat && repeat == 1) {
                        query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id, scoresByKey, totalByKey
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                        queryParams = [schedule_id, form_id, store_id]
                    } else {
                        query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id, scoresByKey, totalByKey
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                        queryParams = [
                            schedule_id,
                            form_id,
                            store_id,
                            generateIdDate,
                        ]
                    }
                } else {
                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father, checkin_id
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                    queryParams = [schedule_id, form_id, generateIdDate]
                }
            }

            const result = await realDb.getAllAsync(query, queryParams)

            if (result && result.length > 0) {
                const rowToday = result[0]
                const answers = JSON.parse(rowToday.answers)
                const questions = JSON.parse(rowToday.questions)
                const type_form = JSON.parse(rowToday.type_form)
                const scoresByKey = JSON.parse(rowToday.scoresByKey) || null
                const totalByKey = JSON.parse(rowToday.totalByKey) || null

                console.log('🔍 DEBUG getFormsAnswers - Datos leídos de BD:', {
                    form_id: form_id,
                    heladera: rowToday.heladera,
                    heladera_tipo: typeof rowToday.heladera,
                    form_id_father: rowToday.form_id_father,
                    type_form_type: type_form?.type,
                    answersCount: answers.length,
                })

                return {
                    fqa_id: rowToday._id,
                    answers: answers,
                    date_init: rowToday.date_init,
                    date_finish: rowToday.date_finish,
                    status: rowToday.status,
                    repeat: rowToday.repeat,
                    totalScore: rowToday.totalScore,
                    scoreObtained: rowToday.scoreObtainedFloat,
                    cant_questions_yes_na: rowToday.cant_questions_yes_na,
                    progress_questions_yes_na: rowToday.progress_questions_yes_na,
                    scoreTotalQuestionsPonderar:
                        rowToday.scoreTotalQuestionsPonderarFloat,
                    name_form: rowToday.name_form,
                    questions: questions,
                    heladera: rowToday.heladera,
                    create_refrigerator: rowToday.create_refrigerator,
                    type_form: type_form,
                    form_id_father: rowToday.form_id_father ?? null,
                    checkin_id: rowToday.checkin_id ?? null,
                    scoresByKey:
                        scoresByKey && Object.keys(scoresByKey).length > 0
                            ? scoresByKey
                            : null,
                    totalByKey:
                        totalByKey && Object.keys(totalByKey).length > 0
                            ? totalByKey
                            : null,
                }
            } else {
                return {
                    answers: [],
                    date_init: null,
                    form: null,
                    scoresByKey: null,
                    totalByKey: null,
                }
            }
        } catch (error) {
            console.error('Error en la transacción getFormsAnswers - sqlite:', error)
            throw error
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de getFormsAnswersInStore del repository TypeScript.
     *
     * Mantiene la misma interfaz para no romper código existente.
     *
     * @description Obtiene formulario guardado en FORMS_QUESTION_ANSWERS filtrado por día de hoy
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} store_id - ID de la tienda (opcional)
     * @param {*} register_id - ID del registro específico (opcional)
     * @param {*} repeat - Flag de repetición (1 = formulario repetible)
     * @returns Promise con los datos del formulario
     */
    getFormsAnswersInStore = async (
        schedule_id,
        form_id,
        store_id = null,
        register_id = null,
        repeat = null,
    ) => {
        try {
            // ✅ Usar nueva implementación TypeScript del repository (import ES6)
            // console.log(`🚀 ~ getFormsAnswersInStore (wrapper) ~ Invocando nueva implementación TypeScript... schedule_id: ${schedule_id}, form_id: ${form_id}, store_id: ${store_id}, register_id: ${register_id}, repeat: ${repeat}`)
            const result = await getFormsAnswersInStoreNew(
                schedule_id,
                form_id,
                store_id,
                register_id,
                repeat,
            )

            // console.log('🚀 ~ getFormsAnswersInStore (wrapper) ~ result:', result.answers.length)

            return result
        } catch (error) {
            console.error('❌ Error en getFormsAnswersInStore (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    getFormsAnswersInStore_OLD = (schedule_id, form_id, store_id = null, register_id = null, repeat = null) => {
        const db = await openDB()

        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS], 'readonly')
                const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS)
                const request = objectStore.getAll()

                request.onsuccess = (event) => {
                    const courses = event.target.result
                    resolve(courses)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        const generateIdDate = generateIdDateNow()

                        let query
                        let queryParams

                        if (register_id) {
                            query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                WHERE _id = ?;`
                            queryParams = [register_id]
                        } else {
                            if (store_id) {
                                if (repeat && repeat == 1) {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION}) AND date(date_init, 'localtime') = date('now', 'localtime');`
                                    queryParams = [schedule_id, form_id, store_id]
                                } else {
                                    query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                            WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                                    queryParams = [schedule_id, form_id, store_id, generateIdDate]
                                }
                            } else {
                                query = `SELECT _id, answers, date_init, date_finish, repeat, status, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, questions, heladera, create_refrigerator, type_form, form_id_father
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                queryParams = [schedule_id, form_id, generateIdDate]
                            }
                        }

                        tx.executeSql(
                            query,
                            queryParams,
                            (_, result) => {
                                if (result.rows._array.length > 0) {
                                    const rowToday = result.rows.item(0)
                                    const answers = JSON.parse(rowToday.answers)
                                    const questions = JSON.parse(rowToday.questions)
                                    const type_form = JSON.parse(rowToday.type_form)
                                    resolve({
                                        fqa_id: rowToday._id,
                                        answers: answers,
                                        date_init: rowToday.date_init,
                                        date_finish: rowToday.date_finish,
                                        status: rowToday.status,
                                        repeat: rowToday.repeat,
                                        totalScore: rowToday.totalScore,
                                        scoreObtained: rowToday.scoreObtainedFloat,
                                        cant_questions_yes_na: rowToday.cant_questions_yes_na,
                                        progress_questions_yes_na: rowToday.progress_questions_yes_na,
                                        scoreTotalQuestionsPonderar: rowToday.scoreTotalQuestionsPonderarFloat,
                                        name_form: rowToday.name_form,
                                        questions: questions,
                                        heladera: rowToday.heladera,
                                        create_refrigerator: rowToday.create_refrigerator,
                                        type_form: type_form,
                                        form_id_father: rowToday.form_id_father,
                                    })
                                } else {
                                    resolve({
                                        answers: [],
                                        date_init: null,
                                        form: null,
                                    })
                                }
                            },
                            (error) => {
                                console.error('1111 - Error en la transacción getFormsAnswers :', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción getFormsAnswers - sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de updateFormAnswerSchedule del repository TypeScript.
     *
     * Mantiene la misma interfaz para no romper código existente.
     *
     * @description updatea la key answers del registro del formulario: agrega o updatea al array las answers que vaya respondiendo el user, debe tambien updatear la key scoreObtained en base a lo que me llega en la newAnswer.questionScore
     * @param {*} newAnswer - Objeto con la respuesta nueva
     * @param {*} schedule_id - ID del schedule
     * @param {*} form_id - ID del formulario
     * @param {*} store_id - ID de la tienda (opcional)
     * @param {*} remplaceAnswers - Si debe reemplazar todas las respuestas
     * @param {*} clearAnswers - Si debe limpiar respuestas
     * @param {*} type - Tipo de operación (para logs)
     * @returns Promise<number> - 1 si éxito, -1 si error
     */
    updateFormAnswerSchedule = async (
        newAnswer,
        schedule_id,
        form_id,
        store_id = null,
        remplaceAnswers = false,
        clearAnswers = false,
        type = 'null',
        day_unique_id = null, // ✅ Agregar parámetro day_unique_id opcional
    ) => {
        try {
            // ✅ Usar nueva implementación TypeScript del repository
            const result = await updateFormAnswerScheduleNew({
                newAnswer,
                schedule_id,
                form_id,
                store_id,
                replaceAnswers: remplaceAnswers,
                clearAnswers,
                type,
                day_unique_id, // ✅ Pasar day_unique_id si se proporciona
            })

            // Retornar en formato compatible con código antiguo
            return result.status === 1 ? 1 : -1
        } catch (error) {
            console.error('❌ Error en updateFormAnswerSchedule (wrapper):', error)
            throw error
        }
    }

    // ========================================================================
    // 🗑️ DEPRECATED - Implementación antigua (mantener comentada como referencia)
    // Para ver la implementación anterior, revisar git history
    // ========================================================================
    /*
    updateFormAnswerSchedule_OLD = (newAnswer, schedule_id, form_id, store_id = null, remplaceAnswers = false, clearAnswers = false, type = 'null') => {
        // Código antiguo comentado...
            try {
                const db = await openDB()

                // DEPRECATED: Mantener por compatibilidad pero queue ya maneja concurrencia
                if (schedulesService.isUpdating) {
                    console.log('⚠️ Otra operación está en curso (flag legacy). Por favor, espera.')
                    return Promise.reject('Otra operación está en curso.')
                }

                schedulesService.isUpdating = true

                let statusResponse = -1
                
                return new Promise((resolve, reject) => {
                    if (isWeb()) {
                        // Web handling
                    } else {
                        const generateIdDate = generateIdDateNow()
                        
                        // Validar day_unique_id
                        if (!generateIdDate || generateIdDate.length !== 8) {
                            const error = new Error(`❌ day_unique_id inválido: ${generateIdDate}`)
                            console.error(error.message)
                            reject(error)
                            return
                        }
                        
                        let query
                        let queryParams

                        if (store_id) {
                            query = `SELECT answers, status, date_init
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                            queryParams = [schedule_id, form_id, store_id, generateIdDate]
                        } else {
                            query = `SELECT answers, status, date_init
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                            queryParams = [schedule_id, form_id, generateIdDate]
                        }
                        db.transaction(
                            (tx) => {
                                tx.executeSql(
                                    query,
                                    queryParams,
                                    (_, result) => {
                                        if (result.rows._array.length > 0) {
                                            // Validar duplicados
                                            if (result.rows._array.length > 1) {
                                                console.warn('⚠️ Múltiples registros encontrados para el mismo formulario/día:', {
                                                    count: result.rows._array.length,
                                                    schedule_id,
                                                    form_id,
                                                    store_id,
                                                    day_unique_id: generateIdDate
                                                })
                                            }
                                            
                                            const today = new Date().toISOString().split('T')[0]
                                            let rowToday = null

                                            for (let i = 0; i < result.rows._array.length; i++) {
                                                const row = result.rows.item(i)
                                                if (row.date_init && row.date_init.split('T')[0] === today) {
                                                    rowToday = row
                                                    break
                                                }
                                            }

                                            if (!rowToday) {
                                                const dataLogs = {
                                                    query,
                                                    queryParams,
                                                    today,
                                                    result,
                                                }
                                                const dataCapture = {
                                                    log: JSON.stringify(dataLogs),
                                                    date: new Date().toISOString(),
                                                    end: '0updateFormAnswerSchedule',
                                                    additional: {
                                                        schedule_id: schedule_id,
                                                        form_id: form_id,
                                                        store_id: store_id,
                                                        newAnswer: newAnswer,
                                                    },
                                                }

                                                captureAndSaveDataError(dataCapture)
                                                reject('No existe registro para hoy')
                                                return
                                            }

                                            let status = result.rows.item(0).status
                                            if (status != undefined && status != FORM_SCHEDULE_STATUS.IN_PROGRESS) {
                                                this.updateStatusFormAnswerSchedule(
                                                    FORM_SCHEDULE_STATUS.IN_PROGRESS,
                                                    schedule_id,
                                                    form_id,
                                                    store_id,
                                                    `opcion:${type}`,
                                                )
                                            }

                                            let answers = JSON.parse(result.rows.item(0).answers || '[]')

                                            if (remplaceAnswers) {
                                                answers = []
                                            }

                                            let findIndex = -1

                                            if (newAnswer && newAnswer._id) {
                                                findIndex = answers.findIndex((answer) => answer._id.toString() === newAnswer._id.toString())
                                            }

                                            if (findIndex !== -1) {
                                                answers[findIndex] = { ...answers[findIndex], ...newAnswer }
                                            } else {
                                                if (newAnswer) answers.push(newAnswer)
                                            }

                                        const updatedAnswers = JSON.stringify(answers)
                                        // console.log(
                                        //     '1933 ----- 🚀 ~ file: schedules.database.js:1925 ~ schedulesService ~ db.transaction ~ updatedAnswers:',
                                        //     updatedAnswers,
                                        // )

                                        // actualizo
                                        let updateFormAnswer
                                        let insertQuery
                                        if (store_id) {
                                            updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET answers = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                            insertQuery = [updatedAnswers, schedule_id, form_id, store_id, generateIdDate]
                                        } else {
                                            updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET answers = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                                            // WHERE schedule_id = ? AND form_id = ?;`
                                            insertQuery = [updatedAnswers, schedule_id, form_id, generateIdDate]
                                        }
                                        tx.executeSql(
                                            updateFormAnswer,
                                            insertQuery,
                                            (_, result) => {
                                                // console.log('🟢 Registro actualizado con exito: updateFormAnswerSchedule')
                                                statusResponse = 1
                                                resolve({ status: statusResponse })
                                            },
                                            (_, error) => {
                                                console.error('Transaction error: updateFormAnswerSchedule =>', error)
                                                reject(error)
                                            },
                                        )

                                        // get de todas mis answers para caulcular mi suma total

                                        this.getFormsAnswers(schedule_id, form_id, store_id).then((getFormsAnswersResponse) => {
                                            if (getFormsAnswersResponse) {
                                                const answers = getFormsAnswersResponse.answers // las respuestas que guarde
                                                // console.log("1967 ---- 🚀 ~ file: schedules.database.js:1967 ~ schedulesService ~ this.getFormsAnswers ~ answers:", answers)
                                                const questions = getFormsAnswersResponse.questions // las respuestas que guarde
                                                // console.log()

                                                let answersToPonderate = answers.filter((obj) => obj.ponderar === true)
                                                let answersIDS = answersToPonderate.map((q) => q._id)
                                                // console.log('Answers IDs To Ponderate:', answersIDS)
                                                let cantQuestionPonderar = questions.filter((obj) => answersIDS.includes(obj._id))

                                                let scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce((acc, item) => {
                                                    const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                    return acc + (parseFloat(score) || 0)
                                                }, 0)
                                                // console.log(
                                                //     'Preguntas totales a ponderar >= ',
                                                //     cantQuestionPonderar.length,
                                                //     ' - scoreTotalQuestionsPonderar =>',
                                                //     scoreTotalQuestionsPonderar,
                                                // )
                                                let score_obtenido = 0
                                                let score_total_na = 0
                                                const answersNa = []

                                                for (const answer of answers) {
                                                    const isNa = isNaOptionSelected(answer)

                                                    if (answer.ponderar) {
                                                        if (isNa) {
                                                            answersNa.push(answer)
                                                            continue
                                                        }
                                                        // console.log('Sumo puntaje a => ', answer._id, ' - score: ', answer.score)
                                                        score_obtenido += parseFloat(answer.score)
                                                        // console.log('Puntaje obtenido actual =>', score_obtenido)
                                                    }
                                                }

                                                let hide_questions = []

                                                getFormsAnswersResponse.questions.forEach((item) => {
                                                    // recorro preguntas

                                                    let answerResponse = answers.find((answer) => answer.question_id === item._id) // reviso si conteste alguna pregunta

                                                    if (answerResponse !== undefined) {
                                                        if (item.input_type.type === 14) {
                                                            // tengo alguna de type 14 ?

                                                            // busco dentro de mis secondary alguna opcion que elegi
                                                            let secondarySelected = item.secondary_actions.find(
                                                                (action) => action._id === answerResponse.option_selected_id[0],
                                                            )

                                                            // la opcion elegida tiene hide_questions o show_questions
                                                            if (secondarySelected.hide_questions.length > 0) {
                                                                // hide_questions = secondarySelected.hide_questions

                                                                for (const question_id of secondarySelected.hide_questions) {
                                                                    hide_questions.push(question_id)
                                                                }
                                                            }
                                                        }

                                                        // si no tengo respuestas de type 14, pondero sobre todas las questions
                                                        // pero busco de mis questios si tengo alguna de type 14, si tengo y de esa no respondi, a la ponderacion le resto las que esten en hide_questions
                                                        // si respondi, le sumo las que estan en show questions
                                                    }
                                                })
                                                if (hide_questions.length > 0) {
                                                    // console.log("1767 ---- 🚀 ~ file: schedules.database.js:1767 ~ schedulesService ~ this.getFormsAnswers ~ hide_questions:", hide_questions)

                                                    const questionsTransform = getFormsAnswersResponse.questions

                                                    const cantQuestionPonderar = questionsTransform.filter(
                                                        (obj) => obj.ponderar === true && !hide_questions.includes(obj._id),
                                                    )

                                                    score_obtenido = 0
                                                    score_total_na = 0

                                                    const newAnswers = answers.filter((answ) => !hide_questions.includes(answ._id))

                                                    for (const answer of newAnswers) {
                                                        if (answer.ponderar) {
                                                            score_obtenido += parseFloat(answer.score)
                                                        }
                                                    }

                                                    scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce((acc, item) => {
                                                        const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                        return acc + (parseFloat(score) || 0)
                                                    }, 0)
                                                }

                                                if (answersNa.length > 0) {
                                                    const questionsPonderar = cantQuestionPonderar.filter(
                                                        (question) => !answersNa.map((answer) => answer.question_id).includes(question._id),
                                                    )

                                                    scoreTotalQuestionsPonderar = questionsPonderar.reduce((acc, item) => {
                                                        const score = item.questionScore > 0 ? item.questionScore : item.score > 0 ? item.score : 0
                                                        return acc + (parseFloat(score) || 0)
                                                    }, 0)
                                                    // console.log(
                                                    //     'Ajuste por NA - Preguntas totales a ponderar >= ',
                                                    //     questionsPonderar.length + 'SCORE ==>' + scoreTotalQuestionsPonderar,
                                                    // )
                                                }

                                                let progressPonderacion = (score_obtenido * 100) / scoreTotalQuestionsPonderar

                                                // Verifica si es un número entero o no
                                                if (progressPonderacion % 1 !== 0) {
                                                    // Si no es entero, redondea a 2 decimales
                                                    progressPonderacion = parseFloat(progressPonderacion.toFixed(2))
                                                }
                                                console.log('Progreso de la ponderación =>', progressPonderacion)
                                                if (
                                                    typeof progressPonderacion !== 'number' ||
                                                    isNaN(progressPonderacion) ||
                                                    !isFinite(progressPonderacion)
                                                ) {
                                                    progressPonderacion = 0
                                                }

                                                if (score_obtenido > 0) {
                                                    score_obtenido = parseFloat(score_obtenido.toFixed(2))
                                                }

                                                this.updateScoreObtainedFormAnswers(score_obtenido, schedule_id, form_id, store_id)
                                                    .then(() => {
                                                        // console.log('updateScoreObtainedFormAnswers => Actualizado con exito')
                                                    })
                                                    .catch((error) => {
                                                        console.error('updateScoreObtainedFormAnswers => Error: ', error)
                                                    })

                                                this.updateProgressPonderacionFormAnswers(progressPonderacion, schedule_id, form_id, store_id)
                                                    .then(() => {
                                                        // console.log('updateProgressPonderacionFormAnswers => Actualizado con exito')
                                                    })
                                                    .catch(() => {
                                                        console.error('updateProgressPonderacionFormAnswers => Error: ', error)
                                                    })
                                            }
                                        })
                                    } else {
                                        console.error('No se encontró el registro para actualizar updateFormAnswerSchedule')

                                        const dataCapture = {
                                            log: 'No se encontró el registro para actualizar updateFormAnswerSchedule',
                                            date: new Date().toISOString(),
                                            end: '1updateFormAnswerSchedule',
                                            additional: {
                                                schedule_id: schedule_id,
                                                form_id: form_id,
                                                store_id: store_id,
                                                newAnswer: newAnswer,
                                            },
                                        }

                                        captureAndSaveDataError(dataCapture)
                                        reject('No se encontró el registro para actualizar updateFormAnswerSchedule')
                                    }
                                },
                                (_, error) => {
                                    console.error('Error al recuperar el valor de answers:', error)

                                    const dataCapture = {
                                        log: JSON.stringify(error),
                                        date: new Date().toISOString(),
                                        end: '2updateFormAnswerSchedule',
                                        additional: {
                                            schedule_id: schedule_id,
                                            form_id: form_id,
                                            store_id: store_id,
                                            newAnswer: newAnswer,
                                        },
                                    }

                                    captureAndSaveDataError(dataCapture)

                                    reject(error)
                                },
                            )
                        },
                        (error) => {
                            console.error('Error en la transacción:', error)

                            const dataCapture = {
                                log: JSON.stringify(error),
                                date: new Date().toISOString(),
                                end: '3updateFormAnswerSchedule',
                                additional: {
                                    schedule_id: schedule_id,
                                    form_id: form_id,
                                    store_id: store_id,
                                    newAnswer: newAnswer,
                                },
                            }

                            captureAndSaveDataError(dataCapture)

                            reject(error)
                        },
                        () => {
                            // Success callback de la transacción
                            schedulesService.isUpdating = false
                        },
                    )
                }
            })
            } catch (error) {
                // Catch del try principal
                const dataCapture = {
                    log: JSON.stringify(error),
                    date: new Date().toISOString(),
                    end: '4updateFormAnswerSchedule - Outer catch',
                    additional: {
                        schedule_id: schedule_id,
                        form_id: form_id,
                        store_id: store_id,
                        newAnswer: newAnswer,
                    },
                }

                captureAndSaveDataError(dataCapture)
                throw error // Re-throw para que queue lo maneje
            } finally {
                // ✅ SIEMPRE resetear el flag, incluso si hay error
                schedulesService.isUpdating = false
            }
        })
    }
    */
    // ========================================================================
    // FIN DEPRECATED
    // ========================================================================

    // removeKeyOldResponseFormAnswerSchedule = (schedule_id, form_id, store_id = null, arrayHideQuestions) => {
    //     const db = await openDB()

    //     return new Promise((resolve, reject) => {
    //         if (isWeb()) {
    //         } else {
    //             let query
    //             let queryParams

    //             if (store_id) {
    //                 query = `SELECT answers
    //                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
    //                 queryParams = [schedule_id, form_id, store_id, generateIdDate]
    //             } else {
    //                 query = `SELECT answers
    //                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                         WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
    //                 queryParams = [schedule_id, form_id, generateIdDate]
    //             }
    //             db.transaction((tx) => {
    //                 tx.executeSql(
    //                     query,
    //                     queryParams,
    //                     (_, result) => {
    //                         if (result.rows.length > 0) {
    //                             this.updateStatusFormAnswerSchedule(FORM_SCHEDULE_STATUS.IN_PROGRESS, schedule_id, form_id, store_id)

    //                             let answers = JSON.parse(result.rows.item(0).answers || '[]')

    //                             if (answers && answers.length > 0) {
    //                                 answers = answers.filter((ans) => !arrayHideQuestions.includes(ans['_id']))
    //                             }

    //                             const updatedAnswers = JSON.stringify(answers)

    //                             // actualizo
    //                             let updateFormAnswer
    //                             let insertQuery
    //                             if (store_id) {
    //                                 updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                                                         SET answers = ?
    //                                                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
    //                                 insertQuery = [updatedAnswers, schedule_id, form_id, store_id, generateIdDate]
    //                             } else {
    //                                 updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
    //                                                         SET answers = ?
    //                                                         WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
    //                                 insertQuery = [updatedAnswers, schedule_id, form_id, generateIdDate]
    //                             }
    //                             tx.executeSql(
    //                                 updateFormAnswer,
    //                                 insertQuery,
    //                                 (_, result) => {
    //                                     //   console.log('🟢 Registro actualizado con exito: removeKeyOldResponseFormAnswerSchedule')
    //                                     resolve()
    //                                 },
    //                                 (_, error) => {
    //                                     console.error('Transaction error: removeKeyOldResponseFormAnswerSchedule =>', error)
    //                                     reject(error)
    //                                 },
    //                             )

    //                             // get de todas mis answers para caulcular mi suma total

    //                             this.getFormsAnswers(schedule_id, form_id, store_id).then((getFormsAnswersResponse) => {
    //                                 if (getFormsAnswersResponse) {
    //                                     const answers = getFormsAnswersResponse.answers // las respuestas que guarde
    //                                     const questions = getFormsAnswersResponse.questions // las respuestas que guarde
    //                                     console.log(answers.length, 'answers.length')
    //                                     let { scoreTotalQuestionsPonderar } = getCantQuestionPonderar(answers)

    //                                     let score_obtenido = 0
    //                                     let score_total_na = 0

    //                                     for (const answer of answers) {
    //                                         if (answer.ponderar) {
    //                                             score_obtenido += parseInt(answer.score)
    //                                             // if (answer.option_selected_text[0] === 'yes') {
    //                                             //     score_obtenido += answer.questionScore
    //                                             // }

    //                                             // // resto las na del score total posible
    //                                             // if (answer.option_selected_text[0] === 'na') {
    //                                             //     score_total_na += answer.questionScore
    //                                             // }
    //                                         }
    //                                     }

    //                                     let hide_questions = []

    //                                     getFormsAnswersResponse.questions.forEach((item) => {
    //                                         // recorro preguntas

    //                                         let answerResponse = answers.find((answer) => answer.question_id === item._id) // reviso si conteste alguna pregunta

    //                                         if (answerResponse !== undefined) {
    //                                             if (item.input_type.type === 14) {
    //                                                 // tengo alguna de type 14 ?

    //                                                 // busco dentro de mis secondary alguna opcion que elegi
    //                                                 let secondarySelected = item.secondary_actions.find(
    //                                                     (action) => action._id === answerResponse.option_selected_id[0],
    //                                                 )

    //                                                 // la opcion elegida tiene hide_questions o show_questions
    //                                                 if (secondarySelected.hide_questions.length > 0) {
    //                                                     // hide_questions = secondarySelected.hide_questions

    //                                                     for (const question_id of secondarySelected.hide_questions) {
    //                                                         hide_questions.push(question_id)
    //                                                     }
    //                                                 }
    //                                             }

    //                                             // si no tengo respuestas de type 14, pondero sobre todas las questions
    //                                             // pero busco de mis questios si tengo alguna de type 14, si tengo y de esa no respondi, a la ponderacion le resto las que esten en hide_questions
    //                                             // si respondi, le sumo las que estan en show questions
    //                                         }
    //                                     })
    //                                     if (hide_questions.length > 0) {
    //                                         // console.log("1767 ---- 🚀 ~ file: schedules.database.js:1767 ~ schedulesService ~ this.getFormsAnswers ~ hide_questions:", hide_questions)

    //                                         const questionsTransform = getFormsAnswersResponse.questions

    //                                         const cantQuestionPonderar = questionsTransform.filter(
    //                                             (obj) => obj.ponderar === true && !hide_questions.includes(obj._id),
    //                                         )

    //                                         score_obtenido = 0
    //                                         score_total_na = 0

    //                                         const newAnswers = answers.filter((answ) => !hide_questions.includes(answ._id))

    //                                         for (const answer of newAnswers) {
    //                                             if (answer.ponderar) {
    //                                                 score_obtenido += parseInt(answer.score)
    //                                                 // if (answer.option_selected_text[0] === 'yes') {
    //                                                 //     score_obtenido += answer.questionScore
    //                                                 // }

    //                                                 // if (answer.option_selected_text[0] === 'na') {
    //                                                 //     score_total_na += answer.questionScore
    //                                                 // }
    //                                             }
    //                                         }

    //                                         scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce(
    //                                             (accumulator, currentValue) => accumulator + currentValue.questionScore,
    //                                             0,
    //                                         )
    //                                     }

    //                                     let progressPonderacion = (score_obtenido * 100) / scoreTotalQuestionsPonderar
    //                                     // Verifica si es un número entero o no
    //                                     if (progressPonderacion % 1 !== 0) {
    //                                         // Si no es entero, redondea a 2 decimales
    //                                         progressPonderacion = parseFloat(progressPonderacion.toFixed(2))
    //                                     }
    //                                     if (typeof progressPonderacion !== 'number' || isNaN(progressPonderacion)) {
    //                                         progressPonderacion = 0
    //                                     }

    //                                     // updatear la key scoreObtained
    //                                     if (score_obtenido > 0) {
    //                                         score_obtenido = parseFloat(score_obtenido.toFixed(2))
    //                                     }
    //                                     console.log('SCORE OBTENIDO FINAL ===> ', score_obtenido)
    //                                     this.updateScoreObtainedFormAnswers(score_obtenido, schedule_id, form_id, store_id)
    //                                         .then(() => {
    //                                             // console.log('updateScoreObtainedFormAnswers => Actualizado con exito')
    //                                         })
    //                                         .catch((error) => {
    //                                             console.error('updateScoreObtainedFormAnswers => Error: ', error)
    //                                         })

    //                                     this.updateProgressPonderacionFormAnswers(parseInt(progressPonderacion), schedule_id, form_id, store_id)
    //                                         .then(() => {
    //                                             // console.log('updateProgressPonderacionFormAnswers => Actualizado con exito')
    //                                         })
    //                                         .catch(() => {
    //                                             console.error('updateProgressPonderacionFormAnswers => Error: ', error)
    //                                         })
    //                                 }
    //                             })
    //                         } else {
    //                             console.error('No se encontró el registro para actualizar removeKeyOldResponseFormAnswerSchedule')
    //                             reject('No se encontró el registro para actualizar removeKeyOldResponseFormAnswerSchedule')
    //                         }
    //                     },
    //                     (_, error) => {
    //                         console.error('Error al recuperar el valor de removeKeyOldResponseFormAnswerSchedule - answers:', error)
    //                         reject(error)
    //                     },
    //                 )
    //             })
    //         }
    //     })
    // }

    /**
     * @description funcion para updatear la key progress_questions_yes_na del registro de formulario de la tabla SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS
     * @param {*} progress
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {*} store_id
     * @returns
     */
    updateProgressPonderacionFormAnswers = async (
        progress,
        schedule_id,
        form_id,
        store_id,
    ) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            const generateIdDate = generateIdDateNow()
            let updateFormAnswer
            let insertQuery

            if (
                typeof progress !== 'number' ||
                isNaN(progress) ||
                !isFinite(progress)
            ) {
                const error = new Error(
                    'El valor de progress no es un número válido',
                )
                console.error(
                    'Validation error: progress - updateProgressPonderacionFormAnswers =>',
                    error,
                )
                throw error
            }

            if (store_id) {
                updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET progress_questions_yes_na = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                insertQuery = [
                    progress,
                    schedule_id,
                    form_id,
                    store_id,
                    generateIdDate,
                ]
            } else {
                updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET progress_questions_yes_na = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                insertQuery = [progress, schedule_id, form_id, generateIdDate]
            }

            await realDb.runAsync(updateFormAnswer, insertQuery)
        } catch (error) {
            console.error(
                'Transaction error: progress - updateProgressPonderacionFormAnswers =>',
                error,
            )
            throw error
        }
    }

    /**
     * @description updateo la key scoreObtained de un registro de formulario, es la suma de de la key questionScore de cada answer
     * @param {*} totalScoreAnswer
     * @param {*} schedule_id
     * @param {*} form_id
     * @param {*} store_id
     * @returns
     */
    updateScoreObtainedFormAnswers = async (
        totalScoreAnswer,
        schedule_id,
        form_id,
        store_id = null,
    ) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            const generateIdDate = generateIdDateNow()
            let updateFormAnswer
            let insertQuery

            if (store_id) {
                updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET scoreObtainedFloat = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                insertQuery = [
                    totalScoreAnswer,
                    schedule_id,
                    form_id,
                    store_id,
                    generateIdDate,
                ]
            } else {
                updateFormAnswer = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                                                SET scoreObtainedFloat = ?
                                                                WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                insertQuery = [
                    totalScoreAnswer,
                    schedule_id,
                    form_id,
                    generateIdDate,
                ]
            }

            await realDb.runAsync(updateFormAnswer, insertQuery)
        } catch (error) {
            console.error(
                'Transaction error: scoreObtained - updateScoreObtainedFormAnswers =>',
                error,
            )
            throw error
        }
    }

    /**
     * @description creo registo form, en donde guardo el id del form y una key answers que inicializa como un array vacio, a este array vacio despues le voy a pushear los objetos de las respuestas.
     * creo registro con status BLOCKED: 0
     *
     * @param {*} form
     * @returns
     */
    saveFormAnswers = async (form) => {
        if (isWeb()) {
            return
        }

        const {
            schedule_id,
            form_id,
            store_id,
            repeat,
            questions,
            progress,
            totalScore,
            answers = '[]',
            status,
            type_form,
            checkin_id,
        } = form

        // 🚨 DEBUG: Log para detectar llamadas problemáticas
        if (answers === '[]' && status && parseInt(status) === 5) {
            // console.log('🚨 ALERT: saveFormAnswers called with empty answers and completed status')
            // console.log('📋 Form details:', { form_id, schedule_id, store_id, status, answersLength: JSON.parse(answers).length })
        }

        if (
            !(
                JSON.parse(questions).length > 0 ||
                type_form.type == FORM_TYPES.FORM_REDIRECT
            )
        ) {
            return
        }

        const realDb = await getRealDatabase()

        const { cantQuestionPonderar, scoreTotalQuestionsPonderar } =
            getCantQuestionPonderar(questions)

        const date_init = getDateNow()
        const date_finish = ''

        try {
            let query
            let queryParams

            const generateIdDate = generateIdDateNow()

            if (store_id) {
                if (repeat == 1) {
                    query = `SELECT * 
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                    queryParams = [schedule_id, form_id, store_id]
                } else {
                    query = `SELECT * 
                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                    queryParams = [schedule_id, form_id, store_id, generateIdDate]
                }
            } else {
                query = `SELECT * 
                     FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                     WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ? AND status NOT IN (${FORM_SCHEDULE_STATUS.COMPLETED}, ${FORM_SCHEDULE_STATUS.PENDING_CONNECTION});`
                queryParams = [schedule_id, form_id, generateIdDate]
            }

            const result = await realDb.getAllAsync(query, queryParams)
            const registersExistent = result
            // console.log('registersExistent: ', registersExistent.length)

            const isExists = validateDateRegister(registersExistent)
            // console.log('isExists: ', isExists)

            if (!isExists) {
                // agrego el registro porq no existe
                let insert_form
                let insertQuery

                if (store_id) {
                    insert_form = [
                        repeat,
                        date_init,
                        date_finish,
                        schedule_id,
                        form_id,
                        store_id,
                        status,
                        answers,
                        questions,
                        progress,
                        totalScore,
                        form.scoreObtained,
                        cantQuestionPonderar,
                        form.progress_questions_yes_na,
                        scoreTotalQuestionsPonderar,
                        form.name_form,
                        form.heladera,
                        form.create_refrigerator,
                        JSON.stringify(form.type_form),
                        form.form_id_father,
                        form.checkout,
                        generateIdDate,
                        checkin_id,
                    ]
                    insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} (
                                        repeat, 
                                        date_init, 
                                        date_finish,
                                        schedule_id, 
                                        form_id, 
                                        store_id, 
                                        status, 
                                        answers, 
                                        questions, 
                                        progress, 
                                        totalScore, 
                                        scoreObtainedFloat, 
                                        cant_questions_yes_na, 
                                        progress_questions_yes_na, 
                                        scoreTotalQuestionsPonderarFloat, 
                                        name_form, 
                                        heladera, 
                                        create_refrigerator, 
                                        type_form, 
                                        form_id_father,
                                        checkout,
                                        day_unique_id,
                                        checkin_id
                                    ) VALUES (?, ?, ? , ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`
                } else {
                    insert_form = [
                        repeat,
                        date_init,
                        schedule_id,
                        form_id,
                        '',
                        status,
                        answers,
                        questions,
                        progress,
                        totalScore,
                        0,
                        cantQuestionPonderar,
                        0,
                        scoreTotalQuestionsPonderar,
                        form.name_form,
                        form.heladera,
                        form.create_refrigerator,
                        JSON.stringify(form.type_form),
                        form.form_id_father,
                        generateIdDate,
                        checkin_id,
                    ]
                    insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} (
                                        repeat, date_init, schedule_id, form_id, store_id, status, answers, questions, progress, totalScore, scoreObtainedFloat, cant_questions_yes_na, progress_questions_yes_na, scoreTotalQuestionsPonderarFloat, name_form, heladera, create_refrigerator, type_form, form_id_father, day_unique_id, checkin_id
                                    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`
                }

                await realDb.runAsync(insertQuery, insert_form)

                // busco el registro creado
                let getQuery
                let getQueryParams

                if (store_id) {
                    getQuery = `SELECT *
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`

                    getQueryParams = [schedule_id, form_id, store_id, generateIdDate]
                } else {
                    getQuery = `SELECT *
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`

                    getQueryParams = [schedule_id, form_id, generateIdDate]
                }

                const getResult = await realDb.getAllAsync(getQuery, getQueryParams)

                if (getResult.length > 0) {
                    return {
                        end: 'saveFormAnswers',
                        result: getResult[0],
                        message: 'Agrego el registro con exito: saveFormAnswers',
                    }
                } else {
                    return {
                        end: 'saveFormAnswers',
                        result: null,
                        message:
                            'saveFormAnswers: quiero devolver el registro creado pero no se encontro..',
                    }
                }
            } else {
                //  ya existe el registro no hago nada
                let getQuery
                let getQueryParams

                if (store_id) {
                    getQuery = `SELECT *
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`

                    getQueryParams = [schedule_id, form_id, store_id, generateIdDate]
                } else {
                    getQuery = `SELECT *
                                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`

                    getQueryParams = [schedule_id, form_id, generateIdDate]
                }

                const getResult = await realDb.getAllAsync(getQuery, getQueryParams)

                if (getResult.length > 0) {
                    const existingRecord = getResult[0]
                    // console.log('3909 -- el registro existe')
                    // console.log("3451 --- 🚀 ~ schedulesService ~ existingRecord:", existingRecord)
                    const existingAnswers = JSON.parse(existingRecord.answers)
                    // console.log('4913 -- existingAnswers.length:', existingAnswers.length)
                    const newAnswers = JSON.parse(answers)
                    // console.log("4915 --- 🚀 ~ schedulesService ~ newAnswers:", newAnswers)
                    // console.log('4916 ---- status => ', status, typeof status)
                    const newStatus = status ? parseInt(status) : status
                    const existingStatus = existingRecord.status
                        ? parseInt(existingRecord.status)
                        : existingRecord.status

                    let needsUpdate = false

                    // Comparar la clave moderation dentro de answers
                    for (let i = 0; i < newAnswers.length; i++) {
                        const newAnswer = newAnswers[i]
                        const existingAnswer = existingAnswers.find(
                            (ans) => ans._id === newAnswer._id,
                        )
                        if (existingAnswer) {
                            if (
                                JSON.stringify(existingAnswer.moderation) !==
                                JSON.stringify(newAnswer.moderation)
                            ) {
                                needsUpdate = true
                                break
                            }
                        } else {
                            needsUpdate = true
                            break
                        }
                    }

                    // console.log('4937 - status => ', status, 'existingStatus => ', existingStatus, 'newStatus => ', newStatus)
                    if (status && existingStatus != newStatus) {
                        needsUpdate = true
                    }

                    if (needsUpdate) {
                        // console.log('3940 --- updateo anwers.. estas => ', existingAnswers)
                        // console.log('3941 --- updateo anwers.. por estas => ', newAnswers)

                        // 🚨 VALIDACIÓN: No sobrescribir respuestas existentes con array vacío
                        let answersToUpdate = newAnswers
                        if (newAnswers.length === 0 && existingAnswers.length > 0) {
                            // console.log('⚠️ PREVENTED: Se intentó sobrescribir respuestas existentes con array vacío')
                            // console.log('📋 Existing answers length:', existingAnswers.length)
                            // console.log('📋 Form ID:', form_id, 'Status:', newStatus)

                            answersToUpdate = existingAnswers //TODO: Mantener las respuestas existentes
                        }

                        let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} SET answers = ?`
                        const updateParams = [JSON.stringify(answersToUpdate)]

                        if (status) {
                            updateQuery += `, status = ?`
                            updateParams.push(newStatus)
                        }

                        updateQuery += ` WHERE schedule_id = ? AND form_id = ?`
                        updateParams.push(schedule_id, form_id)

                        if (store_id) {
                            updateQuery += ` AND store_id = ?`
                            updateParams.push(store_id)
                        }

                        if (generateIdDate) {
                            updateQuery += ` AND day_unique_id = ?`
                            updateParams.push(generateIdDate)
                        }

                        updateQuery += `;`

                        console.log('😎query de actualizacion: ', updateQuery)
                        console.log('😎params de actualizacion: ', updateParams)

                        await realDb.runAsync(updateQuery, updateParams)

                        const updatedResult = await realDb.getAllAsync(
                            getQuery,
                            getQueryParams,
                        )

                        if (updatedResult.length > 0) {
                            const updatedRecord = updatedResult[0]
                            // console.log("34888 --- 🚀 ~ schedulesService ~ updatedRecord:", updatedRecord)
                            return {
                                end: 'saveFormAnswers',
                                result: updatedRecord,
                                message: 'Registro actualizado con éxito',
                            }
                        } else {
                            throw new Error(
                                'No se pudo obtener el registro actualizado',
                            )
                        }
                    } else {
                        // console.log('3493 --- NO UPDATEO NADA --')
                        return {
                            end: 'saveFormAnswers',
                            result: existingRecord,
                            message:
                                'El registro ya existe y no se necesita actualizar',
                        }
                    }
                } else {
                    return {
                        end: 'saveFormAnswers',
                        result: null,
                        message:
                            'saveFormAnswers: quiero devolver el registro que ya existe pero no se encontro..',
                    }
                }
            }
        } catch (error) {
            console.error('Transaction saveFormAnswers error:', error)
            throw error
        }
    }

    /**
     * @description funcion para validar que se hayan respondido todas las respuestas del formulario
     * @param {*} form
     * @returns {Promise<{message: string; complete: boolean}>}
     */
    isReponseTotalQuestions = async (form) => {
        if (isWeb()) {
            return {
                message: 'isReponseTotalQuestions: Web not supported',
                complete: false,
            }
        }

        const realDb = await getRealDatabase()
        const { schedule_id, form_id, store_id } = form

        try {
            const generateIdDate = generateIdDateNow()
            let query
            let queryParams

            if (store_id) {
                query = `SELECT * 
                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                         WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                queryParams = [schedule_id, form_id, store_id, generateIdDate]
            } else {
                query = `SELECT * 
                         FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                         WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                queryParams = [schedule_id, form_id, generateIdDate]
            }

            const result = await realDb.getAllAsync(query, queryParams)

            let complete = false
            if (result.length > 0) {
                const form_questions = JSON.parse(result[0].questions)
                const form_answers = JSON.parse(result[0].answers)
                console.log('result[0]: ', result[0])
                if (
                    form_questions &&
                    Array.isArray(form_questions) &&
                    form_questions.length > 0 &&
                    form_answers &&
                    Array.isArray(form_answers) &&
                    form_answers.length > 0
                ) {
                    if (form_questions.length === form_answers.length) {
                        console.log(
                            '-------- form_questions.length: ',
                            form_questions.length,
                        )
                        console.log(
                            '-------- form_answers.length: ',
                            form_answers.length,
                        )
                        complete = true
                    }
                }

                return {
                    message: 'isReponseTotalQuestions: Registro encontrado',
                    complete: complete,
                }
            } else {
                return {
                    message:
                        'isReponseTotalQuestions: No se encontraron registros..',
                    complete: complete,
                }
            }
        } catch (error) {
            console.error('Error in isReponseTotalQuestions:', error)
            throw error
        }
    }

    /**
     * @description funcion para validar si esta dentro de la distancia de la tienda para saber si puede o no hacer checkin
     * @param {string} store_id id de la tienda a la que quiere realizar checkin
     * @param {{lat ; lng}} current_distance a cuanta distancia se encuentra el usuario que quiere hacer checking
     * @returns {Promise<boolean>}
     */
    isValidDistanceCheckin = async (store_id, current_distance) => {
        if (isWeb()) {
            return true
        }

        const realDb = await getRealDatabase()

        let { lat, lng } = current_distance

        if (lat == null || lat == undefined || lat == '') {
            lat = 0
        }

        if (lng == null || lng == undefined || lng == '') {
            lng = 0
        }

        try {
            let query = `
                            SELECT maximum_distance_for_checkin, lat, lng
                            FROM ${SCHEDULES_STORE_NAMES.STORES}
                            WHERE _id = ?;
                `
            let params = [store_id]

            const result = await realDb.getAllAsync(query, params)

            if (result.length === 0) {
                throw new Error('Store not found')
            }

            const maximum_distance_for_checkin =
                result[0].maximum_distance_for_checkin
            const latStore = result[0].lat
            const lngStore = result[0].lng

            if (lat == 0 || lng == 0) {
                return true
            }

            if (latStore == 0 || lngStore == 0) {
                return true
            }

            if (latStore == null || latStore == undefined || latStore == '') {
                return true
            }

            if (lngStore == null || lngStore == undefined || lngStore == '') {
                return true
            }

            if (
                !maximum_distance_for_checkin ||
                maximum_distance_for_checkin === 0
            ) {
                return true
            } else {
                // a cuantos metros de distancia se encuentra mi tienda
                const store_distance = distanceInMeters(lat, lng, latStore, lngStore)

                if (store_distance <= maximum_distance_for_checkin) {
                    console.log(
                        'la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin',
                    ) // la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin
                    return true
                } else {
                    console.log(
                        'la distancia en m de la que estoy de la tienda es mayor a la maxima seteada por la tienda para hacer checkin',
                    ) // la distancia en m de la que estoy de la tienda es menor o igual a la maxima seteada por la tienda para hacer checkin
                    return false
                }
            }
        } catch (error) {
            console.error('No se pudo obtener la tienda:', error)
            throw error
        }
    }

    addForm = async (
        form,
        wise = false,
        heladera = false,
        create_refrigerator = false,
        form_id_father = null,
        retryCount = 0,
    ) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 100

        if (isWeb()) {
            const db = await openDB()
            // En el caso de web, puedes manejar la inserción del formulario de la misma manera que la tienda
            const transaction = db.transaction(
                [SCHEDULES_STORE_NAMES.FORMS],
                'readwrite',
            )
            const objectStore = transaction.objectStore(SCHEDULES_STORE_NAMES.FORMS)

            return new Promise((resolve, reject) => {
                const request = objectStore.add(form)
                request.onsuccess = () => resolve()
                request.onerror = (event) => reject(event.target.error)
            })
        }

        try {
            const realDb = await getRealDatabase()

            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id = ?`,
                [form._id],
            )
            const count = result.count || 0

            if (count === 0) {
                // ✅ Validación defensiva: Asegurar que todos los campos tengan valores válidos
                const safeForm = {
                    _id: form._id || '',
                    index_form: form.index_form ?? 0,
                    repeat: !!form.repeat,
                    can_repeat: !!form.can_repeat,
                    icon: form.icon || '',
                    check_in: !!form.check_in,
                    name_form: form.name_form || '',
                    icon_check: form.icon_check || '',
                    type_form: form.type_form || {},
                    contents: Array.isArray(form.contents) ? form.contents : [],
                    segments: form.segments || {},
                    progress: !!form.progress,
                    totalScore: form.totalScore ?? 0,
                    show_barcode: !!form.show_barcode,
                    tags: Array.isArray(form.tags) ? form.tags : [],
                    sub_title: form.sub_title || '',
                    description: form.description || '',
                }

                // ✅ Serializar campos JSON
                const serializedFields = {
                    type_form: JSON.stringify(safeForm.type_form),
                    contents: JSON.stringify(safeForm.contents),
                    segments: JSON.stringify(safeForm.segments),
                    tags: JSON.stringify(safeForm.tags),
                }

                let insertForm = [
                    safeForm._id,
                    safeForm.index_form,
                    safeForm.repeat ? 1 : 0,
                    safeForm.can_repeat ? 1 : 0,
                    safeForm.icon,
                    safeForm.check_in ? 1 : 0,
                    safeForm.name_form,
                    safeForm.icon_check,
                    serializedFields.type_form,
                    serializedFields.contents,
                    serializedFields.segments,
                    0, // status
                    safeForm.progress ? 1 : 0,
                    safeForm.totalScore,
                    safeForm.show_barcode ? 1 : 0,
                    serializedFields.tags,
                    wise ? 1 : 0,
                    heladera ? 1 : 0,
                    create_refrigerator ? 1 : 0,
                    form_id_father || null,
                    safeForm.sub_title,
                    safeForm.description,
                ]

                await realDb.runAsync(
                    `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS} (
                        _id,
                        index_form,
                        repeat,
                        can_repeat,
                        icon,
                        check_in,
                        name_form,
                        icon_check,
                        type_form,
                        contents,
                        segments,
                        status,
                        progress,
                        totalScore,
                        show_barcode,
                        tags,
                        wise,
                        heladera,
                        create_refrigerator,
                        form_id_father,
                        sub_title,
                        description
                    ) VALUES (?, ?, ?, ?,?, ?, ?, ?,?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`,
                    insertForm,
                )
                console.log(
                    '✅ [addForm] Formulario guardado:',
                    safeForm._id,
                    safeForm.name_form,
                )
            } else {
                if (heladera) {
                    await this.updateQuestionForms(form)
                }
            }
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [addForm] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.addForm(
                    form,
                    wise,
                    heladera,
                    create_refrigerator,
                    form_id_father,
                    retryCount + 1,
                )
            }

            // ✅ Log detallado del error con información del formulario
            console.error('❌ [addForm] Error insertando formulario:', {
                error: error.message,
                form_id: form._id,
                form_name: form.name_form,
                wise,
            })

            throw error
        }
    }

    updateQuestionForms = async (form, retryCount = 0) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 100

        try {
            const realDb = await getRealDatabase()

            let query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS}
                SET contents = ?
                WHERE _id = ?;`

            let queryParams = [JSON.stringify(form.contents || []), form._id]

            const result = await realDb.runAsync(query, queryParams)
            return result
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [updateQuestionForms] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.updateQuestionForms(form, retryCount + 1)
            }
            console.error('Transaction updateQuestionForms Error => ', error)
            throw error
        }
    }

    addAllForms = async (
        forms,
        wise = false,
        heladera = false,
        create_refrigerator = false,
        form_id_father = null,
        batchSize = 1000,
    ) => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            let startIndex = 0

            while (startIndex < forms.length) {
                const batchForms = forms.slice(startIndex, startIndex + batchSize)

                if (batchForms.length > 0) {
                    const placeholders = batchForms
                        .map(
                            () =>
                                '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
                        )
                        .join(',')

                    const values = batchForms.flatMap((form) => [
                        form._id,
                        form.index_form,
                        form.repeat,
                        form.can_repeat,
                        form.icon,
                        form.check_in,
                        form.name_form,
                        form.icon_check,
                        JSON.stringify(form.type_form),
                        JSON.stringify(form.contents),
                        JSON.stringify(form.segments),
                        0, // status
                        form.progress,
                        form.totalScore,
                        form.show_barcode,
                        JSON.stringify(form.tags),
                        wise,
                        heladera,
                        create_refrigerator,
                        form_id_father,
                        form.start_ejecution,
                        form.end_ejecution,
                        form.link,
                        form.objective,
                        form.weigh,
                        form.sub_title,
                        form.description,
                    ])

                    await realDb.runAsync(
                        `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.FORMS} (
                            _id,
                            index_form,
                            repeat,
                            can_repeat,
                            icon,
                            check_in,
                            name_form,
                            icon_check,
                            type_form,
                            contents,
                            segments,
                            status,
                            progress,
                            totalScore,
                            show_barcode,
                            tags,
                            wise,
                            heladera,
                            create_refrigerator,
                            form_id_father,
                            start_ejecution,
                            end_ejecution,
                            link,
                            objective,
                            weigh,
                            sub_title,
                            description
                        ) VALUES ${placeholders};`,
                        values,
                    )

                    startIndex += batchSize
                }
            }
        } catch (error) {
            console.error('Error en addAllForms:', error)
            throw error
        }
    }

    getFormsWise = async () => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE wise = 1;`,
                [],
            )

            console.log('forms: ', result.length)

            try {
                for (const form of result) {
                    form.contents = JSON.parse(form.contents)
                    form.type_form = JSON.parse(form.type_form)
                }
            } catch (error) {}

            return result
        } catch (error) {
            console.error('Error en la transacción getFormsWise -sqlite:', error)
            throw error
        }
    }

    deleteFormAnswer = async (
        schedule_id,
        form_id,
        store_id = null,
        day_unique_id = null,
        retryCount = 0,
    ) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 100

        if (isWeb()) {
            throw new Error(
                'La eliminación de registros no está soportada en la plataforma web.',
            )
        }

        try {
            const realDb = await getRealDatabase()
            const generateIdDate = day_unique_id || generateIdDateNow()
            let query
            let queryParams

            if (store_id) {
                query = `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                        WHERE schedule_id = ? AND form_id = ? AND store_id = ? AND day_unique_id = ?;`
                queryParams = [schedule_id, form_id, store_id, generateIdDate]
            } else {
                query = `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                                        WHERE schedule_id = ? AND form_id = ? AND day_unique_id = ?;`
                queryParams = [schedule_id, form_id, generateIdDate]
            }

            const result = await realDb.runAsync(query, queryParams)
            return result.changes
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [deleteFormAnswer] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.deleteFormAnswer(
                    schedule_id,
                    form_id,
                    store_id,
                    day_unique_id,
                    retryCount + 1,
                )
            }
            console.error('deleteFormAnswer error:', error)
            throw error
        }
    }

    getAllCampaigns = async () => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS}`,
                [],
            )

            const campaigns = result.map((row) => ({
                _id: row._id,
                name: row.name,
                code: row.code,
                image: row.image,
                date_int: row.date_init,
                date_finish: row.date_finish,
                steps: JSON.parse(row.steps),
                total_progres: row.total_progres,
                scheduleIds: JSON.parse(row.schedules),
                values: JSON.parse(row.camp_values),
                schedules: [],
                schedule_free: row.schedule_free,
                date_moderation_init: row.date_moderation_init,
                date_moderation_finish: row.date_moderation_finish,
                moderation: row.moderation,
            }))

            // Fetch schedules details for all campaigns using Promise.all
            const schedulePromises = campaigns.map(async (campaign) => {
                const scheduleDetailPromises = campaign.scheduleIds.map(
                    async (scheduleId) => {
                        const scheduleResult = await realDb.getAllAsync(
                            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES} WHERE _id = ?`,
                            [scheduleId],
                        )

                        if (scheduleResult.length > 0) {
                            const scheduleRow = scheduleResult[0]
                            return {
                                _id: scheduleRow._id,
                                date_finish: scheduleRow.date_finish,
                                date_init: scheduleRow.date_init,
                                form_finish:
                                    scheduleRow.form_finish.length > 6
                                        ? JSON.parse(scheduleRow.form_finish)
                                        : null,
                                form_init:
                                    scheduleRow.form_init.length > 6
                                        ? JSON.parse(scheduleRow.form_init)
                                        : null,
                                stores: [],
                                progress_diary: scheduleRow.progress_diary,
                                additional: JSON.parse(scheduleRow.additional),
                                distributor: JSON.parse(scheduleRow.distributor),
                                activities: JSON.parse(scheduleRow.activities),
                                others_activities: JSON.parse(
                                    scheduleRow.others_activities,
                                ),
                                vacation: scheduleRow.vacation,
                                activities_advances: JSON.parse(
                                    scheduleRow.activities_advances,
                                ),
                                others_activities_advances: JSON.parse(
                                    scheduleRow.others_activities_advances,
                                ),
                                forms_additionals: JSON.parse(
                                    scheduleRow.forms_additionals,
                                ),
                                forms_incidence: scheduleRow.forms_incidence,
                                reports: JSON.parse(scheduleRow.reports),
                                data_version: scheduleRow.data_version,
                            }
                        }
                        throw new Error('Schedule not found')
                    },
                )

                campaign.schedules = await Promise.all(scheduleDetailPromises)
            })

            await Promise.all(schedulePromises)
            return campaigns
        } catch (error) {
            console.error('Transaction error - getAllCampaigns:', error)
            throw error
        }
    }

    addCampaing = async (campaing) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.CAMPAINGS} WHERE _id = ?`,
                [campaing._id],
            )

            const count = result.length
            let agendas_ids = campaing.schedules.map((item) => item._id)

            if (count === 0) {
                let insertstore = [
                    campaing._id,
                    campaing.name,
                    campaing.code,
                    campaing.image,
                    campaing.date_int,
                    campaing.date_finish,
                    JSON.stringify(campaing.steps || {}),
                    campaing.total_progres,
                    JSON.stringify(agendas_ids),
                    JSON.stringify(campaing.values),
                    campaing.schedule_free,
                    campaing.date_moderation_init,
                    campaing.date_moderation_finish,
                    campaing.moderation,
                    campaing.campaing_subtitle ?? '',
                ]

                const insertResult = await realDb.runAsync(
                    `INSERT INTO ${SCHEDULES_STORE_NAMES.CAMPAINGS} (
                        _id,
                        name,
                        code,
                        image,
                        date_init,
                        date_finish,
                        steps,
                        total_progres,
                        schedules,
                        camp_values,
                        schedule_free,
                        date_moderation_init,
                        date_moderation_finish,
                        moderation,
                        campaing_subtitle
                    ) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?);`,
                    insertstore,
                )
                return insertResult
            } else {
                const existingCampaing = result[0]
                if (
                    existingCampaing.date_moderation_init !==
                        campaing.date_moderation_init ||
                    existingCampaing.date_moderation_finish !==
                        campaing.date_moderation_finish ||
                    existingCampaing.moderation !== campaing.moderation
                ) {
                    const updateResult = await realDb.runAsync(
                        `UPDATE ${SCHEDULES_STORE_NAMES.CAMPAINGS} SET 
                        name = ?, 
                        code = ?, 
                        image = ?, 
                        date_init = ?, 
                        date_finish = ?, 
                        steps = ?, 
                        total_progres = ?, 
                        schedules = ?, 
                        camp_values = ?, 
                        schedule_free = ?, 
                        date_moderation_init = ?, 
                        date_moderation_finish = ?, 
                        moderation = ?
                        WHERE _id = ?`,
                        [
                            campaing.name,
                            campaing.code,
                            campaing.image,
                            campaing.date_int,
                            campaing.date_finish,
                            JSON.stringify(campaing.steps || {}),
                            campaing.total_progres,
                            JSON.stringify(agendas_ids),
                            JSON.stringify(campaing.values),
                            campaing.schedule_free,
                            campaing.date_moderation_init,
                            campaing.date_moderation_finish,
                            campaing.moderation,
                            campaing._id,
                        ],
                    )
                    return updateResult
                }
                return
            }
        } catch (error) {
            console.error('Transaction addstore error:', error)
            throw error
        }
    }

    addAllStoresPFrelation = async (data = [], batchSize = 1000) => {
        if (isWeb()) {
            return false
        }

        if (!Array.isArray(data) || data.length === 0) {
            return true
        }

        // ✅ Native usa getRealDatabase
        const db = await getRealDatabase()

        try {
            let startIndex = 0

            while (startIndex < data.length) {
                const batch = data.slice(startIndex, startIndex + batchSize)

                const placeholders = batch.map(() => '(?,?,?)').join(', ')
                const values = batch.flatMap((item) => [
                    item._id ?? item.pos_id,
                    item.pos_id,
                    JSON.stringify(item.forms_ids ?? []),
                ])

                await db.runAsync(
                    `
                INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION} (
                    _id,
                    pos_id,
                    forms_id
                ) VALUES ${placeholders}
                `,
                    values,
                )

                startIndex += batchSize
            }

            return true
        } catch (error) {
            console.error('addAllStoresPFrelation - Transaction error:', error)
            throw error
        }
    }

    addInfoCreateStores = async (data) => {
        if (!data) {
            throw new Error('Data is required')
        }

        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            const query = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.CREATE_STORES} 
                           (_id, forms, areas, maximum_distance_for_checkin) 
                           VALUES (?, ?,?,?)`
            const params = [
                '1',
                JSON.stringify(data.forms),
                JSON.stringify(data.areas || []),
                data.maximum_distance_for_checkin || 0,
            ]

            const result = await realDb.runAsync(query, params)
            console.log('POSInfo entry added or replaced successfully:', result)
            return result
        } catch (error) {
            console.error('Transaction error - addInfoCreateStores:', error)
            throw error
        }
    }

    getInfoCreateStores = async () => {
        console.log('🏪 getInfoCreateStores (wrapper)')

        // ✅ Usar la nueva implementación
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.getInfoCreateStores()
    }

    /**
     * @description crea store en pos_schedule y stores db sqlite
     */
    addStoreLocal = async (data, forms = []) => {
        console.log('🏪 addStoreLocal (wrapper) =>', data?.name)

        if (!data) {
            return Promise.reject(new Error('Data is required'))
        }

        // ✅ Usar la nueva implementación
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.addStoreLocal(data, forms)
    }

    addSchedulePos = async (schedule_pos) => {
        if (isWeb()) {
            throw new Error('web not found')
        }

        const realDb = await getRealDatabase()
        const existsInDB = schedule_pos.status_send == STATUS_SEND.SEND

        let schedulePosParams = [
            schedule_pos._id,
            schedule_pos.store_id,
            schedule_pos.schedule_id,
            JSON.stringify(schedule_pos.forms),
            JSON.stringify([]),
            JSON.stringify([]),
            STORE_STATUS.PENDING,
            schedule_pos.lat,
            schedule_pos.lng,
            existsInDB,
            schedule_pos.status_send,
        ]

        let schedulePosQuery = `
            INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
            (_id, store_id, schedule_id, forms, forms_relevant, forms_featured, campaign_status, lat, lng, existsInDB, status_send) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);
        `

        try {
            await realDb.runAsync(schedulePosQuery, schedulePosParams)
            const result = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE _id = ?`,
                [schedule_pos._id],
            )
            return result
        } catch (error) {
            console.error('addSchedulePos error:', error)
            throw error
        }
    }

    addPos = async (store) => {
        if (isWeb()) {
            throw new Error('web not found')
        }

        const realDb = await getRealDatabase()
        const existsInDB = store.status_send == STATUS_SEND.SEND

        try {
            let storeQuery = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES} 
            (_id, name, address, lat, lng, code, cluster, area, region, format, flag, maximum_distance_for_checkin, segment, distributor_id, existsInDB, organization_id, status_send) 
            VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`
            let storeParams = [
                store._id,
                store.name,
                store.address || '',
                parseFloat(store.lat || 0),
                parseFloat(store.lng || 0),
                store.code || '',
                store.cluster || '',
                store.area || '',
                store.region || '',
                store.format || '',
                store.flag || '',
                store.maximum_distance_for_checking || 0,
                store.store_segment || '',
                store.distributor_id || null,
                existsInDB,
                store.organization_id || null,
                store.status_send || STATUS_SEND.SEND,
            ]

            await realDb.runAsync(storeQuery, storeParams)

            // hago el select despues del insert
            const querySelect = `
                SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES} 
                WHERE _id = ?
             `
            const insertedPos = await realDb.getFirstAsync(querySelect, [store._id])
            return insertedPos
        } catch (error) {
            console.error('Transaction error => addPos:', error)
            throw error
        }
    }

    addStoreAndSchedulePos = async (
        dataStore,
        dataSchedulePos,
        forms = [],
        isCreateSuccess,
        data,
        organization_id,
    ) => {
        // const status = isCreateSuccess ? STORE_STATUS.PENDING : STORE_STATUS.PENDING_CONNECTION
        const status = STORE_STATUS.PENDING

        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const newId = generateUniqueId()

        let posParams = isCreateSuccess
            ? [
                  dataSchedulePos._id,
                  dataSchedulePos.store_id,
                  dataSchedulePos.schedule_id,
                  dataSchedulePos.forms,
                  JSON.stringify([]),
                  JSON.stringify([]),
                  status,
                  dataStore.lat,
                  dataStore.lng,
                  isCreateSuccess,
              ]
            : [
                  newId,
                  newId,
                  data.schedule_id,
                  forms,
                  JSON.stringify([]),
                  JSON.stringify([]),
                  status,
                  data.lat,
                  data.lng,
                  false,
              ]

        let storeParams = isCreateSuccess
            ? [
                  dataStore._id,
                  dataStore.name,
                  dataStore.address || '',
                  parseFloat(dataStore.lat || 0),
                  parseFloat(dataStore.lng || 0),
                  dataStore.code || '',
                  dataStore.cluster || '',
                  dataStore.area || '',
                  dataStore.region || '',
                  dataStore.format || '',
                  dataStore.flag || '',
                  dataStore.maximum_distance_for_checking || 0,
                  dataStore.store_segment || '',
                  dataStore.distributor_id || null,
                  isCreateSuccess,
                  organization_id,
              ]
            : [
                  newId,
                  data.name,
                  data.address || '',
                  parseFloat(data.lat || 0),
                  parseFloat(data.lng || 0),
                  data.code || '',
                  data.cluster || '',
                  data.area || '',
                  data.region || '',
                  data.format || '',
                  data.flag || '',
                  data.maximum_distance_for_checking || 0,
                  data.store_segment || '',
                  data.distributor_id || null,
                  false,
                  organization_id,
              ]

        try {
            // Inserción en la tabla SCHEDULES_POS
            const posQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} 
            (_id, store_id, schedule_id, forms, forms_relevant, forms_featured, campaign_status, lat, lng, existsInDB) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`

            await realDb.runAsync(posQuery, posParams)
            console.log('POS entry added successfully')

            // Inserción en la tabla STORES
            const storeQuery = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES} 
            (_id, name, address, lat, lng, code, cluster, area, region, format, flag, maximum_distance_for_checkin, segment, distributor_id, existsInDB, organization_id) 
            VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`

            const result = await realDb.runAsync(storeQuery, storeParams)
            console.log('Store entry added successfully')
            return result
        } catch (error) {
            console.error('Transaction error addStoreAndSchedulePos:', error)
            throw error
        }
    }

    updateStoreRecords = async (oldStoreId, newStoreId) => {
        console.log(
            '🔄 updateStoreRecords (wrapper) =>',
            oldStoreId,
            '→',
            newStoreId,
        )

        // ✅ Usar la nueva implementación TypeScript
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.updateStoreRecords(oldStoreId, newStoreId)
    }

    /**
     * 🆕 Limpia tiendas locales temporales cuando ya existe la versión definitiva
     * @description Elimina tiendas con _id que comience con "local_" si ya existe
     * la tienda definitiva en la base de datos (verificando _id_mongoDB)
     * @returns Promise con estadísticas de limpieza
     */
    cleanupLocalStores = async () => {
        console.log('🧹 cleanupLocalStores (wrapper)')

        // ✅ Usar la nueva implementación TypeScript
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.cleanupLocalStores()
    }

    getExistsInDB = async (store_id) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT existsInDB, _id_mongoDB FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?;`,
                [store_id],
            )

            const existsInDB = result.existsInDB
            const _id_mongoDB = result._id_mongoDB
            return { existsInDB, _id_mongoDB }
        } catch (error) {
            console.error('Error en la transacción getExistsInDB:', error)
            throw error
        }
    }

    getPerfectStoreRelationByStore = async (store_id) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()

        try {
            const store = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION} WHERE pos_id = ?;`,
                [store_id],
            )

            if (store) {
                store.forms_id = JSON.parse(store.forms_id)

                const sql = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PERFECT_STORE} WHERE _id IN (${store.forms_id
                    .map(() => '?')
                    .join(',')});`

                const forms = await realDb.getAllAsync(sql, store.forms_id)

                if (forms.length > 0) {
                    const formsUpdate = forms.map((form) => {
                        return {
                            ...form,
                            medias: JSON.parse(form.medias),
                            type: JSON.parse(form.type),
                            tags: JSON.parse(form.tags) || [],
                        }
                    })
                    return formsUpdate
                } else {
                    return []
                }
            } else {
                return []
            }
        } catch (error) {
            console.error(
                'Error en la transacción getPerfectStoreRelationByStore:',
                error,
            )
            throw error
        }
    }

    hasStoresInDatabase = async (totalStores) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES};`,
                [],
            )

            const count = result.count || 0
            console.log('🚀 ~ hasStoresInDatabase ~ stores guardados.. :', count)
            console.log(
                '🚀 ~ hasStoresInDatabase ~ stores que deberia tener.. :',
                totalStores,
            )

            return count == totalStores
        } catch (error) {
            console.error('hasStoresInDatabase - Transaction error:', error)
            throw error
        }
    }

    hasSchedulePosInDatabase = async (totalSchedulePos) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`,
                [],
            )

            const count = result.count || 0
            console.log(
                '🚀 ~ hasSchedulePosInDatabase ~ schedule_pos guardados.. :',
                count,
            )
            console.log(
                '🚀 ~ hasSchedulePosInDatabase ~ schedule_pos que deberia tener.. :',
                totalSchedulePos,
            )
            return count == totalSchedulePos
        } catch (error) {
            console.error('hasSchedulePosInDatabase - Transaction error:', error)
            throw error
        }
    }

    getCountSchedulePos = async () => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS};`,
                [],
            )

            const count = result.count || 0
            return count
        } catch (error) {
            console.error('getCountSchedulePos - Transaction error:', error)
            throw error
        }
    }

    getCountStores = async () => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES};`,
                [],
            )

            const count = result.count || 0
            return count
        } catch (error) {
            console.error('getCountStores - Transaction error:', error)
            throw error
        }
    }

    getCountForms = async () => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS};`,
                [],
            )

            const count = result.count || 0
            return count
        } catch (error) {
            console.error('getCountFormsStore - Transaction error:', error)
            throw error
        }
    }

    hasFormsPerfectStoreInDatabase = async (totalFormsPerfecStore) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_PERFECT_STORE};`,
                [],
            )

            const count = result.count || 0
            console.log(
                '🚀 ~ hasFormsPerfectStoreInDatabase ~ FormsPerfecStore guardados.. :',
                count,
            )
            console.log(
                '🚀 ~ hasFormsPerfectStoreInDatabase ~ totalFormsPerfecStore que deberia tener.. :',
                totalFormsPerfecStore,
            )
            return count == totalFormsPerfecStore
        } catch (error) {
            console.error(
                'hasFormsPerfectStoreInDatabase - Transaction error:',
                error,
            )
            throw error
        }
    }

    hasRelationPerfectStoreInDatabase = async (totalRelationPerfectStore) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.STORES_PF_RELATION};`,
                [],
            )

            const count = result.count || 0
            console.log(
                '🚀 ~ hasRelationPerfectStoreInDatabase ~ relation ps guardados.. :',
                count,
            )
            console.log(
                '🚀 ~ hasRelationPerfectStoreInDatabase ~ relation ps que deberia tener.. :',
                totalRelationPerfectStore,
            )
            return count == totalRelationPerfectStore
        } catch (error) {
            console.error(
                'hasRelationPerfectStoreInDatabase - Transaction error:',
                error,
            )
            throw error
        }
    }

    CheckSavedRecords = async (table, total) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getFirstAsync(
                `
                SELECT COUNT(*) as count 
                FROM ${table}
                WHERE wise = 0;`,
                [],
            )

            const count = result.count || 0
            return count >= total
        } catch (error) {
            console.error('CheckSavedRecords - Transaction error:', error)
            return false
        }
    }

    savePendingImage = async (filename, pickerResult_uri) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        if (!filename || filename === '') {
            console.warn('savePendingImage: filename vacío, no se guarda')
            return false
        }

        const realDb = await getRealDatabase()

        try {
            // Verificar si ya existe la imagen
            const existingImage = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} WHERE filename = ?`,
                [filename],
            )

            if (existingImage) {
                console.log(
                    'Imagen ya existe en la tabla de pendientes, no se guarda nuevamente.',
                )
                return false // Ya existe, no se guarda
            } else {
                // Insertar la imagen si no existe
                const insertQuery = `INSERT INTO ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} (
                    filename,
                    pickerResult_uri
                ) VALUES (?, ?)`

                await realDb.runAsync(insertQuery, [filename, pickerResult_uri])
                console.log('Imagen guardada en la tabla de pendientes.')
                return true // Guardado exitoso
            }
        } catch (error) {
            console.error('Transaction error - savePendingImage:', error)
            throw error
        }
    }

    getPendingsImage = async () => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING}`
            const rows = await realDb.getAllAsync(query, [])
            return rows
        } catch (error) {
            console.error('Transaction error - getPendingsImage:', error)
            throw error
        }
    }

    deletePendingImage = async (filename) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.UPLOAD_IMAGES_PENDING} WHERE filename = ?`
            const result = await realDb.runAsync(query, [filename])

            if (result.changes > 0) {
                console.log(
                    `Imagen con filename "${filename}" eliminada correctamente.`,
                )
                return true // Imagen eliminada
            } else {
                console.log(
                    `No se encontró una imagen con filename "${filename}" para eliminar.`,
                )
                return false // No se encontró la imagen
            }
        } catch (error) {
            console.error('Transaction error - deletePendingImage:', error)
            throw error
        }
    }

    updateFormsTotals = async (
        schedule_id,
        form_id,
        count = null,
        count_of_done = null,
        name_form = null,
    ) => {
        if (isWeb()) {
            console.warn(
                'updateFormsTotals no está disponible en la plataforma web.',
            )
            return null
        }

        const realDb = await getRealDatabase()

        try {
            const whereClause = `_id = ? AND schedule_id = ?`
            const selectParams = [form_id, schedule_id]

            // Consulta SELECT
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} WHERE ${whereClause};`
            const result = await realDb.getAllAsync(query, selectParams)

            if (result.length > 0) {
                // Registro existente: actualizar valores
                let updateQuery = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} SET `
                let updateParams = []

                if (count !== null) {
                    updateQuery += `count = count + ?, `
                    updateParams.push(count)
                }
                if (count_of_done !== null) {
                    updateQuery += `count_of_done = count_of_done + ?, `
                    updateParams.push(count_of_done)
                }

                // Remover la coma final y añadir cláusula WHERE
                updateQuery = updateQuery.slice(0, -2) + ` WHERE ${whereClause};`
                updateParams.push(...selectParams)

                const updateResult = await realDb.runAsync(updateQuery, updateParams)
                return updateResult
            } else {
                if (count != null) {
                    // Registro no existente: insertar nuevo
                    const insertQuery = `
                        INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_TOTALS} 
                        (_id, schedule_id, name, count, count_of_done) 
                        VALUES (?, ?, ?, ?, ?);
                    `
                    const insertParams = [
                        form_id,
                        schedule_id,
                        name_form || '',
                        count || 0,
                        count_of_done || 0,
                    ]

                    const insertResult = await realDb.runAsync(
                        insertQuery,
                        insertParams,
                    )
                    return insertResult
                }
            }
        } catch (error) {
            console.error('Error en updateFormsTotals:', error)
            throw error
        }
    }

    get_name_forms = async (forms_required_names, forms, forms_store) => {
        if (isWeb()) {
            return []
        }

        const realDb = await getRealDatabase()
        const result = await realDb.getAllAsync(
            `SELECT name_form, _id FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${forms_store.map(() => '?').join(',')})`,
            [...forms_store],
        )

        if (result.length > 0) {
            const get_forms = result
            let result_forms = []
            forms_required_names.map((name) => {
                const form_name = get_forms.find((f) => f.name_form == name)
                if (form_name && forms.some((f) => f == form_name._id)) {
                    result_forms.push({
                        _id: form_name._id,
                        name: form_name.name_form,
                    })
                }
            })
            return result_forms
        }

        return []
    }
    getScheduleStore = async (scheduleId, storeId) => {
        const realDb = await getRealDatabase()
        const result = await realDb.getAllAsync(
            `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE schedule_id = ? AND store_id = ? ;`,
            [scheduleId, storeId],
        )

        if (result.length > 0) {
            let schedule_store = {
                _id: result[0]._id,
                forms: JSON.parse(result[0].forms),
                store_id: result[0].store_id,
                status: result[0].status,
            }
            return schedule_store
        }

        return null
    }
    deleteAllTablesTotalForms = async () => {
        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()
        const countResult = await realDb.getFirstAsync(
            `SELECT COUNT(*) as count FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS};`,
            [],
        )

        if (countResult && countResult.count > 0) {
            await realDb.runAsync(
                `DELETE FROM ${SCHEDULES_STORE_NAMES.FORMS_TOTALS};`,
                [],
            )
        }
    }
    upsertScheduleAdvanceLocal = async (
        schedule_id,
        store_id,
        status,
        dateInit,
        forms = [],
        objectives = [],
        textAnswer = null,
    ) => {
        if (isWeb()) {
            console.warn(
                'upsertScheduleAdvanceLocal no está disponible en la plataforma web.',
            )
            return null
        }

        const realDb = await getRealDatabase()

        try {
            const normalizedObjectives = Array.isArray(objectives) ? objectives : []

            const normalizedForms = Array.isArray(forms) ? forms : []

            // Obtén la fecha local en formato YYYY-MM-DD (corregido: no UTC)
            const _now = new Date()
            _now.setMinutes(_now.getMinutes() - _now.getTimezoneOffset())
            const today = _now.toISOString().split('T')[0]

            // Consulta el registro existente para la fecha local de hoy
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                 WHERE schedule_id = ? AND pos_id = ? AND date_init >= ?`,
                [schedule_id, store_id, today],
            )

            let existingRecord = result.length > 0 ? result[0] : null
            let existingForms = []
            if (existingRecord && existingRecord.forms) {
                try {
                    existingForms = JSON.parse(existingRecord.forms)
                } catch (parseError) {
                    console.error('Error parsing existing forms:', parseError)
                    throw parseError
                }
            }

            if (
                normalizedForms.length > 0 &&
                (existingForms.length == 0 ||
                    !existingForms.find((f) => f._id == normalizedForms[0]['_id']))
            ) {
                existingForms.push(normalizedForms[0])
            }

            const query = existingRecord
                ? `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                   SET forms = ?
                   WHERE _id = ?`
                : `INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES}
                   (_id, schedule_id, pos_id, status, date_init, forms, objectives, date_modified, incidence_answer)
                   VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`

                   console.log('upsertScheduleAdvanceLocal - Query:', query)
            const formsJson = JSON.stringify(normalizedForms)
            const objectivesJson = JSON.stringify(normalizedObjectives)
            const uniqueId = generateUniqueId()

            let safeTextAnswer = ''
            if (textAnswer !== null && textAnswer !== undefined) {
                safeTextAnswer = String(textAnswer)
            }

            const values = existingRecord
                ? [JSON.stringify(existingForms), existingRecord._id]
                : [
                      uniqueId,
                      schedule_id,
                      store_id,
                      status,
                      today,
                      formsJson,
                      objectivesJson,
                      today,
                      safeTextAnswer,
                  ]

            const updateResult = await realDb.runAsync(query, values)
            return updateResult
        } catch (error) {
            console.error('Transaction error upsertScheduleAdvanceLocal:', error)
            throw error
        }
    }
    findAndUpdateStatusProgres = async (schedule_id, store_id) => {
        if (isWeb()) {
            console.warn(
                'findAndUpdateStatusProgres no está disponible en la plataforma web.',
            )
            return null
        }

        const realDb = await getRealDatabase()

        try {
            // fecha local en formato YYYY-MM-DD
            const today = new Date().toISOString().split('T')[0]

            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                 WHERE schedule_id = ? AND pos_id = ? AND date_init >= ?`,
                [schedule_id, store_id, today],
            )

            const records = result
            if (records.length === 0) {
                return false // No hay registro para hoy
            }

            // Obtiene el registro
            const record = records[0]
            let objectives = []
            let forms = []
            try {
                if (record.objectives) {
                    objectives = JSON.parse(record.objectives)
                }
                if (record.forms) {
                    forms = JSON.parse(record.forms)
                }
            } catch (parseError) {
                console.error('Error parsing objectives or forms:', parseError)
                throw parseError
            }

            if (objectives.length === 0 || forms.length == 0) {
                return false
            }

            // Verifica si todos los objectives están en forms
            const allObjectivesCompleted = objectives.every((objective) =>
                forms.some((form) => form._id === objective._id),
            )

            return allObjectivesCompleted // Devuelve true si todos los objetivos están completados
        } catch (error) {
            console.error('Transaction error findAndUpdateStatusProgres:', error)
            throw error
        }
    }
    countCompletedObjectivesForSchedule = async (schedule_id) => {
        if (isWeb()) {
            console.warn(
                'countCompletedObjectivesForSchedule no está disponible en la plataforma web.',
            )
            return 0
        }

        const realDb = await getRealDatabase()

        try {
            const today = new Date().toISOString().split('T')[0]
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_ADVANCES} 
                 WHERE schedule_id = ?`,
                [schedule_id],
            )

            const records = result

            if (records.length === 0) {
                return 0 // No hay registros para la agenda
            }

            let completedCount = 0
            let storesCounted = new Set()

            records.forEach((record) => {
                let objectives = []
                let forms = []

                // Parsear objectives y forms
                try {
                    if (record.objectives) {
                        objectives = JSON.parse(record.objectives)
                    }
                    if (record.forms) {
                        forms = JSON.parse(record.forms)
                    }
                } catch (parseError) {
                    console.error('Error parsing objectives or forms:', parseError)
                    throw parseError
                }

                //? Si una tienda es cerrada por sistema sin completar los forms, figura como visitada pero no cuenta al objetivo
                //Verifica si todos los objectives están en forms

                if (
                    objectives &&
                    objectives.length > 0 &&
                    forms &&
                    forms.length > 0 &&
                    !storesCounted.has(record.pos_id)
                ) {
                    const allObjectivesCompleted = objectives.every((objective) =>
                        forms.some((form) => form._id === objective._id),
                    )

                    if (allObjectivesCompleted) {
                        storesCounted.add(record.pos_id)
                        completedCount++
                    }
                }
                // if (objectives && objectives.length > 0 && forms && forms.length > 0) {
                //     const allObjectivesCompleted = objectives.every((objective) => forms.some((form) => form._id === objective._id))

                //     if (allObjectivesCompleted) {
                //         storesCounted.add(record.pos_id);
                //         completedCount++
                //     }
                // }
            })

            return completedCount // Devuelve el total de registros completados
        } catch (error) {
            console.error(
                'Transaction error countCompletedObjectivesForSchedule:',
                error,
            )
            throw error
        }
    }

    getFormsRealizedByStore = async (
        schedule_id,
        store_id_1,
        store_id_2,
        day_unique_id = null,
    ) => {
        if (isWeb()) {
            console.warn(
                'getFormsRealizedByStore no está disponible en la plataforma web.',
            )
            return []
        }

        const realDb = await getRealDatabase()

        try {
            let query, params

            if (store_id_2 && store_id_2 != null && store_id_2 != undefined) {
                query = `
                    SELECT * 
                    FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                    WHERE schedule_id = ? AND (store_id = ? OR store_id = ?)
                `

                params = [schedule_id, store_id_1, store_id_2]
            } else {
                query = `
                SELECT * 
                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                WHERE schedule_id = ? AND store_id = ?
            `
                params = [schedule_id, store_id_1]
            }

            if (day_unique_id != null && day_unique_id != undefined) {
                query += ` AND day_unique_id = ?`
                params.push(day_unique_id)
            }

            // console.log('getFormsRealizedByStore query:', query, 'params:', params)

            const result = await realDb.getAllAsync(query, params)

            if (result.length > 0) {
                let modifiedResult = result
                    .map((form) => {
                        form.answers = JSON.parse(form.answers)
                        return form
                    })
                    .filter((form) => form.answers.length > 0)

                return modifiedResult
            } else {
                return []
            }
        } catch (error) {
            console.error('Transaction error getFormsRealizedByStore:', error)
            throw error
        }
    }

    getFQAByStore = async (formsIds, storeId, scheduleId) => {
        const realDb = await getRealDatabase()
        const generateIdDate = generateIdDateNow()

        const query = `
                        SELECT *
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                        WHERE day_unique_id = ? AND schedule_id = ? AND store_id = ? AND form_id IN (${formsIds.map(() => '?').join(',')});

                    `
        const params = [generateIdDate, scheduleId, storeId, ...formsIds]

        const result = await realDb.getAllAsync(query, params)
        if (result.length > 0) {
            let modifiedResult = result.map((form) => {
                form.answers = JSON.parse(form.answers)
                return form
            })
            return modifiedResult
        }

        return []
    }

    getFormsByStore = async (schedule_id, store_id_1, store_id_2) => {
        const realDb = await getRealDatabase()
        let query = `
                        SELECT * 
                        FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                        WHERE schedule_id = ? AND store_id = ?;
                    `
        let params = [schedule_id, store_id_1]

        if (store_id_2 && store_id_2 != null && store_id_2 != undefined) {
            query = `
                            SELECT * 
                            FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} 
                            WHERE schedule_id = ? AND (store_id = ? OR store_id = ?);
                        `
            params = [schedule_id, store_id_1, store_id_2]
        }

        const result = await realDb.getAllAsync(query, params)
        if (result.length > 0) {
            let modifiedResult = result
                .map((form) => {
                    form.answers = JSON.parse(form.answers)
                    return form
                })
                .filter((form) => form.answers.length > 0)
            return modifiedResult
        }

        return []
    }

    updateKeyCheckoutFQA = async (value, register_id) => {
        const realDb = await getRealDatabase()
        const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                                    SET checkout = ?
                                    WHERE _id = ?;`
        const paramsQuery = [value, register_id]

        const result = await realDb.runAsync(query, paramsQuery)
        console.log(
            `6387 - FORM QUESTIONS ANSWERS updated checkout = ${value} successfully:`,
            result,
        )
        return result
    }

    saveAllLinksAssetsPS = async (dataLinks) => {
        console.log('dataLinks:', dataLinks.length)

        if (!dataLinks || dataLinks.length === 0) {
            console.error('6537 -- dataLinks vacio')
            return
        }

        const realDb = await getRealDatabase()

        try {
            const statusNoDownload = 0

            for (let index = 0; index < dataLinks.length; index++) {
                const link = dataLinks[index]
                const query = `INSERT INTO ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
                            (indexRoute, status, rute)
                            VALUES (?, ?, ?);`
                const paramsQuery = [index, statusNoDownload, link]

                await realDb.runAsync(query, paramsQuery)
            }
        } catch (error) {
            console.error('Transaction error - saveAllLinksAssetsPS:', error)
            throw error
        }
    }

    updateStatusLinkAssetsPS = async (status, rute) => {
        const realDb = await getRealDatabase()

        try {
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
                            SET status = ?
                            WHERE rute = ?;`
            const paramsQuery = [status, rute]

            const result = await realDb.runAsync(query, paramsQuery)
            return result
        } catch (error) {
            console.error('Transaction error - updateStatusLinkAssetsPS:', error)
            throw error
        }
    }

    getLinksAssetsPSDonwloadFailed = async () => {
        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS} WHERE status = 0;`,
                [],
            )

            if (result.length > 0) {
                return result
            } else {
                return []
            }
        } catch (error) {
            console.error(
                'Transaction error - getLinksAssetsPSDonwloadFailed:',
                error,
            )
            throw error
        }
    }

    isTotalAssetsDownloaded = async () => {
        try {
            const realDb = await getRealDatabase()

            const result = await realDb.getFirstAsync(
                `
            SELECT COUNT(*) AS count
            FROM ${SCHEDULES_STORE_NAMES.ASSETS_PF_LINKS}
            WHERE status = 0
            `,
            )

            const count = result?.count ?? 0
            return count === 0
        } catch (error) {
            console.error('Transaction error - isTotalAssetsDownloaded:', error)
            throw error
        }
    }

    // create pos
    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de saveFieldsCreatePos del repository TypeScript.
     *
     * @description Guarda campos de creación de tiendas en SCHEDULE_STORES_CREATE_FIELDS
     * @param {Array} fields - Array de campos a guardar
     * @returns {Promise<void>}
     */
    saveFieldsCreatePos = async (fields) => {
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')

        return scheduleRepository.saveFieldsCreatePos(fields)
    }
    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de saveFieldsOrganizationCreatePos del repository TypeScript.
     *
     * @description Guarda campos de organización en SCHEDULE_STORES_CREATE_FIELDS_ORGANIZATIONS
     * @param {Array} fields - Array de campos a guardar
     * @returns {Promise<void>}
     */
    saveFieldsOrganizationCreatePos = async (fields) => {
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')

        return scheduleRepository.saveFieldsOrganizationCreatePos(fields)
    }
    getFieldsCreatePos = async () => {
        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS}`,
                [],
            )

            const fields = result.map((row) => ({
                _id: row._id,
                name: row.name,
                title: row.title,
                type: row.type,
                required: row.required === 1,
                dependency: row.dependency,
                placeholder: row.placeholder,
                options: JSON.parse(row.options || '[]'),
                optionsFrom: row.optionsFrom,
                configure: row.configure ? JSON.parse(row.configure) : null,
                validate_unique: row.validate_unique === 1,
                table: row.table_name || null,
            }))

            return fields
        } catch (error) {
            console.error('Transaction error => getFieldsCreatePos:', error)
            throw error
        }
    }
    getFieldOrganizationsCreatePos = async () => {
        const realDb = await getRealDatabase()

        try {
            const result = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_FIELDS_ORGANIZATIONS}`,
                [],
            )

            const fields = result.map((row) => ({
                _id: row._id,
                name: row.name,
                title: row.title,
                type: row.type,
                required: row.required === 1,
                dependency: row.dependency,
                placeholder: row.placeholder,
                options: JSON.parse(row.options || '[]'),
                optionsFrom: row.optionsFrom,
                configure: row.configure ? JSON.parse(row.configure) : null,
                validate_unique: row.validate_unique === 1,
                table: row.table_name || null,
            }))

            return fields
        } catch (error) {
            console.error(
                'Transaction error => getFieldORganizationsCreatePos:',
                error,
            )
            throw error
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de saveCountryData del repository TypeScript.
     *
     * @description Guarda datos de país con provincias y ciudades
     * @param {Object} country - Datos del país a guardar
     * @returns {Promise<void>}
     */
    saveCountryData = async (country) => {
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return scheduleRepository.saveCountryData(country)
    }

    getLocationData = async () => {
        const realDb = await getRealDatabase()

        try {
            const countriesRows = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES};`,
                [],
            )

            const provinceRows = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES};`,
                [],
            )

            const citiesRows = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES};`,
                [],
            )

            const dataReturn = {
                countries: countriesRows,
                provinces: provinceRows,
                cities: citiesRows,
            }

            return dataReturn
        } catch (error) {
            console.error('Error fetching getLocationData:', error)
            throw error
        }
    }

    getCountryData = async (countryId) => {
        console.log('getCountryData => countryId= ', countryId)
        if (!countryId || getCountryData.length == 0) {
            throw new Error('countryId invalid')
        }

        const realDb = await getRealDatabase()

        try {
            const countryRow = await realDb.getFirstAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES} WHERE _id = ?;`,
                [countryId],
            )

            console.log('countryResult => ', countryRow)
            if (!countryRow) {
                throw new Error('Country not found')
            }

            console.log('countryRow => ', countryRow)
            const country = {
                _id: countryRow._id,
                name: countryRow.name,
                prefix: countryRow.prefix,
                geo_code: countryRow.geo_code,
                provinces: [],
            }

            const provinceRows = await realDb.getAllAsync(
                `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES} WHERE country_id = ?`,
                [countryId],
            )

            console.log('remaining => ', provinceRows.length)

            if (provinceRows.length === 0) {
                country.provinces = []
                return country
            }

            const provinces = await Promise.all(
                provinceRows.map(async (provinceRow) => {
                    const cityResult = await realDb.getAllAsync(
                        `SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES} WHERE province_id = ?`,
                        [provinceRow._id],
                    )

                    return {
                        _id: provinceRow._id,
                        name: provinceRow.name,
                        cities: cityResult.map((cityRow) => ({
                            _id: cityRow._id,
                            name: cityRow.name,
                        })),
                    }
                }),
            )

            country.provinces = provinces
            return country
        } catch (error) {
            console.error('Error fetching country data:', error)
            throw error
        }
    }

    getProvinceById = async (provinceId) => {
        if (!provinceId || provinceId.trim().length == 0) {
            throw new Error('provinceId invalid')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `
                SELECT * 
                FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES}
                WHERE _id = ?;
            `
            const params = [provinceId]
            const row = await realDb.getFirstAsync(query, params)
            return row
        } catch (error) {
            console.error('Transaction error => getProvinceById:', error)
            throw error
        }
    }

    getDataAddressByCityId = async (cityId) => {
        if (!cityId || cityId.trim().length == 0) {
            throw new Error('cityId invalid')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `
                SELECT 
                    cities._id as city_id,
                    cities.name as city_name,
                    provinces._id as province_id,
                    provinces.name as province_name,
                    countries._id as country_id,
                    countries.name as country_name,
                    countries.prefix,
                    countries.geo_code
                FROM ${SCHEDULES_STORE_NAMES.STORES_CREATE_CITIES} AS cities
                JOIN ${SCHEDULES_STORE_NAMES.STORES_CREATE_PROVINCES} AS provinces
                    ON cities.province_id = provinces._id
                JOIN ${SCHEDULES_STORE_NAMES.STORES_CREATE_COUNTRIES} AS countries
                    ON cities.country_id = countries._id
                WHERE cities._id = ?;
            `
            const params = [cityId]

            const row = await realDb.getFirstAsync(query, params)
            console.log('row => , row')
            return row
        } catch (error) {
            console.error('Transaction error => getCityById:', error)
            throw error
        }
    }

    getOrganizationsPending = async () => {
        if (isWeb()) {
            console.warn(
                'getOrganizationsPending no está disponible en la plataforma web.',
            )
            return []
        }

        const realDb = await getRealDatabase()

        try {
            const query = `
                    SELECT * 
                    FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                    WHERE status_send = ?;
                `
            const params = [STATUS_SEND.PENDING]
            const rows = await realDb.getAllAsync(query, params)
            return rows
        } catch (error) {
            console.error('Transaction error => getOrganizationsPending:', error)
            throw error
        }
    }

    /**
     * 🆕 MIGRADO A TYPESCRIPT REPOSITORY
     *
     * Wrapper de compatibilidad que usa la nueva implementación moderna
     * de addOrganizaciones del repository TypeScript.
     *
     * @description Guarda organizaciones en SCHEDULE_STORES_ORGANIZATIONS
     * @param {Array} organizations - Array de organizaciones a guardar
     * @param {number} status - Estado (deprecado, ya no se usa)
     * @returns {Promise<void>}
     */
    addOrganizaciones = async (organizations) => {
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return scheduleRepository.addOrganizaciones(organizations)
    }

    addOrganization = async (organization) => {
        if (!organization || organization == null || organization == undefined) {
            return null
        }

        const realDb = await getRealDatabase()

        try {
            const query = `
                INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} (
                    _id,
                    name,
                    cuit,
                    code,
                    status_send
                ) VALUES (?, ?, ?, ?, ?)
            `

            const params = [
                organization._id,
                organization.name || '',
                organization.cuit || '',
                organization.code,
                organization.status,
            ]

            await realDb.runAsync(query, params)

            // Luego de insertar, hacemos un SELECT para devolver la organización creada
            const querySelect = `
                SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                WHERE _id = ?
            `

            const insertedOrg = await realDb.getFirstAsync(querySelect, [
                organization._id,
            ])
            return insertedOrg
        } catch (error) {
            console.error('Transaction error => addOrganizaciones:', error)
            throw error
        }
    }

    /**
     * @description si existe la organización devuelve true
     */
    organizationExists = async (organization) => {
        const realDb = await getRealDatabase()

        try {
            const duplicateCheckQuery = `
                SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                WHERE name = ? OR cuit = ?
                LIMIT 1
            `

            const result = await realDb.getFirstAsync(duplicateCheckQuery, [
                organization.name,
                organization.cuit,
            ])

            if (result) {
                console.log(
                    'organizationExists Organización duplicada detectada, no se inserta resolve true',
                )
                return true
            } else {
                console.log(
                    'organizationExists Organización no existe resolve false',
                )
                return false
            }
        } catch (error) {
            console.error(
                'organizationExists Error al verificar duplicados de organización:',
                error,
            )
            throw error
        }
    }

    /**
     * @description si existe la organización devuelve true
     */
    posExists = async (store) => {
        if (isWeb()) {
            throw new Error('web not found')
        }

        const realDb = await getRealDatabase()

        try {
            let duplicateCheckQuery
            let duplicateParams

            if (store.code && store.code !== '') {
                duplicateCheckQuery = `
                    SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES}
                    WHERE name = ? OR code = ?
                    LIMIT 1
                `
                duplicateParams = [store.name, store.code]
            } else {
                // valido solo por name
                duplicateCheckQuery = `
                    SELECT * FROM ${SCHEDULES_STORE_NAMES.STORES}
                    WHERE name = ?
                    LIMIT 1
                `
                duplicateParams = [store.name]
            }

            const result = await realDb.getFirstAsync(
                duplicateCheckQuery,
                duplicateParams,
            )

            if (result) {
                console.log(
                    'posExists store duplicada detectada, no se inserta resolve true',
                )
                return true
            } else {
                console.log('posExists store no existe resolve false')
                return false
            }
        } catch (error) {
            console.error('Transaction error => posExists:', error)
            throw error
        }
    }

    getNextOrganizationCode = async () => {
        const realDb = await getRealDatabase()

        try {
            const query = `SELECT code FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} ORDER BY code DESC LIMIT 1`
            const result = await realDb.getAllAsync(query, [])

            if (!result || result.length === 0) {
                return _INITIAL_CODE_ORGANIZATION
            } else {
                const lastCode = result[0].code
                const nextCode = padCodeComplete(
                    parseInt(lastCode) + 1,
                    CANT_DIGIT_CODE_COMPANY,
                )
                return nextCode
            }
        } catch (error) {
            console.error('Transaction error => getNextOrganizationCode:', error)
            throw error
        }
    }

    saveStoresAndOrganizationsPendings = async (dataSave, responseCreateSqlite) => {
        const realDb = await getRealDatabase()

        try {
            const query = `
                INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND} (
                    data_send,
                    data_match
                ) VALUES (?, ?);
            `

            const params = [
                JSON.stringify(dataSave),
                JSON.stringify(responseCreateSqlite),
            ]

            const result = await realDb.runAsync(query, params)
            console.log('8141 === > ', result)
            return result
        } catch (error) {
            console.error(
                'Transaction error => saveStoresAndOrganizationsPendings:',
                error,
            )
            throw error
        }
    }

    getStoresAndOrganizationsPendings = async () => {
        const realDb = await getRealDatabase()

        try {
            const query = `
                SELECT *
                FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND};
            `

            const result = await realDb.getAllAsync(query, [])

            if (result && result.length > 0) {
                const dataPending = result
                    .map((register) => {
                        return {
                            ...register,
                            data_match: JSON.parse(register.data_match),
                            data_send: JSON.parse(register.data_send),
                        }
                    })
                    .filter((register) => {
                        const createOrg = register.data_send?.createOrganization
                        const orgId = register.data_send?.organization?._id

                        if (createOrg === false && isLocalGeneratedId(orgId)) {
                            return false
                        }
                        return true
                    })
                return dataPending
            }

            return []
        } catch (error) {
            console.error('Error saveStoresAndOrganizationsPendings:', error)
            throw error
        }
    }

    getStoresAndOrganizationsPendingsWithLocalOrg = async () => {
        const realDb = await getRealDatabase()

        try {
            const query = `
                SELECT *
                FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND};
            `

            const result = await realDb.getAllAsync(query, [])

            if (result && result.length > 0) {
                const dataPending = result
                    .map((register) => ({
                        ...register,
                        data_match: JSON.parse(register.data_match),
                        data_send: JSON.parse(register.data_send),
                    }))
                    .filter((register) => {
                        const createOrg = register.data_send?.createOrganization
                        const orgId = register.data_send?.organization?._id
                        // Solo devuelve los que cumplen ambas condiciones
                        return createOrg === false && isLocalGeneratedId(orgId)
                    })
                return dataPending
            } else {
                return []
            }
        } catch (error) {
            console.error(
                'Error getStoresAndOrganizationsPendingsWithLocalOrg:',
                error,
            )
            throw error
        }
    }

    removeStoresAndOrganizationsPendingsById = async (register_id) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES_PENDING_SEND} WHERE _id = ?`
            const result = await realDb.runAsync(query, [register_id])

            console.log('removeStoresAndOrganizationsPendingsById => ', result)

            if (result.changes > 0) {
                return true
            } else {
                return false
            }
        } catch (error) {
            console.error(
                'Transaction error - removeStoresAndOrganizationsPendingsById:',
                error,
            )
            throw error
        }
    }

    removeStoresPendingsById = async (register_id) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES} WHERE _id = ?`
            const result = await realDb.runAsync(query, [register_id])

            console.log('removeStoresPendingsById => ', result)

            if (result.changes > 0) {
                return true
            } else {
                return false
            }
        } catch (error) {
            console.error('Transaction error - removeStoresPendingsById:', error)
            throw error
        }
    }

    removeSchedulePosPendingsById = async (register_id) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} WHERE _id = ?`
            const result = await realDb.runAsync(query, [register_id])

            console.log('removeSchedulePosPendingsById => ', result)

            if (result.changes > 0) {
                return true
            } else {
                return false
            }
        } catch (error) {
            console.error(
                'Transaction error - removeSchedulePosPendingsById:',
                error,
            )
            throw error
        }
    }

    removeOrganizationPendingsById = async (register_id) => {
        if (isWeb()) {
            throw new Error('Not supported on web')
        }

        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS} WHERE _id = ?`
            const result = await realDb.runAsync(query, [register_id])

            console.log('removeOrganizationPendingsById => ', result)
            if (result.changes > 0) {
                return true
            } else {
                return false
            }
        } catch (error) {
            console.error(
                'Transaction error - removeOrganizationPendingsById:',
                error,
            )
            throw error
        }
    }

    getOrganizations = async () => {
        const realDb = await getRealDatabase()

        try {
            const query = `
                SELECT *
                FROM ${SCHEDULES_STORE_NAMES.STORES_ORGANIZATIONS}
                ORDER BY name COLLATE NOCASE;`

            const rows = await realDb.getAllAsync(query, [])

            const organizations = rows.map((row) => ({
                _id: row._id,
                code: row.code,
                cuit: row.cuit,
                name: row.name,
            }))
            return organizations
        } catch (error) {
            console.error('Transaction error => getOrganizations:', error)
            throw error
        }
    }

    existsInLocalDB = async (table, fields) => {
        const realDb = await getRealDatabase()

        try {
            console.log('existsInLocalDB => table:', table, 'fields:', fields)

            const whereClauses = fields.map((f) => `${f.label} = ?`).join(' OR ')
            const params = fields.map((f) => f.value)
            const query = `SELECT * FROM ${table} WHERE ${whereClauses} LIMIT 1`

            console.log('existsInLocalDB => query:', query, 'params:', params)

            const result = await realDb.getAllAsync(query, params)

            console.log('existsInLocalDB => result:', result)

            if (result && result.length > 0) {
                console.log(
                    '11111posExists store duplicada detectada, no se inserta resolve true',
                )
                return true
            } else {
                console.log('2222posExists store no existe resolve false')
                return false
            }
        } catch (error) {
            console.error('Transaction error - existsInLocalDB:', error)
            throw error
        }
    }

    updateKeyCheckinFqa = async (checkinId, formsIdsFqa) => {
        if (!formsIdsFqa || formsIdsFqa.length === 0) {
            return
        }

        if (isWeb()) {
            return
        }

        const realDb = await getRealDatabase()

        try {
            const placeholders = formsIdsFqa.map(() => '?').join(',')
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS} SET checkin_id = ? WHERE _id IN (${placeholders});`
            const params = [checkinId, ...formsIdsFqa]

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            console.error('updateKeyCheckinFqa error:', error)
            throw error
        }
    }

    // forms pendingSync
    /**
     * Crea un registro PENDING por cada form visible al momento del checkin.
     * Se llama una sola vez por checkin (idempotente: si ya existen registros, no hace nada).
     */
    initCheckinFormTracking = async (checkin_id, schedule_id, store_id, forms) => {
        if (!checkin_id || !schedule_id || !forms || forms.length === 0) return

        try {
            const realDb = await getRealDatabase()

            for (const form of forms) {
                if (!form._id) continue

                // Verificar si ya existe un registro para este checkin + form (idempotente)
                const exists = await realDb.getFirstAsync(
                    `SELECT _id FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                     WHERE checkin_id = ? AND activity_id = ?;`,
                    [checkin_id, form._id],
                )

                if (!exists) {
                    await realDb.runAsync(
                        `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                         (activity_id, status, date, count, form_name, status_send, schedule_id, store_id, checkin_id, can_send)
                         VALUES (?, ?, datetime('now', 'localtime'), 0, ?, ?, ?, ?, ?, 1);`,
                        [
                            form._id,
                            FORM_SCHEDULE_STATUS.PENDING,
                            form.name_form,
                            STATUS_FORM_PENDING_SYNC.NO_SENDED,
                            schedule_id,
                            store_id,
                            checkin_id,
                        ],
                    )
                }
            }

            console.log(
                '✅ initCheckinFormTracking: registros creados para checkin_id',
                checkin_id,
            )
        } catch (error) {
            console.error('initCheckinFormTracking error:', error)
        }
    }

    addFormPendingSync = async (form, retryCount = 0) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 100

        if (!form || form == null || form == undefined) {
            return
        }

        try {
            const realDb = await getRealDatabase()

            const {
                activity_id,
                status,
                date,
                count,
                form_name,
                status_send,
                schedule_id,
                store_id,
                checkin_id,
            } = form

            let can_send_value = true

            // ✅ FIX: Retornar early si es formulario "wise" - no requiere sincronización de agenda
            if (schedule_id === 'wise') {
                console.log(
                    '⚠️ addFormPendingSync: Formulario WISE detectado - saltando inserción en FORMS_PENDING_SYNC',
                )
                return
            }

            if (
                schedule_id == null ||
                schedule_id == undefined ||
                schedule_id == ''
            ) {
                throw new Error(
                    'addFormPendingSync - schedule_id is required => schedule_id => ' +
                        schedule_id,
                )
            }
            if (
                activity_id == null ||
                activity_id == undefined ||
                activity_id == ''
            ) {
                throw new Error('addFormPendingSync - activity_id is required')
            }

            let isFormToStoreId = false

            if (
                store_id &&
                store_id !== '' &&
                store_id !== null &&
                store_id !== undefined
            ) {
                isFormToStoreId = true
            }

            let existingQuery, existingParams

            if (isFormToStoreId == false) {
                existingQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                          WHERE schedule_id = ? AND activity_id = ?;`
                existingParams = [schedule_id, activity_id]
            } else {
                existingQuery = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                                          WHERE schedule_id = ? AND activity_id = ? AND store_id = ? AND checkin_id = ?;`
                existingParams = [schedule_id, activity_id, store_id, checkin_id]
            }

            console.log(
                `${isFormToStoreId ? 'Con' : 'Sin'} store_id, buscando formulario existente:`,
            )

            const existing = await realDb.getAllAsync(existingQuery, existingParams)

            if (existing && existing.length > 0) {
                console.log(
                    'Existing form found, updating status/date/count:',
                    existing[0],
                )

                // Solo incrementar count cuando el form fue efectivamente completado.
                // Abrir el form (IN_PROGRESS/PENDING) no debe contar como completado.
                const isCompleted =
                    status === FORM_SCHEDULE_STATUS.COMPLETED ||
                    status === FORM_SCHEDULE_STATUS.PENDING_CONNECTION
                const countExpression = isCompleted ? 'count + 1' : 'count'

                // Solo actualizar date cuando el form se completa.
                // Para PENDING/IN_PROGRESS preservar la fecha existente (la del checkin).
                // Resetear status_send a NO_SENDED para que sendPendingFormSync lo reenvíe.
                let updateQuery = isCompleted
                    ? `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                       SET status = ?, date = ?, count = ${countExpression}, can_send = ?, status_send = ?`
                    : `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                       SET status = ?, count = ${countExpression}, can_send = ?, status_send = ?`
                let updateParams = isCompleted
                    ? [
                          status,
                          date,
                          can_send_value,
                          STATUS_FORM_PENDING_SYNC.NO_SENDED,
                      ]
                    : [status, can_send_value, STATUS_FORM_PENDING_SYNC.NO_SENDED]

                updateQuery += ' WHERE schedule_id = ? AND activity_id = ?'
                updateParams.push(schedule_id, activity_id)

                if (isFormToStoreId && store_id) {
                    updateQuery += ' AND store_id = ?'
                    updateParams.push(store_id)
                }
                if (checkin_id) {
                    updateQuery += ' AND checkin_id = ?'
                    updateParams.push(checkin_id)
                }

                const updateResult = await realDb.runAsync(updateQuery, updateParams)
                console.log('✅ Form pending sync updated successfully')
                return updateResult
            } else {
                console.log('No existing form found, proceeding to insert.')

                let valuesKeys =
                    'activity_id, status, date, count, form_name, status_send, schedule_id'
                let valuesPlaceholders = '?,?,?,?,?,?,?'

                let paramsQuery = [
                    activity_id,
                    status,
                    date,
                    count,
                    form_name,
                    status_send,
                    schedule_id,
                ]
                console.log('isFormToStoreId => ', isFormToStoreId)
                if (isFormToStoreId == true) {
                    paramsQuery.push(store_id)
                    valuesKeys += ', store_id'
                    valuesPlaceholders += ',?'

                    console.log('checkin_id => ', checkin_id)
                    if (checkin_id) {
                        paramsQuery.push(checkin_id)
                        valuesKeys += ', checkin_id'
                        valuesPlaceholders += ',?'
                    }

                    if (!isValidMongoId(checkin_id)) {
                        can_send_value = false
                    }

                    paramsQuery.push(can_send_value)
                    valuesKeys += ', can_send'
                    valuesPlaceholders += ',?'
                }

                let query = `INSERT INTO ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} 
                                (${valuesKeys}) 
                                VALUES (${valuesPlaceholders});`

                const result = await realDb.runAsync(query, paramsQuery)
                console.log('Form added to pending sync:', result)
                return result
            }
        } catch (error) {
            // ✅ Manejar "database is locked" con retry
            if (
                error.message &&
                error.message.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [addFormPendingSync] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.addFormPendingSync(form, retryCount + 1)
            }
            console.error('Transaction error - addFormPendingSync:', error)
            throw error
        }
    }

    getFormsPendingSync = async () => {
        const realDb = await getRealDatabase()

        try {
            // Enviar TODOS los registros pendientes de sincronización, sin importar el status.
            // Incluye los PENDING creados al hacer checkin y los completados.
            // El backend siempre recibe la vista más actualizada de cada form.
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                           WHERE status_send = ? AND can_send = ?;`
            const params = [STATUS_FORM_PENDING_SYNC.NO_SENDED, true]

            const result = await realDb.getAllAsync(query, params)

            if (!result || result.length === 0) {
                return []
            }

            return result
        } catch (error) {
            console.error('Transaction error - getFormsPendingSync:', error)
            throw error
        }
    }

    /**
     * @description Updatea el status_send del formulario, que que determina si hay que sincronizar o no
     * @param {*} schedule_id
     * @param {*} activity_id
     * @param {*} status_send
     * @returns
     */
    updateFormPendingSyncStatusSend = async (
        schedule_id,
        activity_id,
        status_send,
    ) => {
        // console.log('🔄 updateFormPendingSyncStatusSend (wrapper) =>', { schedule_id, activity_id, status_send })

        // ✅ Usar la nueva implementación
        const scheduleRepository =
            await import('../db-sqlite/repositories/schedule.repository')
        return await scheduleRepository.updateFormPendingSyncStatusSend(
            schedule_id,
            activity_id,
            status_send,
        )
    }

    /**
     * @description Marca status_send por _id (primary key).
     * Más seguro que usar schedule_id+activity_id que puede afectar otras sesiones.
     */
    updateFormPendingSyncStatusSendById = async (ids, status_send) => {
        if (!ids || ids.length === 0) return
        const realDb = await getRealDatabase()

        try {
            const placeholders = ids.map(() => '?').join(',')
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                           SET status_send = ?
                           WHERE _id IN (${placeholders});`
            return await realDb.runAsync(query, [status_send, ...ids])
        } catch (error) {
            console.error('updateFormPendingSyncStatusSendById error:', error)
            throw error
        }
    }

    /**
     * @description Updatea el status real del formulario
     * @param {*} schedule_id
     * @param {*} activity_id
     * @param {*} status
     * @returns
     */
    updateFormPendingSyncStatus = async (schedule_id, activity_id, status) => {
        const realDb = await getRealDatabase()

        try {
            // Incrementar count solo cuando el form fue efectivamente completado
            const isCompleted =
                status === FORM_SCHEDULE_STATUS.COMPLETED ||
                status === FORM_SCHEDULE_STATUS.PENDING_CONNECTION
            const countExpression = isCompleted ? 'count + 1' : 'count'

            // Actualiza solo el registro más reciente (sesión actual).
            // Resetea status_send para que sendPendingFormSync lo reenvíe con el nuevo status.
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                           SET status = ?, date = datetime('now', 'localtime'), status_send = ?, count = ${countExpression}
                           WHERE _id = (
                               SELECT _id FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC}
                               WHERE schedule_id = ? AND activity_id = ?
                               ORDER BY _id DESC LIMIT 1
                           );`
            const params = [
                status,
                STATUS_FORM_PENDING_SYNC.NO_SENDED,
                schedule_id,
                activity_id,
            ]

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            console.error('Transaction error - updateFormPendingSyncStatus:', error)
            throw error
        }
    }

    getStoreMaxDistance = async (store_id) => {
        if (!store_id || store_id === null || store_id === undefined) return 0

        if (isWeb()) {
            return 0
        }

        const realDb = await getRealDatabase()

        try {
            let query = `
                SELECT maximum_distance_for_checkin
                FROM ${SCHEDULES_STORE_NAMES.STORES}
                WHERE _id = ?;
            `
            let params = [store_id]

            const result = await realDb.getFirstAsync(query, params)

            if (result && result.maximum_distance_for_checkin !== undefined) {
                return result.maximum_distance_for_checkin
            }
            return 0
        } catch (error) {
            console.error('No se pudo obtener la tienda:', error)
            throw error
        }
    }

    getFormsPendingSyncByCheckinId = async (checkin_id) => {
        const realDb = await getRealDatabase()

        try {
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} WHERE checkin_id = ?;`
            const params = [checkin_id]

            const result = await realDb.getAllAsync(query, params)
            return result
        } catch (error) {
            console.error(
                'Transaction findFormsPendingSyncByCheckinId error:',
                error,
            )
            throw error
        }
    }

    updateCanSendFormsPendingSyncById = async (formIds, can_send) => {
        const realDb = await getRealDatabase()

        try {
            if (!formIds || formIds.length === 0) return

            const placeholders = formIds.map(() => '?').join(',')
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} SET can_send = ? WHERE _id IN (${placeholders});`
            const params = [can_send, ...formIds]

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            console.error(
                'Error updating can_send en formularios pendientes de sincronización:',
                error,
            )
            throw error
        }
    }

    updateCheckinIdFormsPendingSyncById = async (formIds, checkin_id) => {
        const realDb = await getRealDatabase()

        try {
            if (!formIds || formIds.length === 0) return

            const placeholders = formIds.map(() => '?').join(',')
            const query = `UPDATE ${SCHEDULES_STORE_NAMES.FORMS_PENDING_SYNC} SET checkin_id = ? WHERE _id IN (${placeholders});`
            const params = [checkin_id, ...formIds]

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            console.error(
                'Error updating checkin_id en formularios pendientes de sincronización:',
                error,
            )
            throw error
        }
    }

    getFormsPendingSyncToSend = async (schedule_id, checkin_id, forms = []) => {
        try {
            if (!forms || forms.length === 0) {
                return []
            }

            const realDb = await getRealDatabase()

            let query = ''
            let params = []

            const placeholders = forms.map(() => '?').join(', ')

            if (checkin_id != null) {
                query = `
                SELECT *
                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                WHERE schedule_id = ?
                  AND checkin_id = ?
                  AND form_id IN (${placeholders})
            `
                params = [schedule_id, checkin_id, ...forms]
            } else {
                query = `
                SELECT *
                FROM ${SCHEDULES_STORE_NAMES.FORMS_QUESTION_ANSWERS}
                WHERE schedule_id = ?
                  AND form_id IN (${placeholders})
            `
                params = [schedule_id, ...forms]
            }

            const rows = await realDb.getAllAsync(query, params)

            if (rows.length === 0) {
                return []
            }

            return rows.map((form) => ({
                ...form,
                answers: safeJSONParse(form.answers),
                questions: safeJSONParse(form.questions),
                type_form: safeJSONParse(form.type_form),
            }))
        } catch (error) {
            console.error('getFormsPendingSyncToSend - Transaction error:', error)
            throw error
        }
    }

    updateStoreKPIS = async (store_id, kpis) => {
        if (!store_id || !kpis || kpis.length === 0) return

        const realDb = await getRealDatabase()
        if (!kpis || kpis.length === 0) return

        const query = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} (store_id, indicators) VALUES (?, ?);`
        const params = [store_id, JSON.stringify(kpis)]

        const result = await realDb.runAsync(query, params)
        console.log('KPI guardado con exito')
        return result
    }
    clearStoreKPIS = async (store_id) => {
        if (!store_id) return

        const realDb = await getRealDatabase()

        const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} WHERE store_id = ?;`
        const params = [store_id]

        const result = await realDb.runAsync(query, params)
        console.log('KPI eliminado con exito')
        return result
    }
    updateStoresKPIS = async (indicators) => {
        if (!indicators || indicators.length === 0) return

        const realDb = await getRealDatabase()

        try {
            await realDb.withTransactionAsync(async () => {
                for (const { pos_id, kpis_indicators } of indicators) {
                    if (!pos_id || !kpis_indicators) continue

                    const query = `INSERT OR REPLACE INTO ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} 
                                  (store_id, indicators) 
                                  VALUES (?, ?);`
                    const params = [pos_id, JSON.stringify(kpis_indicators)]

                    await realDb.runAsync(query, params)
                    console.log(`KPI de tienda ${pos_id} guardado con éxito`)
                }
            })

            console.log(
                `✅ ${indicators.length} KPIs de tiendas guardados con éxito`,
            )
        } catch (error) {
            console.error('Error guardando KPIs de tiendas:', error)
            throw error
        }
    }

    getStoreKPIS = async (store_id) => {
        const realDb = await getRealDatabase()
        const query = `SELECT indicators FROM ${SCHEDULES_STORE_NAMES.STORES_INDICATORS} WHERE store_id = ?;`
        const params = [store_id]

        const result = await realDb.getFirstAsync(query, params)
        if (result && result.indicators) {
            const kpis = JSON.parse(result.indicators)
            console.log('KPI recuperado con exito')
            return kpis
        }

        return []
    }

    createActivityDistributorPending = async (data) => {
        const realDb = await getRealDatabase()

        try {
            const checkin_id = data?.checkin_id || null
            const activities = data?.activities || null
            const schedule_id = data?.schedule_id || null

            let query = ''
            let valuesKeys = ''
            let valuesPlaceholders = ''
            let params = []

            if (checkin_id) {
                valuesKeys += 'checkin_id, '
                valuesPlaceholders += '?, '
                params.push(checkin_id)
            }
            if (schedule_id) {
                valuesKeys += 'schedule_id, '
                valuesPlaceholders += '?, '
                params.push(schedule_id)
            }
            if (activities) {
                valuesKeys += 'activities, '
                valuesPlaceholders += '?, '
                params.push(activities)
            }

            query = `
                INSERT INTO ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS} 
                (${valuesKeys.slice(0, -2)}) 
                VALUES (${valuesPlaceholders.slice(0, -2)});
            `

            const result = await realDb.runAsync(query, params)
            return result
        } catch (error) {
            console.error('Error creating activity distributor pending:', error)
            throw error
        }
    }

    getActivitiesDistributorsPending = async () => {
        const realDb = await getRealDatabase()

        try {
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS};`
            const result = await realDb.getAllAsync(query, [])
            return result
        } catch (error) {
            console.error('Error getting activities distributors pending:', error)
            throw error
        }
    }

    deleteByIdActivitiesDistributorsPending = async (id) => {
        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${SCHEDULES_STORE_NAMES.SCHEDULE_ACTIVITIES_ADVANCES_PENDINGS} WHERE _id = ?;`
            const result = await realDb.runAsync(query, [id])
            return result
        } catch (error) {
            console.error('Error deleting activities distributors pending:', error)
            throw error
        }
    }

    getFormsByIds = async (formsIds) => {
        const realDb = await getRealDatabase()

        try {
            const query = `SELECT * FROM ${SCHEDULES_STORE_NAMES.FORMS} WHERE _id IN (${formsIds.map(() => '?').join(', ')});`
            const result = await realDb.getAllAsync(query, formsIds)
            return result
        } catch (error) {
            console.error('Error getting forms by IDs:', error)
            throw error
        }
    }

    deleteTableRegister = async (table, retryCount = 0) => {
        const MAX_RETRIES = 3
        const RETRY_DELAY = 100 // ms
        const realDb = await getRealDatabase()

        try {
            const query = `DELETE FROM ${table};`
            const result = await realDb.runAsync(query, [])
            console.log(`✅ Table ${table} cleared successfully`)
            return result
        } catch (error) {
            // Manejar database locked con retry
            if (
                error?.message?.includes('database is locked') &&
                retryCount < MAX_RETRIES
            ) {
                console.warn(
                    `⚠️ [deleteTableRegister] Database locked, retrying ${retryCount + 1}/${MAX_RETRIES}...`,
                )
                await new Promise((resolve) =>
                    setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
                )
                return this.deleteTableRegister(table, retryCount + 1)
            }
            console.error(`Error deleting table register from ${table}:`, error)
            throw error
        }
    }

    updateStoreLocation = async (store_id, lat, lng, address) => {
        // 1. Validar que tengamos el ID de la tienda
        if (!store_id) {
            console.warn('updateStoreLocation: store_id es requerido.')
            return null
        }

        const realDb = await getRealDatabase()

        const storesFields = []
        const storesParams = []
        const posFields = []
        const posParams = []

        if (lat !== undefined && lat !== null) {
            storesFields.push('lat = ?')
            storesParams.push(lat)
            posFields.push('lat = ?')
            posParams.push(lat)
        }
        if (lng !== undefined && lng !== null) {
            storesFields.push('lng = ?')
            storesParams.push(lng)
            posFields.push('lng = ?')
            posParams.push(lng)
        }

        if (address !== undefined && address !== null) {
            storesFields.push('address = ?')
            storesParams.push(address)
        }

        try {
            let storesResult = null
            let posResult = null

            // Ejecutar el update de STORES si llegó algún campo válido
            if (storesFields.length > 0) {
                const storesQuery = `UPDATE ${SCHEDULES_STORE_NAMES.STORES} SET ${storesFields.join(', ')} WHERE _id = ?;`
                storesParams.push(store_id)
                storesResult = await realDb.runAsync(storesQuery, storesParams)
                console.log('Tabla STORES actualizada con éxito')
            }

            // Ejecutar el update de SCHEDULES_POS si llegó lat o lng
            // (Asumo que el identificador en esta tabla también se relaciona con store_id)
            if (posFields.length > 0) {
                const posQuery = `UPDATE ${SCHEDULES_STORE_NAMES.SCHEDULES_POS} SET ${posFields.join(', ')} WHERE store_id = ?;`
                posParams.push(store_id)
                posResult = await realDb.runAsync(posQuery, posParams)
                console.log('Tabla SCHEDULES_POS actualizada con éxito')
            }

            // Retornamos un objeto con los resultados de ambas operaciones
            return { storesResult, posResult }
        } catch (error) {
            console.error('Error al actualizar las ubicaciones:', error)
            throw error
        }
    }
}

export const schedulesServices = new schedulesService()

/**
 * @description Valida si existe un registro con fecha de hoy
 * @param {Array} registersExistent
 * @returns {boolean}
 */
const validateDateRegister = (registersExistent = []) => {
    if (!Array.isArray(registersExistent) || registersExistent.length === 0) {
        return false
    }

    const today = new Date().toISOString().split('T')[0]

    return registersExistent.some(
        (row) => row?.date_init && row.date_init.split('T')[0] === today,
    )
}

export function calculateDistance(lat1, lon1, lat2, lon2) {
    let rad = function (x) {
        return (x * Math.PI) / 180
    }
    let R = 6378.137 //Radio de la Earth en km
    let dLat = rad(lat2 - lat1)
    let dLong = rad(lon2 - lon1)
    let a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(rad(lat1)) *
            Math.cos(rad(lat2)) *
            Math.sin(dLong / 2) *
            Math.sin(dLong / 2)
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    //aquí obtienes la distancia en metros por la conversion 1Km =1000m
    var d = R * c * 1000
    return d
}

/**
 *
 * @param {*} questions
 * @returns {{cantQuestionPonderar<number> ; scoreTotalQuestionsPonderar<number>}}
 */
const getCantQuestionPonderar = (questions) => {
    const questionsTransform = JSON.parse(questions)
    const cantQuestionPonderar = questionsTransform.filter(
        (obj) => obj.ponderar === true,
    )
    const scoreTotalQuestionsPonderar = cantQuestionPonderar.reduce(
        (accumulator, currentValue) => {
            const questionScore = currentValue.questionScore ?? 0
            const score = currentValue.score ?? 0
            return accumulator + questionScore + score
        },
        0,
    )

    return {
        scoreTotalQuestionsPonderar,
        cantQuestionPonderar: cantQuestionPonderar.length,
    }
}

// Función para calcular la bounding box
function calculateBoundingBox(latitude, longitude, distance) {
    const earthRadius = 6371 // Radio de la Tierra en kilómetros
    const toRadians = (degrees) => (degrees * Math.PI) / 180
    const toDegrees = (radians) => (radians * 180) / Math.PI

    const angularDistance = distance / earthRadius // Convertir la distancia a un ángulo en radianes

    const minLatitude = latitude - toDegrees(angularDistance)
    const maxLatitude = latitude + toDegrees(angularDistance)

    const deltaLongitude = Math.asin(
        Math.sin(angularDistance) / Math.cos(toRadians(latitude)),
    )

    const minLongitude = longitude - toDegrees(deltaLongitude)
    const maxLongitude = longitude + toDegrees(deltaLongitude)

    return {
        minLatitude: minLatitude,
        maxLatitude: maxLatitude,
        minLongitude: minLongitude,
        maxLongitude: maxLongitude,
    }
}

// Función para generar un ID único
export function generateUniqueId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

const newcalculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRadians = (degree) => degree * (Math.PI / 180)

    const R = 6371e3 // Radio de la Tierra en metros
    const φ1 = toRadians(lat1)
    const φ2 = toRadians(lat2)
    const Δφ = toRadians(lat2 - lat1)
    const Δλ = toRadians(lng2 - lng1)

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distancia en metros
}

const isNaOptionSelected = (answer) => {
    return (
        (answer.option_selected_text &&
            answer.option_selected_text.includes('na')) ||
        (answer.questionOptionSelectedText &&
            answer.questionOptionSelectedText.includes('na'))
    )
}
